/**
 * PlaygroundView — React port of the NanoClaw Dashboard layout.
 *
 * Mirrors the dashboard's structure: left nav sidebar, Chat page (session sidebar +
 * chat area), Cards page (template sidebar + 3-col detail). Uses Handlebars
 * template renderer for card rendering via hot-updatable HTML templates.
 *
 * Sessions are loaded from api.getSessions() (shared with HistoryView).
 * Templates are loaded from NanoClaw dashboard API.
 *
 * NOTE: No camera/mic/LiveKit access — this is a desktop-only playground view.
 * Do NOT import useLiveKit, navigator.mediaDevices, or AudioContext here.
 */

import { useState, useEffect, useCallback, useRef, useMemo, Suspense, Fragment } from 'react';
import { Routes, Route, Navigate, NavLink, Outlet, useParams, useNavigate } from 'react-router-dom';
import MarkdownRenderer from './MarkdownRenderer';
import ThinkingPlanCard from './modules/ThinkingPlanCard';
import PersistentHtmlRenderer from './PersistentHtmlRenderer';
import { renderTemplate, clearTemplateCache } from '../services/templateRenderer';
import {
  api,
  tokenStore,
  keysListRequest,
  keysCreateRequest,
  keysUpdateRequest,
  keysDeleteRequest,
  cardsListRequest,
  cardsCreateRequest,
  cardsUpdateRequest,
  cardsDeleteRequest,
} from '../services/api';
import MemoryContent from './MemoryView';
import { useSessionEventStream } from '../hooks/useSessionEventStream';
import MdStreamingRenderer, { STREAMABLE_TEMPLATES } from './MdStreamingRenderer';

// ── Model selector constants (curated list, mirrors vi_agent) ──
const AVAILABLE_MODELS = [
  { id: 'claude-opus-4-6', label: 'Opus 4.6' },
  { id: 'claude-sonnet-4-6', label: 'Sonnet 4.6' },
  { id: 'claude-haiku-4-5', label: 'Haiku 4.5' },
  { id: 'gemini-3-flash', label: 'Gemini 3 Flash' },
  { id: 'gemini-3.1-pro', label: 'Gemini 3.1 Pro' },
  { id: 'gemini-3.1-flash-lite', label: 'Gemini 3.1 Flash Lite' },
  { id: 'MiniMax-M2.7', label: 'MiniMax 2.7' },
  { id: 'kimi-k2.5', label: 'Kimi K2.5' },
  { id: 'system', label: 'System' },
];
const DEFAULT_MODEL = 'claude-opus-4-6';

// ── Dashboard CSS variables (inline, to avoid class conflicts with main app) ──

const _isVideoUrl = (url) => /\.(mp4|mov|webm)($|\?)/i.test(url);

const V = {
  bg: '#f5f5f7',
  bgCard: '#ffffff',
  bgHover: '#eeeef0',
  border: '#d8d8dc',
  text: '#1d1d1f',
  textDim: '#6e6e73',
  accent: '#5856d6',
  accentDim: '#eeedf5',
  green: '#34c759',
  red: '#ff3b30',
  yellow: '#ff9500',
  blue: '#007aff',
  purple: '#af52de',
  font: "'SF Mono', 'Cascadia Code', 'JetBrains Mono', monospace",
  fontUi: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
};

// ── Card extraction: flatten cards from synthetic tasks for rendering ──
// NOTE: No NanoClaw dashboard REST calls — sessions come from api.getSessions().

function deduplicateCards(tasks) {
  const allCards = {};
  const cardTaskMap = {};
  const sorted = [...tasks].sort((a, b) => (a.requestTs || a.ts || 0) - (b.requestTs || b.ts || 0));
  for (const task of sorted) {
    const taskCards = task.cards || {};
    for (const [id, card] of Object.entries(taskCards)) {
      if (!allCards[id] || card.status === 'finalized') {
        allCards[id] = { cardId: id, ...card };
        cardTaskMap[id] = task;
      }
    }
  }
  return { allCards, cardTaskMap, sortedTasks: sorted };
}


// ── Infrastructure vs content-bearing card classification ──
// Infrastructure cards are system/internal (thinking, speech, memory updates) — they don't
// represent user-visible results. Content cards are everything else (freeform-html, text-result,
// info-card, recipe, etc.). The sibling `debug-html` template renders the same as `freeform-html`
// but is bucketed into a collapsible Debug panel (tool I/O from agent-speak hooks).
// Unknown templates default to content (safer to show than hide).
const INFRASTRUCTURE_TEMPLATES = new Set([
  'thinking', 'thinking-process',
  'agent-speech',
  'memory-update',
  'session-summary',
  'package-activation',
]);

function hasContentCards(cards) {
  if (!cards) return false;
  return Object.values(cards).some(card => !INFRASTRUCTURE_TEMPLATES.has(card.template));
}

// ── Format helpers ──

function formatTime(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  const now = new Date();
  const diff = now - d;
  if (diff < 60000) return 'just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function formatDateTime(ts) {
  if (!ts) return '';
  return new Date(ts).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function formatDuration(ms) {
  if (!ms || ms < 0) return '';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function formatSkillTag(slug) {
  if (!slug) return '';
  return slug.replace(/^agent:/, '');
}

// ── TemplateCardPreview — renders a template via Handlebars into a srcdoc iframe ──
//
// Three states:
//   loading      → spinner-ish placeholder while fetching
//   ready        → iframe with compiled HTML
//   unavailable  → fallback rendering of card.data so the user still sees content
//                  even when nanoclaw doesn't have this template registered

function TemplateCardPreview({ template, data, cardId, sessionId }) {
  const [state, setState] = useState({ status: 'loading' });
  const iframeRef = useRef(null);
  const dataJson = JSON.stringify(data);

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    renderTemplate(template, data).then(result => {
      if (cancelled || !result) return;
      setState(result);
    });
    return () => { cancelled = true; };
  }, [template, dataJson]);

  // Bridge vi_action messages from this iframe → window CustomEvent so the
  // chat page can react. Used by ask-user-question Submit to "continue the
  // chat": the chosen option label / custom text becomes the next user turn.
  // sessionId travels with the event so the follow-up dispatch lands on the
  // card's OWN session — never stale React state.
  useEffect(() => {
    const onMsg = (e) => {
      if (e.source !== iframeRef.current?.contentWindow) return;
      const d = e.data;
      if (!d || d.type !== 'vi_action') return;
      if (d.action === 'user_reply') {
        let answers = null;
        try { answers = typeof d.reply === 'string' ? JSON.parse(d.reply) : d.reply; } catch { /* malformed */ }
        const detail = { template, cardId, sessionId, answers, raw: d };
        window.dispatchEvent(new CustomEvent('vi-card-reply', { detail }));
      } else {
        window.dispatchEvent(new CustomEvent('vi-card-action', { detail: { template, sessionId, ...d } }));
      }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [template, cardId, sessionId]);

  if (state.status === 'loading') {
    return <div style={{ padding: 16, color: V.textDim, fontSize: 13 }}>Loading template...</div>;
  }

  if (state.status === 'unavailable' || state.status === 'error') {
    return <FallbackCardPreview template={template} data={data} reason={state.status} message={state.message} />;
  }

  return (
    <iframe
      key={template + dataJson}
      ref={iframeRef}
      srcDoc={state.html}
      // ``allow-same-origin`` so embedded ``<img src="/api/media/...">``
      // fetches inherit the parent's session cookie (null-origin iframes
      // are cross-site and drop it).
      sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      style={{ width: '100%', border: 'none', minHeight: 200 }}
      onLoad={() => {
        const iframe = iframeRef.current;
        if (iframe?.contentDocument?.body) {
          iframe.style.height = iframe.contentDocument.body.scrollHeight + 'px';
        }
      }}
    />
  );
}

// ── FallbackCardPreview — best-effort renderer when the template is missing ──
//
// Walks card.data shape-agnostically and renders strings/arrays/objects as
// a structured outline. Keeps the user unblocked when nanoclaw's template
// registry is missing an entry the model decided to use.

function renderFallbackValue(value, depth = 0) {
  if (value == null) return null;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return <span style={{ color: V.text }}>{String(value)}</span>;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return <span style={{ color: V.textDim, fontStyle: 'italic' }}>(empty)</span>;
    return (
      <ul style={{ margin: '4px 0', paddingLeft: 20, color: V.text }}>
        {value.map((item, i) => (
          <li key={i} style={{ marginBottom: 4 }}>{renderFallbackValue(item, depth + 1)}</li>
        ))}
      </ul>
    );
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value);
    if (entries.length === 0) return null;
    return (
      <div style={{ marginLeft: depth > 0 ? 8 : 0 }}>
        {entries.map(([k, v]) => (
          <div key={k} style={{ marginBottom: 6 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: V.textDim, textTransform: 'capitalize' }}>{k}</div>
            <div style={{ marginTop: 2 }}>{renderFallbackValue(v, depth + 1)}</div>
          </div>
        ))}
      </div>
    );
  }
  return <span style={{ color: V.textDim }}>{String(value)}</span>;
}

function FallbackCardPreview({ template, data, reason, message }) {
  const title = data?.title || data?.name || data?.heading;
  const banner = reason === 'error'
    ? `Template "${template}" failed to render`
    : `Template "${template}" not available — showing raw fields`;
  return (
    <div style={{
      padding: '14px 16px',
      border: `1px dashed ${V.border || '#d4d4d8'}`,
      borderRadius: 8,
      background: 'rgba(250, 204, 21, 0.04)',
    }}>
      <div style={{
        fontSize: 11, color: '#a16207', textTransform: 'uppercase',
        letterSpacing: 0.5, marginBottom: 8, fontWeight: 600,
      }}>
        ⚠ {banner}
      </div>
      {title && (
        <div style={{ fontSize: 16, fontWeight: 700, color: V.text, marginBottom: 10 }}>{title}</div>
      )}
      {renderFallbackValue(data && Object.fromEntries(Object.entries(data).filter(([k]) => k !== 'title' && k !== 'name' && k !== 'heading')))}
      {message && (
        <div style={{ marginTop: 10, fontSize: 11, color: V.textDim, fontFamily: 'ui-monospace, monospace' }}>{message}</div>
      )}
    </div>
  );
}

// ── Shared: render a card via template renderer ──

function CardRenderer({ card }) {
  // Thinking cards use ThinkingPlanCard (structured plan display with fold/unfold)
  if (card.template === 'thinking' || card.template === 'thinking-process') {
    return <ThinkingPlanCard data={card.data || {}} cardStatus={card.status} />;
  }

  // Agent speech cards — markdown-rendered bubble
  if (card.template === 'agent-speech') {
    const msg = card.data?.message || '';
    return msg ? (
      <MarkdownRenderer style={{
        padding: '12px 16px', fontSize: 14, lineHeight: 1.6,
        color: V.text, fontFamily: V.fontUi,
      }}>{msg}</MarkdownRenderer>
    ) : null;
  }

  // For freeform-html / debug-html (same renderer, different bucket) or any card with raw HTML
  // data, render via PersistentHtmlRenderer.
  const htmlContent = card.data?.html || card.data?.htmlContent;
  if (
    (card.template === 'freeform-html' ||
      card.template === 'freeform_html' ||
      card.template === 'debug-html') &&
    htmlContent
  ) {
    const trimmed = htmlContent.trim();
    const looksLikeMarkdown = !trimmed.startsWith('<') && (
      /^#{1,6}\s/m.test(trimmed) ||
      /^\*{1,2}[^*]+\*{1,2}/m.test(trimmed) ||
      /^[-*+]\s/m.test(trimmed) ||
      /^\d+\.\s/m.test(trimmed) ||
      /```[\s\S]*```/m.test(trimmed)
    );
    if (looksLikeMarkdown) {
      return (
        <div style={{ padding: '12px 16px' }}>
          <MarkdownRenderer content={trimmed} />
        </div>
      );
    }
    return <PersistentHtmlRenderer streamingChunks={[htmlContent]} isStreaming={false} />;
  }

  // During streaming, render md-* templates inline for instant progressive updates.
  // After finalize, fall through to Handlebars + iframe for full styling.
  const isDone = card.status === 'finalized' || card.status === 'done';
  if (!isDone && STREAMABLE_TEMPLATES.has(card.template)) {
    return <MdStreamingRenderer template={card.template} data={card.data || {}} darkMode={false} />;
  }

  // All other templates → render via Handlebars + iframe.
  // Pass sessionId so user_reply events (e.g. ask-user-question Submit)
  // dispatch the follow-up against the card's OWN session, not whatever
  // React state currently holds — prevents stale-session bugs after
  // rapid navigation.
  return (
    <TemplateCardPreview
      template={card.template}
      data={card.data || {}}
      cardId={card.cardId}
      sessionId={card.sessionId}
    />
  );
}

// ── Raw data debug view — shows card JSON in a scrollable <pre> block ──

function RawDataView({ card }) {
  return (
    <pre style={{
      margin: 0, padding: '12px 16px', fontSize: 11, lineHeight: 1.5,
      fontFamily: V.font, background: '#1a1a2e', color: '#a8e6cf',
      overflow: 'auto', maxHeight: '60vh', whiteSpace: 'pre-wrap',
      wordBreak: 'break-all',
    }}>
      {JSON.stringify(card, null, 2)}
    </pre>
  );
}

// ── DebugPanel — collapsible bucket for `debug-html` cards (agent-speak tool I/O) ──
// Issue #1007. Uses native <details>/<summary> for zero-JS collapse. Same CardRenderer
// as main cards so rendering stays byte-for-byte identical to inline `freeform-html`.
function DebugPanel({ cards }) {
  if (!cards || cards.length === 0) return null;
  return (
    <details
      style={{
        marginTop: 16, marginBottom: 16, maxWidth: '80%', marginRight: 'auto',
        border: `1px solid ${V.border}`, borderRadius: 8, background: V.bgCard,
      }}
    >
      <summary
        style={{
          padding: '8px 12px', fontSize: 11, fontWeight: 600,
          textTransform: 'uppercase', letterSpacing: '0.04em',
          color: V.textDim, cursor: 'pointer', userSelect: 'none',
          listStyle: 'none',
        }}
      >
        🛠 Debug ({cards.length} {cards.length === 1 ? 'card' : 'cards'})
      </summary>
      <div style={{ borderTop: `1px solid ${V.border}`, padding: '8px 12px' }}>
        {cards.map(card => (
          <div
            key={card.cardId}
            style={{
              marginBottom: 8, background: '#fff', color: '#111',
              border: `1px solid ${V.border}`, borderRadius: 6, overflow: 'hidden',
            }}
          >
            <CardRenderer card={card} />
          </div>
        ))}
      </div>
    </details>
  );
}

function RawToggleButton({ active, onClick, style }) {
  return (
    <button
      onClick={onClick}
      title="Toggle raw data"
      style={{
        background: active ? 'rgba(88,86,214,0.15)' : 'transparent',
        color: active ? V.accent : V.textDim,
        border: `1px solid ${active ? V.accent : 'transparent'}`,
        borderRadius: 4, padding: '2px 6px', fontSize: 10, cursor: 'pointer',
        fontFamily: V.font, fontWeight: 600, lineHeight: 1,
        transition: 'all 0.15s',
        ...style,
      }}
    >
      &lt;/&gt;
    </button>
  );
}

// ══════════════════════════════════════════════════════════════════════
// NAV SIDEBAR — mirrors dashboard .sidebar
// ══════════════════════════════════════════════════════════════════════

// Environment badge colors
const ENV_COLORS = { dev: '#22c55e', staging: '#f59e0b', production: '#ef4444' };

function NavSidebar() {
  // Third-party dev platform (see memory/project_access_model): admin-only
  // pages hit 403 for regular users, so hide them entirely.
  const ALL_PAGES = [
    { id: 'chat',      icon: '\u2709',         label: 'Chat' },
    // Prompt Presets 暂时隐藏 — 等阶段 10 `/v1/chat/completions` 接入运行时注入后再打开
    { id: 'prompts',   icon: '\uD83D\uDCC4',   label: 'Prompt Presets', hidden: true },
    { id: 'keys',      icon: '\uD83D\uDD11',   label: 'API Keys' },
    { id: 'intention', icon: '\uD83C\uDFAF',   label: 'Intention' },
    { id: 'templates', icon: '\u25A6',         label: 'Templates' },
    { id: 'packages',  icon: '\uD83D\uDCE6',   label: 'Packages',  adminOnly: true },
    { id: 'skills',    icon: '\u2728',         label: 'Skills' },
    { id: 'tools',     icon: '\uD83D\uDD27',   label: 'Tools' },
    { id: 'memory',    icon: '\uD83E\uDDE0',   label: 'Memory' },
    { id: 'apis',      icon: '\uD83D\uDD0D',   label: 'API Health', adminOnly: true },
    { id: 'webhooks',  icon: '\uD83D\uDD14',   label: 'Webhooks' },
    { id: 'usage',     icon: '\uD83D\uDCCA',   label: 'Usage' },
    { id: 's3',        icon: '\u2601',         label: 'S3',        adminOnly: true },
  ];
  const [role, setRole] = useState(null);
  useEffect(() => {
    api.getMe()
      .then(me => setRole(me?.role || 'user'))
      .catch(() => setRole('user'));
  }, []);
  const isAdmin = role === 'admin';
  const pages = ALL_PAGES.filter(p => !p.hidden && (isAdmin || !p.adminOnly));

  const viUserId = api.getViUserId();
  const deviceId = api.getDeviceId();
  const baseUrl = api.getBaseUrl();
  // Fetch environment from backend
  const [envInfo, setEnvInfo] = useState(null);
  useEffect(() => {
    api.authFetch('/api/nanoclaw/dashboard/environment')
      .then(r => r.json())
      .then(d => setEnvInfo(d))
      .catch(() => {});
  }, []);
  // Fetch user balance
  const [balance, setBalance] = useState(null);
  useEffect(() => {
    if (!viUserId) return;
    api.request('/api/users/me/profile', {}, { retries: 1, timeout: 5000 })
      .then(d => { if (d?.balance != null) setBalance(d.balance); })
      .catch(() => {});
  }, [viUserId]);
  // Fetch service versions at runtime — each service reports its own
  const [versions, setVersions] = useState(null);
  useEffect(() => {
    if (!viUserId) return;
    const v = { frontend: import.meta.env.VITE_APP_VERSION || '?' };
    Promise.allSettled([
      api.request('/api/config', {}, { retries: 0, timeout: 3000 }).then(c => { v.api = c?.service_version; }),
      api.authFetch('/api/nanoclaw/dashboard/version').then(r => r.json()).then(d => { v.nano = d?.version; }),
    ]).then(() => {
      setVersions(Object.values(v).some(x => x && x !== '?') ? v : null);
    });
  }, [viUserId]);

  return (
    <aside style={{
      width: 200, background: V.bgCard, borderRight: `1px solid ${V.border}`,
      display: 'flex', flexDirection: 'column', flexShrink: 0,
    }}>
      <div style={{ padding: '16px 14px', borderBottom: `1px solid ${V.border}` }}>
        <div style={{ fontSize: 15, fontWeight: 600, letterSpacing: '-0.02em', color: V.text }}>
          Playground
        </div>
        {envInfo?.environment && (
          <div style={{
            marginTop: 8, padding: '6px 0', textAlign: 'center', borderRadius: 6,
            fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em',
            background: ENV_COLORS[envInfo.environment] || ENV_COLORS.dev,
            color: '#fff',
          }}>
            {envInfo.environment || 'unknown'}
          </div>
        )}
      </div>
      <nav style={{ padding: 6, flex: 1, overflowY: 'auto' }}>
        {pages.map(p => (
          <NavLink
            key={p.id}
            to={`/playground/${p.id}`}
            style={({ isActive }) => ({
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '8px 10px', borderRadius: 8, cursor: 'pointer',
              fontSize: 12, textDecoration: 'none',
              background: isActive ? V.accent : 'transparent',
              color: isActive ? '#fff' : V.textDim,
              transition: 'all 0.15s',
            })}
            onMouseEnter={e => {
              if (!e.currentTarget.classList.contains('active')) e.currentTarget.style.background = V.bgHover;
            }}
            onMouseLeave={e => {
              if (!e.currentTarget.classList.contains('active')) e.currentTarget.style.background = 'transparent';
            }}
          >
            <span style={{ fontSize: 14, width: 18, textAlign: 'center' }}>{p.icon}</span>
            {p.label}
          </NavLink>
        ))}
      </nav>
      {/* Info panel — click anywhere to copy all */}
      <div style={{
        padding: '10px 14px', borderTop: `1px solid ${V.border}`,
        fontSize: 10, color: V.textDim, lineHeight: 1.6, cursor: 'pointer',
      }}
        title="Click to copy all"
        onClick={() => {
          const lines = [];
          if (viUserId) lines.push(`vi-user: ${viUserId}`);
          if (deviceId) lines.push(`device: ${deviceId}`);
          if (baseUrl) lines.push(`api: ${baseUrl.replace(/^https?:\/\//, '')}`);
          navigator.clipboard.writeText(lines.join('\n'));
        }}
      >
        <InfoRow label="vi-user" value={viUserId} />
        <InfoRow label="device" value={deviceId} />
        {baseUrl && <InfoRow label="api" value={baseUrl.replace(/^https?:\/\//, '')} />}
        {viUserId && (
          <div style={{ marginTop: 4 }}>
            <button
              onClick={async (e) => {
                e.stopPropagation();
                try {
                  const { tokenStore } = await import('../services/api.js');
                  const token = tokenStore.getAccess();
                  if (!token) { alert('Not signed in'); return; }
                  await navigator.clipboard.writeText(token);
                  e.target.textContent = 'Copied!';
                  setTimeout(() => { e.target.textContent = 'Copy Token'; }, 1500);
                } catch (err) { alert('Failed: ' + err.message); }
              }}
              style={{
                background: 'transparent', border: `1px solid ${V.border}`,
                borderRadius: 4, padding: '2px 8px', fontSize: 9, color: V.textDim,
                cursor: 'pointer', width: '100%',
              }}
            >Copy Token</button>
          </div>
        )}
        {/* Service versions — fetched at runtime from each service */}
        {versions && (
          <>
            <div style={{ borderTop: `1px solid ${V.border}`, margin: '6px 0' }} />
            {Object.entries(versions).map(([label, ver]) =>
              ver && ver !== '?' ? <InfoRow key={label} label={label} value={ver} /> : null
            )}
          </>
        )}
      </div>
      <div style={{
        padding: '8px 14px', borderTop: `1px solid ${V.border}`,
        display: 'flex', gap: 6,
      }}>
        <button
          onClick={async () => {
            try {
              const { logoutLocal } = await import('../services/api.js');
              logoutLocal();
              window.location.href = '/';
            } catch (e) {
              alert('退出失败: ' + (e.message || e));
            }
          }}
          style={{
            flex: 1, padding: '6px 0', border: `1px solid ${V.border}`, borderRadius: 6,
            background: 'transparent', color: V.text, fontSize: 11,
            cursor: 'pointer', textAlign: 'center',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = V.bgHover; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
        >
          退出登录
        </button>
        <button
          onClick={async () => {
            if (!window.confirm(
              '⚠️ 确认注销账号？\n\n注销后您的所有资源（包括账户余额）将被回收且不可恢复。此操作不可撤销。'
            )) return;
            if (!window.confirm('再次确认：您确定要永久注销此账号吗？')) return;
            try {
              await api.deleteAccount();
              const { logoutLocal } = await import('../services/api.js');
              logoutLocal();
              window.location.href = '/';
            } catch (e) {
              alert('注销失败: ' + (e.message || e));
            }
          }}
          style={{
            flex: 1, padding: '6px 0', border: '1px solid rgba(255,77,79,0.4)', borderRadius: 6,
            background: 'transparent', color: '#ff4d4f', fontSize: 11,
            cursor: 'pointer', textAlign: 'center',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,77,79,0.1)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
        >
          注销账号
        </button>
      </div>
      <div style={{
        padding: '12px 14px', borderTop: `1px solid ${V.border}`,
        fontSize: 11, color: V.textDim,
      }}>
        <span style={{
          display: 'inline-block', width: 6, height: 6, borderRadius: '50%',
          background: V.green, marginRight: 6, verticalAlign: 'middle',
        }} />
        Connected
      </div>
    </aside>
  );
}

function InfoRow({ label, value }) {
  if (!value) return null;
  return (
    <div style={{ display: 'flex', gap: 4, alignItems: 'baseline' }}>
      <span style={{ color: V.textDim, flexShrink: 0 }}>{label}</span>
      <span style={{
        color: V.text, fontFamily: V.font, fontSize: 9,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        opacity: 0.8,
      }}
        title={value}
      >
        {value}
      </span>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// CHAT PAGE — mirrors dashboard chat-layout
// ══════════════════════════════════════════════════════════════════════

// ── ChatInputBar — iOS-style floating pill (matching LiveSessionView) ──

function ChatInputBar({ onSend, disabled, isExecuting, onStop, selectedModel, onModelChange, lockedModel, models = [] }) {
  const [text, setText] = useState('');
  const [images, setImages] = useState([]);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const fileRef = useRef(null);
  const inputRef = useRef(null);

  const handleSend = useCallback(() => {
    if (!text.trim() && images.length === 0) return;
    onSend(text.trim(), images.map(i => i.url));
    setText('');
    setImages([]);
    if (inputRef.current) inputRef.current.style.height = 'auto';
  }, [text, images, onSend]);

  const handleFileSelect = useCallback(async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    setShowAddMenu(false);
    for (const file of files) {
      try {
        const isVideo = file.type.startsWith('video/');
        let url;
        if (isVideo) {
          const ext = file.name.match(/\.(mp4|mov|webm)$/i)?.[1]?.toLowerCase() || 'mp4';
          url = await api.uploadBlob(file, ext);
        } else {
          const dataUrl = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (ev) => resolve(ev.target.result);
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });
          url = await api.uploadDataUrl(dataUrl);
        }
        if (url) setImages(prev => [...prev, { url, name: file.name, isVideo }]);
      } catch (err) { console.error('Upload failed:', err); }
    }
  }, []);

  const hasContent = text.trim() || images.length > 0;

  return (
    <div style={{ padding: '8px 16px 12px', background: V.bgCard }}>
      {/* Image preview strip */}
      {images.length > 0 && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap', padding: '0 4px' }}>
          {images.map((img, i) => (
            <div key={i} style={{ position: 'relative' }}>
              {img.isVideo || /\.(mp4|mov|webm)$/i.test(img.url) ? (
                <div style={{ width: 56, height: 56, borderRadius: 12, overflow: 'hidden', position: 'relative', background: '#000' }}>
                  <video src={img.url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} preload="metadata" muted />
                  <div style={{
                    position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: 'rgba(0,0,0,0.2)', color: '#fff', fontSize: 16,
                  }}>&#9654;</div>
                </div>
              ) : (
                <img src={img.url} alt="" style={{ width: 56, height: 56, borderRadius: 12, objectFit: 'cover' }} />
              )}
              <button
                onClick={() => setImages(prev => prev.filter((_, j) => j !== i))}
                style={{
                  position: 'absolute', top: -6, right: -6, width: 20, height: 20,
                  borderRadius: '50%', background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none',
                  fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  backdropFilter: 'blur(4px)',
                }}
              >×</button>
            </div>
          ))}
        </div>
      )}
      <div style={{
        display: 'flex', alignItems: 'flex-end', gap: 6,
        padding: '6px 8px', borderRadius: 28,
        background: 'rgba(255,255,255,0.85)',
        backdropFilter: 'blur(40px)',
        WebkitBackdropFilter: 'blur(40px)',
        boxShadow: '0 0 0 1px rgba(0,0,0,0.06), 0 4px 24px rgba(0,0,0,0.06)',
      }}>
        {/* + button */}
        <button
          onClick={() => { setShowAddMenu(prev => !prev); fileRef.current?.click(); }}
          style={{
            width: 36, height: 36, borderRadius: '50%', border: 'none',
            background: showAddMenu ? 'rgba(0,0,0,0.08)' : 'rgba(0,0,0,0.04)',
            color: showAddMenu ? 'rgba(0,0,0,0.5)' : 'rgba(0,0,0,0.25)',
            cursor: 'pointer', fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'all 0.3s',
            transform: showAddMenu ? 'rotate(45deg)' : 'none',
          }}
        >+</button>
        {/* Model selector — inline, between + and text input */}
        {lockedModel ? (
          <span style={{
            fontSize: 12, color: V.textDim, opacity: 0.7, whiteSpace: 'nowrap', userSelect: 'none',
          }}>
            🔒 {models.find(m => m.id === lockedModel)?.label || lockedModel}
          </span>
        ) : selectedModel && onModelChange ? (
          <select
            value={selectedModel}
            onChange={e => { onModelChange(e.target.value); try { localStorage.setItem('playground-model', e.target.value); } catch (_) { /* storage unavailable */ } }}
            style={{
              background: 'rgba(0,0,0,0.04)', border: 'none', borderRadius: 18,
              color: 'rgba(0,0,0,0.55)', fontSize: 13, padding: '6px 12px', cursor: 'pointer',
              outline: 'none', fontFamily: V.fontUi, height: 36,
              WebkitAppearance: 'none', appearance: 'none',
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%23999' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
              backgroundRepeat: 'no-repeat', backgroundPosition: 'right 8px center',
              paddingRight: 24,
            }}
          >
            {models.map(m => (
              <option key={m.id} value={m.id}>{m.label}</option>
            ))}
          </select>
        ) : null}
        {/* Text input */}
        <textarea
          ref={inputRef}
          value={text}
          onChange={e => { setText(e.target.value); e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 96) + 'px'; }}
          onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); handleSend(); } }}
          placeholder="Ask anything..."
          disabled={disabled}
          rows={1}
          style={{
            flex: 1, background: 'transparent', border: 'none', outline: 'none',
            fontSize: 15, fontWeight: 400, color: '#000', letterSpacing: '-0.01em', minWidth: 0,
            resize: 'none', lineHeight: '1.4', maxHeight: 96, overflow: 'auto',
          }}
        />
        {/* Stop button (visible during execution) */}
        {isExecuting && onStop && (
          <button
            onClick={onStop}
            style={{
              width: 36, height: 36, borderRadius: '50%', border: 'none',
              background: '#ef4444', color: '#fff', cursor: 'pointer',
              fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.2s',
              boxShadow: '0 2px 8px rgba(239,68,68,0.3)',
            }}
            title="Stop"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
              <rect x="1" y="1" width="12" height="12" rx="2" />
            </svg>
          </button>
        )}
        {/* Send button */}
        <button
          onClick={handleSend}
          disabled={!hasContent || disabled}
          style={{
            width: 36, height: 36, borderRadius: '50%', border: 'none',
            background: hasContent ? '#000' : 'rgba(0,0,0,0.08)',
            color: hasContent ? '#fff' : 'rgba(0,0,0,0.3)',
            cursor: hasContent ? 'pointer' : 'default',
            fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'all 0.2s',
            boxShadow: hasContent ? '0 2px 8px rgba(0,0,0,0.2)' : 'none',
          }}
        >↑</button>
      </div>
      <input ref={fileRef} type="file" accept="image/*,video/mp4,video/webm,video/quicktime" multiple style={{ display: 'none' }} onChange={handleFileSelect} />
    </div>
  );
}

// ── LiveCardBlock — renders a streaming card from nanoClaw ──

function LiveCardBlock({ card }) {
  const isThinking = card.template === 'thinking' || card.template === 'thinking-process';

  // Thinking cards render directly — ThinkingPlanCard has its own styling
  if (isThinking) {
    return <CardRenderer card={card} />;
  }

  return (
    <div style={{
      background: V.bg, border: `1px solid ${V.border}`,
      borderRadius: 8, overflow: 'hidden', marginTop: 6,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '8px 12px', fontSize: 11, fontWeight: 600,
        textTransform: 'uppercase', letterSpacing: '0.04em',
        color: V.textDim, borderBottom: `1px solid ${V.border}`, background: V.bg,
      }}>
        {card.template || 'card'}
        <span style={{
          background: card.status === 'finalized' ? 'rgba(52,199,89,0.12)' : 'rgba(255,149,0,0.12)',
          color: card.status === 'finalized' ? V.green : V.yellow,
          padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 500, marginLeft: 'auto',
        }}>
          {card.status === 'finalized' ? 'done' : 'streaming'}
        </span>
      </div>
      <div style={{ background: '#fff', color: '#111' }}>
        <CardRenderer card={card} />
      </div>
    </div>
  );
}

// ── FetchModeSelector — developer-facing data-source picker ──

const FETCH_MODES = [
  { value: 'auto',    label: 'Auto' },
  { value: 'get',     label: 'GET' },
  { value: 'sse',     label: 'SSE' },
  { value: 'get+sse', label: 'GET+SSE' },
];

function FetchModeSelector({ mode, onChange, style }) {
  return (
    <select
      value={mode}
      onChange={e => onChange(e.target.value)}
      onClick={e => e.stopPropagation()}
      title="Data source (developer debug): Auto / GET / SSE / GET+SSE"
      style={{
        background: mode === 'auto' ? 'transparent' : 'rgba(88,86,214,0.15)',
        color: mode === 'auto' ? V.textDim : V.accent,
        border: `1px solid ${mode === 'auto' ? V.border : V.accent}`,
        borderRadius: 4, padding: '2px 6px', fontSize: 10, cursor: 'pointer',
        fontFamily: V.font, fontWeight: 600, lineHeight: 1,
        ...style,
      }}
    >
      {FETCH_MODES.map(m => (
        <option key={m.value} value={m.value}>{m.label}</option>
      ))}
    </select>
  );
}

function shouldFetchGet(mode, status, TERMINAL_STATUSES) {
  if (mode === 'get' || mode === 'get+sse') return true;
  if (mode === 'sse') return false;
  // auto: only terminal sessions fetch GET snapshot
  return !!(status && TERMINAL_STATUSES.has(status));
}

function shouldConnectSSE(mode, status, TERMINAL_STATUSES) {
  if (mode === 'sse' || mode === 'get+sse') return true;
  if (mode === 'get') return false;
  // auto: only active (non-terminal) sessions connect SSE
  return !(status && TERMINAL_STATUSES.has(status));
}

// ── ProcessingPill — live indicator while a task is queued / running ──

function ProcessingPill({ session }) {
  const liveTasks = (session?.tasks || []).filter(t => t.status === 'running' || t.status === 'queued');
  const [now, setNow] = useState(0);
  useEffect(() => { setNow(Date.now()); }, []);
  useEffect(() => {
    if (liveTasks.length === 0) return;
    const id = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(id);
  }, [liveTasks.length]);
  if (liveTasks.length === 0) return null;
  const t = liveTasks[0];

  // Source of truth: the RUNNING task's `executor` (set from the live
  // `exec_start.executor` event). Fall back to session-sticky only when the
  // task hasn't emitted exec_start yet.
  const taskExecutor = t.executor || null;
  const sessionExecutor = taskExecutor || session.context?.executor;
  const sessionModel = session.context?.model;
  const modelLabel = sessionModel
    ? (AVAILABLE_MODELS.find(m => m.id === sessionModel)?.label || sessionModel)
    : null;
  const execLabel = sessionExecutor === 'fast'
    ? '⚡ Fast · Gemini 3 Flash'
    : sessionExecutor === 'sdk'
      ? `🔷 SDK · ${modelLabel || 'model'}`
      : (modelLabel ? `🔷 ${modelLabel}` : 'processing');

  const cards = Object.values(t.cards || {});
  const liveCards = cards.filter(c => c.status !== 'finalized' && c.status !== 'done');
  const latest = (liveCards.length > 0 ? liveCards : cards).sort((a, b) => (b.ts || 0) - (a.ts || 0))[0];
  const tpl = latest?.template || '';
  const cardKind = (() => {
    if (!tpl) return null;
    if (tpl === 'thinking-process' || tpl === 'thinking') return { label: 'think', emoji: '🧠' };
    if (tpl === 'debug-html') return { label: 'tool', emoji: '🔧' };
    if (tpl === 'agent-speech' || tpl === 'md-text' || tpl === 'freeform-html') return { label: 'text', emoji: '💬' };
    if (tpl === 'ask-user-question') return { label: 'ask', emoji: '❓' };
    if (tpl === 'todo-list') return { label: 'plan', emoji: '✅' };
    if (tpl.startsWith('md-')) return { label: 'data', emoji: '📊' };
    return { label: tpl, emoji: '🃏' };
  })();

  const startedTs = t.dispatched_at ? new Date(t.dispatched_at).getTime() : (t.ts || session.latestTs || now);
  const elapsedSec = Math.max(0, Math.floor((now - startedTs) / 1000));
  const elapsedLabel = elapsedSec < 60 ? `${elapsedSec}s` : `${Math.floor(elapsedSec / 60)}m${elapsedSec % 60}s`;

  if (t.status === 'queued') {
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 6,
        fontSize: 10, fontWeight: 500, whiteSpace: 'nowrap',
        background: 'rgba(255,149,0,0.12)', color: V.yellow,
      }} title={`Task ${t.taskId || ''} queued`}>
        <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: 3, background: V.yellow, animation: 'spin 1.2s linear infinite' }} />
        Queued · {elapsedLabel}
      </span>
    );
  }

  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, padding: '2px 8px', borderRadius: 6,
      fontSize: 10, fontWeight: 500, whiteSpace: 'nowrap',
      background: 'rgba(255,149,0,0.12)', color: V.yellow,
    }} title={`Task ${t.taskId || ''} running — executor=${sessionExecutor || 'unset'}, model=${sessionModel || 'unset'}`}>
      <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: 3, background: V.yellow, animation: 'spin 1.2s linear infinite' }} />
      Processing
      <span style={{ opacity: 0.85 }}>· {execLabel}</span>
      {cardKind && (
        <span style={{
          padding: '0 5px', borderRadius: 3, background: 'rgba(255,149,0,0.18)',
          color: V.yellow, fontSize: 9, fontWeight: 600, letterSpacing: '0.04em',
        }}>{cardKind.emoji} {cardKind.label}</span>
      )}
      <span style={{ opacity: 0.7 }}>· {elapsedLabel}</span>
    </span>
  );
}

// ── Session Info Header — collapsible metadata bar above chat messages ──

function SessionInfoHeader({ session, rawMode, onToggleRaw, fetchMode, onChangeFetchMode }) {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  const [shareUrl, setShareUrl] = useState(null);

  // Pre-fetch share URL so the click handler can write to the clipboard
  // synchronously. iOS Safari rejects navigator.clipboard.writeText when
  // it isn't in the same task as the user gesture — any `await` between
  // the click and the write breaks the chain and the write silently fails.
  useEffect(() => {
    const sid = session?.sessionId;
    if (!sid) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await api.request(`/api/users/sessions/${sid}/share`, { method: 'POST' });
        if (!cancelled) setShareUrl(`${window.location.origin}/playground/share/${data.token}`);
      } catch {
        const viUserId = api.getViUserId();
        if (viUserId && !cancelled) {
          setShareUrl(`${window.location.origin}/playground/share/${viUserId}/${sid}`);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [session?.sessionId]);

  const copyWithFallback = (text) => {
    let ok = false;
    try {
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(text);
        ok = true;
      }
    } catch (_) { /* fall through */ }
    if (!ok) {
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        ok = true;
      } catch (_) { /* give up */ }
    }
    return ok;
  };

  const flashCopied = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = () => {
    if (copied) return;
    // Fast path — prefetched URL: stay synchronous so iOS Safari accepts
    // the clipboard write inside the user-gesture task.
    if (shareUrl) {
      if (copyWithFallback(shareUrl)) flashCopied();
      return;
    }
    // Lazy fallback — only reached if prefetch hasn't completed yet.
    (async () => {
      try {
        const data = await api.request(`/api/users/sessions/${session.sessionId}/share`, { method: 'POST' });
        const url = `${window.location.origin}/playground/share/${data.token}`;
        setShareUrl(url);
        if (copyWithFallback(url)) flashCopied();
      } catch {
        const viUserId = api.getViUserId();
        if (viUserId) {
          const url = `${window.location.origin}/playground/share/${viUserId}/${session.sessionId}`;
          setShareUrl(url);
          if (copyWithFallback(url)) flashCopied();
        }
      }
    })();
  };

  const firstPrompt = session.tasks?.[0]?.prompt || '';
  const skillSlug = session.tasks?.[0]?.skillSlug;
  const status = session.status || 'unknown';
  const startedAt = session.latestTs ? new Date(session.latestTs).toLocaleString() : null;
  const taskCount = session.tasks?.length || 0;
  const hasTimeline = session.timeline?.length > 0;
  // Session-level executor badge must reflect LATEST task's executor when a
  // task is live or was the most recent one. session.context.executor is
  // sticky-read from Redis at session-FETCH time and lags real turns.
  const latestTask = (() => {
    const ts = session.tasks || [];
    if (ts.length === 0) return null;
    const running = ts.find(t => t.status === 'running' || t.status === 'queued' || t.status === 'loading');
    if (running) return running;
    return ts.reduce((a, b) => ((b._ts || 0) > (a._ts || 0) ? b : a));
  })();
  const effectiveExecutor = (latestTask?.executor) || session.context?.executor;
  const cardTemplates = new Set();
  if (session.result?.cards) {
    for (const card of Object.values(session.result.cards)) {
      if (card.template) cardTemplates.add(card.template);
    }
  }

  const statusColor = {
    completed: V.green, active: V.blue, failed: V.red,
    queued: V.yellow, created: V.textDim,
    stopped: '#ff9500', timeout: V.red,
  }[status] || V.textDim;

  const pill = (text, bg, color) => (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 6,
      fontSize: 10, fontWeight: 500, background: bg, color,
      whiteSpace: 'nowrap',
    }}>{text}</span>
  );

  return (
    <div style={{
      borderBottom: `1px solid ${V.border}`, background: V.bgCard,
      fontSize: 12, flexShrink: 0,
    }}>
      {/* Summary row — always visible */}
      <div className="pg-session-header-row" style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '8px 16px', cursor: 'pointer',
      }} onClick={() => setExpanded(!expanded)}>
        <span style={{ fontSize: 10, color: V.textDim }}>{expanded ? '▼' : '▶'}</span>
        {pill(status, `${statusColor}20`, statusColor)}
        {skillSlug && pill(formatSkillTag(skillSlug), 'rgba(175,82,222,0.12)', V.purple)}
        <span style={{
          flex: 1, overflow: 'hidden', textOverflow: 'ellipsis',
          whiteSpace: 'nowrap', color: V.text, fontWeight: 500,
        }}>
          {session.title || firstPrompt || session.sessionId}
        </span>
        <span style={{ fontSize: 10, color: V.textDim }}>{taskCount} task{taskCount !== 1 ? 's' : ''}</span>
        {hasTimeline && <span style={{ fontSize: 10, color: V.textDim }}>{session.timeline.length} rounds</span>}
        {session.context?.model && (
          <span style={{
            display: 'inline-block', padding: '2px 8px', borderRadius: 6,
            fontSize: 10, fontWeight: 500, background: 'rgba(175,82,222,0.08)',
            color: V.purple, whiteSpace: 'nowrap',
          }}>
            {AVAILABLE_MODELS.find(m => m.id === session.context.model)?.label || session.context.model}
          </span>
        )}
        {effectiveExecutor && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 3, padding: '2px 8px', borderRadius: 6,
            fontSize: 10, fontWeight: 500, whiteSpace: 'nowrap',
            background: effectiveExecutor === 'fast' ? 'rgba(52,199,89,0.12)' : 'rgba(88,86,214,0.12)',
            color: effectiveExecutor === 'fast' ? '#2f855a' : '#5856d6',
          }} title={`Session executor: ${effectiveExecutor}${latestTask?.executor && latestTask.executor !== session.context?.executor ? ' (from live task)' : ''}`}>
            {effectiveExecutor === 'fast' ? '⚡ Fast' : '🔷 SDK'}
          </span>
        )}
        <ProcessingPill session={session} />

        {session.sessionId && (
          <button
            onClick={e => { e.stopPropagation(); handleShare(); }}
            style={{
              background: copied ? V.green : V.accent, color: '#fff', border: 'none',
              borderRadius: 4, padding: '3px 10px', fontSize: 10, cursor: 'pointer',
              fontWeight: 600, transition: 'background 0.2s',
            }}
          >
            {copied ? 'Copied!' : 'Share'}
          </button>
        )}
        {onChangeFetchMode && (
          <FetchModeSelector
            mode={fetchMode || 'auto'}
            onChange={onChangeFetchMode}
          />
        )}
        {onToggleRaw && (
          <RawToggleButton
            active={rawMode}
            onClick={e => { e.stopPropagation(); onToggleRaw(); }}
          />
        )}
      </div>
      {/* Expanded details */}
      {expanded && (
        <div style={{
          padding: '0 16px 10px', display: 'grid',
          gridTemplateColumns: '1fr 1fr', gap: '4px 24px',
          fontSize: 11, color: V.textDim, lineHeight: 1.8,
        }}>
          <div><b style={{ color: V.text }}>Session ID</b></div>
          <div style={{ fontFamily: V.font, fontSize: 10, wordBreak: 'break-all' }}>{session.sessionId}</div>
          {api.getViUserId() && <>
            <div><b style={{ color: V.text }}>VI User ID</b></div>
            <div style={{ fontFamily: V.font, fontSize: 10 }}>{api.getViUserId()}</div>
          </>}
          {startedAt && <>
            <div><b style={{ color: V.text }}>Started</b></div>
            <div>{startedAt}</div>
          </>}
          {skillSlug && <>
            <div><b style={{ color: V.text }}>Executor</b></div>
            <div>{skillSlug}</div>
          </>}
          {cardTemplates.size > 0 && <>
            <div><b style={{ color: V.text }}>Card Templates</b></div>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {[...cardTemplates].map(t => (
                <span key={t} style={{
                  background: V.bgHover, padding: '1px 6px', borderRadius: 4,
                  fontSize: 10, fontFamily: V.font,
                }}>{t}</span>
              ))}
            </div>
          </>}
          {session.artifacts?.length > 0 && <>
            <div><b style={{ color: V.text }}>Artifacts</b></div>
            <div>{session.artifacts.length} file{session.artifacts.length !== 1 ? 's' : ''}</div>
          </>}
          {shareUrl && <>
            <div><b style={{ color: V.text }}>Share URL</b></div>
            <div style={{
              fontFamily: V.font, fontSize: 10, wordBreak: 'break-all',
              cursor: 'pointer', color: V.accent,
            }} onClick={handleShare} title="Click to copy">{shareUrl}</div>
          </>}
        </div>
      )}
    </div>
  );
}

// ── Unified Timeline: merges historical tasks + live blocks into one sorted list ──

function UnifiedTimeline({ sortedTasks, allCards, cardTaskMap, liveBlocks, sending, rawMode }) {
  const [perCardRaw, setPerCardRaw] = useState({});
  const toggleCardRaw = useCallback((cardId) => {
    setPerCardRaw(prev => ({ ...prev, [cardId]: !prev[cardId] }));
  }, []);
  // Flat list, sorted by ts. Dedup cards by cardId, prompts by text.
  const messages = useMemo(() => {
    const items = [];
    const seenCardIds = new Set();

    // Collect live prompt texts for dedup against historical
    const livePromptTexts = new Set();
    for (const b of liveBlocks) {
      if (b.type === 'user' && b.text) livePromptTexts.add(b.text);
    }

    // Historical: user prompts + cards + text results
    for (const task of sortedTasks) {
      // Skip historical prompt if live already has it (prevents duplicate during active chat)
      if (task.prompt && !livePromptTexts.has(task.prompt)) {
        items.push({
          key: `prompt-${task.taskId}`,
          type: 'user',
          text: task.prompt,
          mediaUrls: task.mediaUrls || [],
          ts: task.ts || 0,
        });
      }
      for (const [id, card] of Object.entries(task.cards || {})) {
        if (cardTaskMap[id] !== task) continue;
        seenCardIds.add(id);
        // debug-html cards are partitioned out into the DebugPanel below — skip here.
        if (card.template === 'debug-html') continue;
        items.push({
          key: `card-${id}`,
          type: 'card',
          card: { cardId: id, ...card },
          ts: card.ts || task.ts || 0,
        });
      }
      if (task.result && !hasContentCards(task.cards)) {
        items.push({
          key: `result-${task.taskId}`,
          type: 'text-result',
          text: task.result,
          ts: (task.ts || 0) + 1,
        });
      }
    }

    // Live: user inputs + cards + errors — skip cards already in historical
    for (let i = 0; i < liveBlocks.length; i++) {
      const b = liveBlocks[i];
      if (b.type === 'card' && b.card?.cardId && seenCardIds.has(b.card.cardId)) continue;
      // Live debug-html cards also partitioned out into DebugPanel.
      if (b.type === 'card' && b.card?.template === 'debug-html') continue;
      items.push({
        key: `live-${b.type}-${b.type === 'card' ? b.card?.cardId : i}`,
        ...b,
        ts: b.ts || 0,
      });
    }

    // Sort everything by ts ascending. That's the only rule.
    items.sort((a, b) => (a.ts || 0) - (b.ts || 0));
    return items;
  }, [sortedTasks, allCards, cardTaskMap, liveBlocks]);

  // Collect debug-html cards (historical + live) for the bottom DebugPanel.
  // Sorted by ts so the panel reads chronologically.
  const debugCards = useMemo(() => {
    const out = [];
    const seen = new Set();
    for (const task of sortedTasks) {
      for (const [id, card] of Object.entries(task.cards || {})) {
        if (cardTaskMap[id] !== task) continue;
        if (card.template !== 'debug-html') continue;
        seen.add(id);
        out.push({ cardId: id, ...card, ts: card.ts || task.ts || 0 });
      }
    }
    for (const b of liveBlocks) {
      if (b.type !== 'card' || !b.card) continue;
      if (b.card.template !== 'debug-html') continue;
      if (b.card.cardId && seen.has(b.card.cardId)) continue;
      out.push({ ...b.card, ts: b.ts || 0 });
    }
    out.sort((a, b) => (a.ts || 0) - (b.ts || 0));
    return out;
  }, [sortedTasks, cardTaskMap, liveBlocks]);

  return (
    <>
      {messages.map(msg => {
        if (msg.type === 'user') return (
          <div key={msg.key} style={{ marginBottom: 16, maxWidth: '80%', marginLeft: 'auto' }}>
            <div style={{
              padding: '12px 16px', borderRadius: 12, fontSize: 14,
              lineHeight: 1.5, wordBreak: 'break-word',
              background: V.accent, color: '#fff',
              borderBottomRightRadius: 4,
            }}>
              {msg.mediaUrls?.length > 0 && (
                <div style={{ marginBottom: 8 }}>
                  {msg.mediaUrls.map((url, j) => (
                    _isVideoUrl(url) ? (
                      <video key={j} src={url} controls playsInline preload="metadata" style={{
                        maxWidth: 200, maxHeight: 150, borderRadius: 8, margin: 2,
                      }} />
                    ) : (
                      <img key={j} src={url} alt="" style={{
                        maxWidth: 200, maxHeight: 150, borderRadius: 8, margin: 2,
                      }} onError={e => { e.target.style.display = 'none'; }} />
                    )
                  ))}
                </div>
              )}
              {msg.text}
            </div>
            <div style={{ fontSize: 11, color: V.textDim, marginTop: 4, padding: '0 4px', textAlign: 'right' }}>
              {msg.skillSlug && (
                <span style={{
                  background: 'rgba(175,82,222,0.12)', color: V.purple,
                  padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 500,
                  marginRight: 6,
                }}>
                  {formatSkillTag(msg.skillSlug)}
                </span>
              )}
              {formatDateTime(msg.ts)}
            </div>
          </div>
        );

        if (msg.type === 'card') {
          const card = msg.card;
          const isThinkingCard = card.template === 'thinking' || card.template === 'thinking-process';
          const isLive = msg.key.startsWith('live-');
          const cardTime = (msg.ts || card.ts || card.createdAt) ? formatDateTime(msg.ts || card.ts || card.createdAt) : '';

          // Thinking cards + live cards — render with CardRenderer/LiveCardBlock + time
          if (isThinkingCard || isLive) {
            return (
              <div key={msg.key} style={{ marginBottom: 16, maxWidth: '80%', marginRight: 'auto' }}>
                {isLive && !isThinkingCard ? <LiveCardBlock card={card} /> : <CardRenderer card={card} />}
                {cardTime && <div style={{ fontSize: 11, color: V.textDim, marginTop: 4, padding: '0 4px' }}>{cardTime}</div>}
              </div>
            );
          }

          // Historical content cards — full header with status badge
          const statusBadge = card.status === 'finalized'
            ? { bg: 'rgba(52,199,89,0.12)', color: V.green, text: 'done' }
            : { bg: 'rgba(255,149,0,0.12)', color: V.yellow, text: 'streaming' };
          const showRaw = rawMode || perCardRaw[card.cardId];

          return (
            <div key={msg.key} style={{ marginBottom: 16, maxWidth: '80%', marginRight: 'auto' }}>
              <div style={{
                background: V.bg, border: `1px solid ${V.border}`,
                borderRadius: 8, overflow: 'hidden', marginTop: 6,
              }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '8px 12px', fontSize: 11, fontWeight: 600,
                  textTransform: 'uppercase', letterSpacing: '0.04em',
                  color: V.textDim, borderBottom: `1px solid ${V.border}`,
                  background: V.bg,
                }}>
                  {card.template || 'card'}
                  <span style={{ fontWeight: 400, fontSize: 10, marginLeft: 'auto' }}>
                    {cardTime}
                  </span>
                  <span style={{
                    background: statusBadge.bg, color: statusBadge.color,
                    padding: '2px 8px', borderRadius: 6, fontSize: 10,
                    fontWeight: 500,
                  }}>
                    {statusBadge.text}
                  </span>
                  <RawToggleButton
                    active={showRaw}
                    onClick={() => toggleCardRaw(card.cardId)}
                  />
                </div>
                <div style={{ background: '#fff', color: '#111', maxHeight: '80vh', overflow: 'auto' }}>
                  {showRaw ? <RawDataView card={card} /> : <CardRenderer card={card} />}
                </div>
              </div>
            </div>
          );
        }

        if (msg.type === 'text-result') return (
          <div key={msg.key} style={{ marginBottom: 16, maxWidth: '80%', marginRight: 'auto' }}>
            <div style={{
              padding: '12px 16px', borderRadius: 12, fontSize: 14,
              lineHeight: 1.5, wordBreak: 'break-word',
              background: V.bgCard, border: `1px solid ${V.border}`,
              borderBottomLeftRadius: 4, color: V.text,
              whiteSpace: 'pre-wrap',
            }}>
              {msg.text}
            </div>
            <div style={{ fontSize: 11, color: V.textDim, marginTop: 4, padding: '0 4px' }}>
              {formatDuration(msg.durationMs)}
            </div>
          </div>
        );

        if (msg.type === 'error') return (
          <div key={msg.key} style={{ marginBottom: 16, maxWidth: '80%', marginRight: 'auto' }}>
            <div style={{ padding: '8px 12px', borderRadius: 8, background: 'rgba(255,59,48,0.1)', color: V.red, fontSize: 13 }}>
              Error: {msg.text}
            </div>
          </div>
        );

        return null;
      })}

      {/* Thinking indicator */}
      {sending && liveBlocks.filter(b => b.type === 'card').length === 0 && (
        <div style={{ marginBottom: 16, maxWidth: '80%', marginRight: 'auto' }}>
          <div style={{ padding: '10px 16px', borderRadius: 12, background: V.bgCard, border: `1px solid ${V.border}`, borderBottomLeftRadius: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 16, animation: 'pulse 1.5s infinite' }}>🧠</span>
            <span style={{ fontSize: 13, color: V.textDim }}>Thinking...</span>
          </div>
        </div>
      )}

      {/* Debug panel — agent-speak tool I/O cards, collapsed by default (Issue #1007) */}
      <DebugPanel cards={debugCards} />
    </>
  );
}

// ── PromptInspector — expandable debug view of the assembled Gemini prompt ──

function PromptInspector() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleToggle = useCallback(async () => {
    const next = !open;
    setOpen(next);
    if (next) {
      // Always re-fetch on expand
      setLoading(true);
      try {
        const result = await api.getBriefPrompt();
        setData(result);
      } catch (err) {
        setData({ error: err.message });
      } finally {
        setLoading(false);
      }
    }
  }, [open]);

  return (
    <div style={{ marginTop: 8 }}>
      <button
        onClick={handleToggle}
        style={{
          background: 'none', border: 'none', padding: 0,
          fontSize: 12, color: V.textDim, cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 4,
        }}
      >
        <span style={{ fontSize: 10, transition: 'transform 0.15s', transform: open ? 'rotate(90deg)' : 'none' }}>
          {'\u25B6'}
        </span>
        Prompt
        {data?.package_count != null && (
          <span style={{ color: V.accent, fontFamily: V.font }}>({data.package_count} packages)</span>
        )}
      </button>
      {open && (
        <div style={{
          marginTop: 8, padding: '12px 14px', borderRadius: 10,
          background: V.bgCard, border: `1px solid ${V.border}`,
          fontSize: 11, fontFamily: V.font, lineHeight: 1.6,
          maxHeight: 400, overflowY: 'auto',
        }}>
          {loading && <div style={{ color: V.textDim }}>Loading...</div>}
          {data?.error && <div style={{ color: V.red }}>{data.error}</div>}
          {data && !data.error && (
            <>
              <div style={{ marginBottom: 10 }}>
                <span style={{ fontWeight: 700, color: V.accent }}>Model: </span>
                <span style={{ color: V.text }}>{data.model}</span>
              </div>
              <div style={{ marginBottom: 10 }}>
                <span style={{ fontWeight: 700, color: V.accent }}>System Prompt:</span>
                <pre style={{
                  margin: '4px 0 0', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                  color: V.text, fontSize: 10, padding: 8, borderRadius: 6,
                  background: V.bg,
                }}>
                  {data.system_prompt}
                </pre>
              </div>
              <div style={{ marginBottom: 10 }}>
                <span style={{ fontWeight: 700, color: V.accent }}>Context Text:</span>
                <pre style={{
                  margin: '4px 0 0', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                  color: V.text, fontSize: 10, padding: 8, borderRadius: 6,
                  background: V.bg,
                }}>
                  {data.context_text}
                </pre>
              </div>
              {data.package_ids && (
                <div>
                  <span style={{ fontWeight: 700, color: V.accent }}>Valid Package IDs ({data.package_ids.length}):</span>
                  <div style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {data.package_ids.map(id => (
                      <span key={id} style={{
                        padding: '1px 6px', borderRadius: 4,
                        background: 'rgba(52,199,89,0.1)', color: V.green,
                        fontSize: 10,
                      }}>
                        #{id}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// INTENTION PAGE — image upload → streaming brief → intention cards → start session
// ══════════════════════════════════════════════════════════════════════

function IntentionPage() {
  const navigate = useNavigate();
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [summary, setSummary] = useState('');
  const [intentions, setIntentions] = useState(null);
  const [mediaContext, setMediaContext] = useState(null); // Issue #1002: per-image descriptions from brief
  const [error, setError] = useState(null);
  const [dispatching, setDispatching] = useState(null);
  const availableModels = AVAILABLE_MODELS;
  const [selectedModel, setSelectedModel] = useState(() => {
    try { return localStorage.getItem('playground-model') || DEFAULT_MODEL; } catch { return DEFAULT_MODEL; }
  });
  const fileRef = useRef(null);
  const abortRef = useRef(null);
  const dropRef = useRef(null);

  // Timing state
  const [timings, setTimings] = useState(null); // { start, upload, firstToken, summaryDone, intentions, complete }
  const [elapsed, setElapsed] = useState(0);
  // Issue #1015: correlation id for /profile <session_id> — shown in the
  // timing bar AND reused as the actual DB session_id when the user clicks
  // an intention. Same id end-to-end (Loki, Langfuse, Redis quick_response,
  // /api/users/exec session_id).
  const [briefSessionId, setBriefSessionId] = useState(null);
  const [briefSessionIdCopied, setBriefSessionIdCopied] = useState(false);

  // Robust copy with execCommand fallback (some browser contexts block
  // navigator.clipboard silently). Sets short "Copied!" feedback.
  const copyBriefSessionId = useCallback(() => {
    if (!briefSessionId) return;
    let ok = false;
    try {
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(briefSessionId);
        ok = true;
      }
    } catch (_) { /* fall through */ }
    if (!ok) {
      try {
        const ta = document.createElement('textarea');
        ta.value = briefSessionId;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        ok = true;
      } catch (_) { /* give up */ }
    }
    if (ok) {
      setBriefSessionIdCopied(true);
      setTimeout(() => setBriefSessionIdCopied(false), 1200);
    }
  }, [briefSessionId]);
  const timerRef = useRef(null);

  // Live elapsed timer — ticks every 100ms while streaming
  useEffect(() => {
    if (streaming && timings?.start) {
      timerRef.current = setInterval(() => setElapsed(Date.now() - timings.start), 100);
    } else {
      clearInterval(timerRef.current);
      if (timings?.complete) setElapsed(timings.complete);
    }
    return () => clearInterval(timerRef.current);
  }, [streaming, timings?.start, timings?.complete]);

  // Cleanup abort on unmount
  useEffect(() => () => { abortRef.current?.(); clearInterval(timerRef.current); }, []);

  const handleFiles = useCallback(async (files) => {
    setUploading(true);
    setError(null);
    const uploadStart = Date.now();
    try {
      // Parallel upload — all files concurrently. Track per-file content-hash
      // cache hits so the timing badge can say "(cached)" when nothing
      // actually hit the network.
      let cacheHits = 0;
      const uploadPromises = files.map(async (file) => {
        const isVideo = file.type.startsWith('video/');
        const onCached = () => { cacheHits += 1; };
        let s3Url;
        if (isVideo) {
          const ext = file.name.match(/\.(mp4|mov|webm)$/i)?.[1]?.toLowerCase() || 'mp4';
          s3Url = await api.uploadBlob(file, ext, { onCached });
        } else {
          const dataUrl = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (ev) => resolve(ev.target.result);
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });
          s3Url = await api.uploadDataUrl(dataUrl, { onCached });
        }
        return { url: s3Url, name: file.name, isVideo };
      });
      const results = await Promise.all(uploadPromises);
      setImages(prev => [...prev, ...results.filter(Boolean)]);
      setTimings(prev => ({
        ...prev,
        upload: Date.now() - uploadStart,
        uploadCacheHits: cacheHits,
        uploadCount: files.length,
      }));
    } catch (err) {
      setError(`Upload failed: ${err.message}`);
    } finally {
      setUploading(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = Array.from(e.dataTransfer?.files || []).filter(f => f.type.startsWith('image/') || f.type.startsWith('video/'));
    if (files.length) handleFiles(files);
  }, [handleFiles]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleSend = useCallback(() => {
    if (images.length === 0) return;
    const t0 = Date.now();
    setStreaming(true);
    setSummary('');
    setIntentions(null);
    setMediaContext(null);
    setError(null);
    setBriefSessionId(null);
    setTimings(prev => ({ upload: prev?.upload, start: t0 }));
    setElapsed(0);

    let gotFirstToken = false;
    const imageUrls = images.map(i => i.url);
    const abort = api.streamSessionBrief(imageUrls, null, {
      onSessionId: (id) => setBriefSessionId(id),
      onSummaryToken: (token) => {
        if (!gotFirstToken) {
          gotFirstToken = true;
          setTimings(prev => ({ ...prev, firstToken: Date.now() - t0 }));
        }
        setSummary(prev => prev + token);
      },
      onIntentions: (items) => {
        setTimings(prev => ({ ...prev, intentions: Date.now() - t0 }));
        setIntentions(items);
      },
      onMedia: (media) => {
        // Issue #1002: per-image descriptions + cache_hit flag.
        setTimings(prev => ({ ...prev, media: Date.now() - t0 }));
        setMediaContext(media);
      },
      onComplete: () => {
        setTimings(prev => ({ ...prev, complete: Date.now() - t0 }));
        setStreaming(false);
      },
      onError: (err) => {
        setTimings(prev => ({ ...prev, complete: Date.now() - t0 }));
        setError(err.message || 'Brief failed');
        setStreaming(false);
      },
    });
    abortRef.current = abort;
  }, [images]);

  const handleStartSession = useCallback(async (intention) => {
    setDispatching(intention.title);
    setError(null);
    try {
      const hashtag = intention.hashtag;
      const skillSlug = hashtag ? hashtag.replace(/^#/, '') : undefined;
      // Include #skill-slug in prompt so exec routing picks it up even if skillSlug param is stripped
      const prompt = intention.title
        + (intention.description ? ` — ${intention.description}` : '')
        + (skillSlug ? ` #${skillSlug}` : '');
      const result = await api.dispatchExec({
        prompt,
        skillSlug,
        mediaUrls: images.map(i => i.url),
        // Issue #1015: reuse the brief/stream session_id so /exec hits the
        // pre-cached `vi:session:{uid}:{sid}:quick_response` HASH and can
        // replay the agent-speech card before the agent boots.
        sessionId: briefSessionId || undefined,
        // Issue #1015: model selected on the intention page — propagates to
        // session.context.model so the chat top bar can display it.
        model: selectedModel,
      });
      if (result?.sessionId) {
        navigate(`/playground/chat/${result.sessionId}`);
      }
    } catch (err) {
      setError(`Failed to start session: ${err.message}`);
    } finally {
      setDispatching(null);
    }
  }, [images, navigate, briefSessionId, selectedModel]);

  // Issue #1015: free-text dispatch from the ChatInputBar below the
  // intention cards. Same dispatch shape as handleStartSession but without
  // the skill_slug — the user typed a custom prompt, so let nanoclaw's
  // routing pick the executor. We DO pass the brief session_id so the
  // (still-cached) media descriptions are reused, and pass the uploaded
  // images so the agent can see them just like an intention click would.
  const handleFreeTextDispatch = useCallback(async (text, extraImageUrls) => {
    if (!text || !text.trim()) return;
    setDispatching('__free_text__');
    setError(null);
    try {
      // Combine images already uploaded for the brief with any extra images
      // that ChatInputBar uploaded inline.
      const briefImageUrls = images.map(i => i.url);
      const mediaUrls = [...briefImageUrls, ...(extraImageUrls || [])];
      const result = await api.dispatchExec({
        prompt: text.trim(),
        mediaUrls,
        sessionId: briefSessionId || undefined,
        model: selectedModel,
      });
      if (result?.sessionId) {
        navigate(`/playground/chat/${result.sessionId}`);
      }
    } catch (err) {
      setError(`Failed to start session: ${err.message}`);
    } finally {
      setDispatching(null);
    }
  }, [images, briefSessionId, selectedModel, navigate]);

  const removeImage = useCallback((idx) => {
    setImages(prev => prev.filter((_, i) => i !== idx));
    setSummary('');
    setIntentions(null);
    setMediaContext(null);
    setTimings(null);
    setElapsed(0);
    setBriefSessionId(null);
  }, []);

  const hasResults = summary || intentions || mediaContext;
  const fmtMs = (ms) => ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;

  return (
    // The outer wrapper fills the parent `<main>` (which has `flex: 1`
    // inside a flex row in PlaygroundLayout). `height: 100%` is required
    // because `<main>` is a block element — `flex: 1` on children would
    // otherwise be ignored and the scroll container would size to its
    // content instead of the viewport.
    <div style={{
      height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden',
      background: V.bg,
    }}>
      {/* Inner scroll container. minHeight: 0 lets it shrink under the
          max-content of its flex children so overflowY actually kicks in. */}
      <div style={{
        flex: 1, minHeight: 0, overflowY: 'auto',
        padding: '24px 20px 40px',
      }}>
        <div style={{
          display: 'flex', flexDirection: 'column', gap: 20,
          maxWidth: 640, width: '100%', margin: '0 auto',
        }}>
        {/* Header */}
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 600, color: V.text, margin: 0, letterSpacing: '-0.02em' }}>
            Intention
          </h2>
          <p style={{ fontSize: 13, color: V.textDim, margin: '4px 0 0' }}>
            Upload photos and get AI-generated intention suggestions to start a session.
          </p>
          <PromptInspector />
        </div>

        {/* Image Upload Zone */}
        <div
          ref={dropRef}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onClick={() => { if (!uploading) fileRef.current?.click(); }}
          style={{
            border: `2px dashed ${V.border}`,
            borderRadius: 16,
            padding: images.length > 0 ? 12 : '40px 20px',
            textAlign: 'center',
            cursor: uploading ? 'wait' : 'pointer',
            background: V.bgCard,
            transition: 'all 0.2s',
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = V.accent; e.currentTarget.style.background = V.accentDim; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = V.border; e.currentTarget.style.background = V.bgCard; }}
        >
          {images.length === 0 ? (
            <div>
              <div style={{ fontSize: 32, marginBottom: 8 }}>{uploading ? '...' : '\uD83D\uDCF7'}</div>
              <div style={{ fontSize: 14, color: V.text, fontWeight: 500 }}>
                {uploading ? 'Uploading...' : 'Drop images here or click to upload'}
              </div>
              <div style={{ fontSize: 12, color: V.textDim, marginTop: 4 }}>
                Supports JPG, PNG, WebP
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
              {images.map((img, i) => (
                <div key={i} style={{ position: 'relative' }}>
                  {img.isVideo || _isVideoUrl(img.url) ? (
                    <div style={{ width: 80, height: 80, borderRadius: 12, overflow: 'hidden', position: 'relative', background: '#000' }}>
                      <video src={img.url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} preload="metadata" muted />
                      <div style={{
                        position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: 'rgba(0,0,0,0.2)', color: '#fff', fontSize: 20,
                      }}>&#9654;</div>
                    </div>
                  ) : (
                    <img
                      src={img.url}
                      alt={img.name}
                      style={{ width: 80, height: 80, borderRadius: 12, objectFit: 'cover' }}
                    />
                  )}
                  <button
                    onClick={(e) => { e.stopPropagation(); removeImage(i); }}
                    style={{
                      position: 'absolute', top: -6, right: -6, width: 22, height: 22,
                      borderRadius: '50%', background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none',
                      fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >{'\u00D7'}</button>
                </div>
              ))}
              {uploading && (
                <div style={{
                  width: 80, height: 80, borderRadius: 12, background: V.bgHover,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 12, color: V.textDim,
                }}>
                  Uploading...
                </div>
              )}
            </div>
          )}
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/*,video/mp4,video/webm,video/quicktime"
          multiple
          style={{ display: 'none' }}
          onChange={(e) => {
            const files = Array.from(e.target.files || []);
            e.target.value = '';
            if (files.length) handleFiles(files);
          }}
        />

        {/* Upload timing badge */}
        {timings?.upload && !hasResults && (
          <div style={{ fontSize: 11, color: V.textDim, textAlign: 'center', marginTop: -12 }}>
            Uploaded in {fmtMs(timings.upload)}
            {timings.uploadCount > 0 && timings.uploadCacheHits === timings.uploadCount && (
              <span style={{ color: V.green, marginLeft: 4 }}>(cached)</span>
            )}
            {timings.uploadCount > 0 && timings.uploadCacheHits > 0 && timings.uploadCacheHits < timings.uploadCount && (
              <span style={{ color: V.green, marginLeft: 4 }}>
                ({timings.uploadCacheHits}/{timings.uploadCount} cached)
              </span>
            )}
          </div>
        )}

        {/* Send Button */}
        {images.length > 0 && !hasResults && (
          <button
            onClick={handleSend}
            disabled={streaming || uploading}
            style={{
              padding: '12px 24px',
              borderRadius: 12,
              border: 'none',
              background: streaming ? V.textDim : V.accent,
              color: '#fff',
              fontSize: 15,
              fontWeight: 600,
              cursor: streaming ? 'wait' : 'pointer',
              transition: 'all 0.2s',
              boxShadow: `0 2px 12px ${V.accent}40`,
            }}
          >
            {streaming ? 'Analyzing...' : 'Analyze Images'}
          </button>
        )}

        {/* Error */}
        {error && (
          <div style={{
            padding: '10px 14px', borderRadius: 10,
            background: 'rgba(255,59,48,0.08)', color: V.red,
            fontSize: 13, fontWeight: 500,
          }}>
            {error}
          </div>
        )}

        {/* Timing Bar — live elapsed + phase deltas */}
        {timings?.start && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
            padding: '8px 12px', borderRadius: 10,
            background: V.bgCard, border: `1px solid ${V.border}`,
            fontSize: 11, fontFamily: V.font, color: V.textDim,
          }}>
            <span style={{
              fontWeight: 700, fontSize: 13, color: streaming ? V.accent : V.green,
              fontVariantNumeric: 'tabular-nums',
            }}>
              {fmtMs(elapsed)}
            </span>
            {briefSessionId && (
              <span
                title={`Click to copy full session_id. Use with /profile <session_id> or as Langfuse trace id.\n\n${briefSessionId}`}
                onClick={copyBriefSessionId}
                style={{
                  padding: '1px 6px', borderRadius: 4,
                  background: briefSessionIdCopied ? 'rgba(52,199,89,0.18)' : 'rgba(255,255,255,0.06)',
                  color: briefSessionIdCopied ? V.green : V.textDim,
                  cursor: 'pointer',
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  userSelect: 'all',
                  transition: 'background 0.15s, color 0.15s',
                }}
              >
                {briefSessionIdCopied ? '✓ copied' : `sid ${briefSessionId}`}
              </span>
            )}
            {timings.upload && (
              <span style={{ padding: '1px 6px', borderRadius: 4, background: 'rgba(0,122,255,0.08)', color: V.blue }}>
                upload {fmtMs(timings.upload)}
                {timings.uploadCount > 0 && timings.uploadCacheHits === timings.uploadCount && ' · cached'}
              </span>
            )}
            {timings.firstToken && (
              <span style={{ padding: '1px 6px', borderRadius: 4, background: 'rgba(52,199,89,0.08)', color: V.green }}>
                TTFT {fmtMs(timings.firstToken)}
              </span>
            )}
            {timings.intentions && (
              <span style={{ padding: '1px 6px', borderRadius: 4, background: 'rgba(88,86,214,0.08)', color: V.accent }}>
                intentions {fmtMs(timings.intentions)}
              </span>
            )}
            {timings.media && (
              <span style={{ padding: '1px 6px', borderRadius: 4, background: 'rgba(255,149,0,0.08)', color: V.yellow }}>
                media {fmtMs(timings.media)}
              </span>
            )}
            {timings.complete && (
              <span style={{ padding: '1px 6px', borderRadius: 4, background: 'rgba(52,199,89,0.08)', color: V.green }}>
                done {fmtMs(timings.complete)}
              </span>
            )}
            {streaming && <span style={{ animation: 'pulse 1s infinite' }}>...</span>}
          </div>
        )}

        {/* Conversational opening — VI's first line. Hook + lead-in to the
            intention list. NOT a dry caption (see BRIEF_STREAM_PROMPT). */}
        {summary && (
          <div style={{
            display: 'flex', gap: 12, alignItems: 'flex-start',
            padding: '14px 16px 16px',
            borderRadius: 16,
            background: `linear-gradient(135deg, ${V.accent}14, ${V.accent}05)`,
            border: `1px solid ${V.accent}26`,
          }}>
            <div style={{
              flexShrink: 0,
              width: 32, height: 32, borderRadius: '50%',
              background: V.accent, color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 16, fontWeight: 700,
              boxShadow: `0 2px 8px ${V.accent}40`,
            }}>
              VI
            </div>
            <div style={{
              flex: 1, minWidth: 0,
              fontSize: 15, lineHeight: 1.55, color: V.text,
              fontWeight: 500,
            }}>
              {summary}
              {streaming && (
                <span style={{ animation: 'pulse 1s infinite', opacity: 0.5, marginLeft: 2 }}>|</span>
              )}
            </div>
          </div>
        )}

        {/* Per-image media context — Issue #1002: brief/stream now writes
            vlm descriptions to the shared Redis registry. This block surfaces
            them so the user can verify what the agent will see at /exec time,
            including whether the description was served from cache. */}
        {mediaContext && mediaContext.length > 0 && (
          <div style={{
            padding: '16px 18px', borderRadius: 14,
            background: V.bgCard, border: `1px solid ${V.border}`,
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10,
            }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: V.textDim, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Image Context
              </div>
              <span style={{ fontSize: 11, color: V.textDim }}>
                {mediaContext.length} image{mediaContext.length > 1 ? 's' : ''}
                {' · '}
                {mediaContext.filter(m => m.cache_hit).length}/{mediaContext.length} cache hit
              </span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {mediaContext.map((m, i) => {
                const img = images[i];
                return (
                  <div key={m.ref || i} style={{
                    display: 'flex', gap: 12, alignItems: 'flex-start',
                    padding: 10, borderRadius: 10,
                    background: V.bg,
                  }}>
                    {img && !img.isVideo && !_isVideoUrl(img.url) && (
                      <img
                        src={img.url}
                        alt=""
                        style={{ width: 56, height: 56, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }}
                      />
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        marginBottom: 4,
                      }}>
                        <code style={{
                          fontSize: 11, fontFamily: V.font, color: V.accent,
                          background: 'rgba(88,86,214,0.08)',
                          padding: '2px 6px', borderRadius: 4,
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                          maxWidth: 220,
                        }}>
                          media://{m.media_id || '—'}
                        </code>
                        <span style={{
                          fontSize: 10, fontWeight: 600,
                          padding: '2px 6px', borderRadius: 4,
                          background: m.cache_hit ? 'rgba(52,199,89,0.12)' : 'rgba(0,122,255,0.10)',
                          color: m.cache_hit ? V.green : V.blue,
                          textTransform: 'uppercase', letterSpacing: '0.03em',
                        }}>
                          {m.cache_hit ? 'cached' : 'fresh'}
                        </span>
                      </div>
                      <div style={{ fontSize: 12, color: m.description ? V.text : V.red, lineHeight: 1.5 }}>
                        {m.description || `(unavailable: ${m.error || 'unknown'})`}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Intention Cards */}
        {intentions && intentions.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: V.textDim, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Suggestions
            </div>
            {intentions.map((intent, i) => (
              <div
                key={i}
                style={{
                  padding: '14px 16px',
                  borderRadius: 14,
                  background: V.bgCard,
                  border: `1px solid ${V.border}`,
                  transition: 'all 0.15s',
                  cursor: 'pointer',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = V.accent; e.currentTarget.style.boxShadow = `0 2px 12px ${V.accent}20`; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = V.border; e.currentTarget.style.boxShadow = 'none'; }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                  <span style={{ fontSize: 24, flexShrink: 0, lineHeight: 1 }}>
                    {intent.icon || '\uD83D\uDCA1'}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: V.text, marginBottom: 4 }}>
                      {intent.title}
                    </div>
                    {intent.description && (
                      <div style={{ fontSize: 13, color: V.textDim, lineHeight: 1.5 }}>
                        {intent.description}
                      </div>
                    )}
                    {/* Issue #1015: developer-facing playground surfaces the
                        pre-cached quick_response so you can see what the
                        agent will say if this card is clicked. */}
                    {intent.quick_response && (
                      <div style={{
                        marginTop: 8,
                        padding: '6px 10px',
                        borderRadius: 8,
                        background: 'rgba(88,86,214,0.08)',
                        borderLeft: `2px solid ${V.accent}`,
                        fontSize: 12,
                        color: V.text,
                        fontStyle: 'italic',
                        lineHeight: 1.4,
                      }}
                        title="Pre-cached quick_response — replayed as an agent-speech card before the agent boots (Issue #1015)"
                      >
                        🗣️ {intent.quick_response}
                      </div>
                    )}
                    {intent.hashtag && (
                      <span style={{
                        display: 'inline-block', marginTop: 6,
                        padding: '2px 8px', borderRadius: 6,
                        background: intent.matched ? 'rgba(52,199,89,0.1)' : 'rgba(255,149,0,0.1)',
                        color: intent.matched ? V.green : V.yellow,
                        fontSize: 11, fontWeight: 500,
                      }}
                        title={intent.matched ? 'Matches installed package' : 'No matching package'}
                      >
                        {intent.hashtag}{!intent.matched && ' ?'}
                      </span>
                    )}
                    {/* Issue #1015: dump any extra fields the LLM returned
                        beyond the canonical icon/title/description/hashtag/
                        quick_response set. Helps when iterating on the
                        prompt — new fields show up automatically without
                        a frontend code change. */}
                    {(() => {
                      const known = new Set(['icon', 'title', 'description', 'hashtag', 'matched', 'quick_response']);
                      const extras = Object.entries(intent).filter(([k]) => !known.has(k));
                      if (extras.length === 0) return null;
                      return (
                        <details style={{ marginTop: 6 }}>
                          <summary style={{ fontSize: 10, color: V.textDim, cursor: 'pointer', userSelect: 'none' }}>
                            + {extras.length} extra field{extras.length > 1 ? 's' : ''}
                          </summary>
                          <pre style={{
                            margin: '4px 0 0 0', padding: '6px 8px',
                            borderRadius: 6, background: 'rgba(0,0,0,0.04)',
                            fontSize: 10, color: V.textDim,
                            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                            overflow: 'auto', maxHeight: 200,
                            whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                          }}>
                            {JSON.stringify(Object.fromEntries(extras), null, 2)}
                          </pre>
                        </details>
                      );
                    })()}
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleStartSession(intent); }}
                    disabled={dispatching !== null}
                    style={{
                      padding: '8px 14px',
                      borderRadius: 10,
                      border: 'none',
                      background: dispatching === intent.title ? V.textDim : V.accent,
                      color: '#fff',
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: dispatching !== null ? 'wait' : 'pointer',
                      flexShrink: 0,
                      transition: 'all 0.2s',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {dispatching === intent.title ? 'Starting...' : 'Start Session'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Issue #1015: free-text dispatch — reuses ChatInputBar so users
            can either click an intention OR type their own prompt to start
            a session. Shown only AFTER the brief returns intentions (so
            the model picker doesn't clutter the upload-only state).
            Inherits the model selector from this page's local state. */}
        {intentions && intentions.length > 0 && (
          <div style={{
            marginTop: 8,
            border: `1px solid ${V.border}`,
            borderRadius: 16,
            background: V.bgCard,
            overflow: 'hidden',
          }}>
            <div style={{
              padding: '8px 16px 4px',
              fontSize: 11, fontWeight: 600,
              color: V.textDim,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
            }}>
              Or type your own prompt
            </div>
            <ChatInputBar
              onSend={(text, extraImageUrls) => handleFreeTextDispatch(text, extraImageUrls)}
              disabled={dispatching !== null}
              isExecuting={false}
              selectedModel={selectedModel}
              onModelChange={setSelectedModel}
              models={availableModels}
            />
          </div>
        )}

        {/* Reset button after results */}
        {hasResults && !streaming && (
          <button
            onClick={() => {
              setImages([]);
              setSummary('');
              setIntentions(null);
              setMediaContext(null);
              setError(null);
              setTimings(null);
              setElapsed(0);
              abortRef.current?.();
            }}
            style={{
              padding: '10px 20px', borderRadius: 10,
              border: `1px solid ${V.border}`, background: 'transparent',
              color: V.textDim, fontSize: 13, fontWeight: 500,
              cursor: 'pointer', alignSelf: 'center',
            }}
          >
            Start Over
          </button>
        )}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// CHAT PAGE — mirrors dashboard chat-layout
// ══════════════════════════════════════════════════════════════════════

function ChatPage({ nanoClaw, sseEvents, viUserId }) {
  const { sessionId: urlSessionId } = useParams();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [selectedSessionId, setSelectedSessionId] = useState(urlSessionId || null);
  const [sessionSearch, setSessionSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [liveBlocks, setLiveBlocks] = useState([]);
  const [sending, setSending] = useState(false);
  const availableModels = AVAILABLE_MODELS;
  const [selectedModel, setSelectedModel] = useState(() => {
    try { return localStorage.getItem('playground-model') || DEFAULT_MODEL; } catch { return DEFAULT_MODEL; }
  });
  const [rawMode, setRawMode] = useState(false);
  // Data-source mode for the current session view — developer debug.
  // auto (default): active → SSE, terminal → GET snapshot.
  // get / sse / get+sse: override both gates explicitly.
  const [fetchMode, setFetchMode] = useState(() => {
    try { return localStorage.getItem('playground-fetch-mode') || 'auto'; } catch { return 'auto'; }
  });
  const onChangeFetchMode = useCallback((next) => {
    setFetchMode(next);
    try { localStorage.setItem('playground-fetch-mode', next); } catch { /* ignore */ }
  }, []);
  const [usageOpen, setUsageOpen] = useState(false);
  const [usageData, setUsageData] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const messagesEndRef = useRef(null);

  // Terminal session statuses — no SSE needed, content from single GET call
  const TERMINAL_STATUSES = useMemo(() => new Set(['completed', 'failed', 'stopped', 'timeout']), []);

  // ── Per-session SSE: only for active (non-terminal) sessions ──
  const sessionSseOnEvent = useCallback((event) => {
    if (nanoClaw?.processEvent) nanoClaw.processEvent(event);
  }, [nanoClaw]);
  // Derive SSE session: null for terminal sessions → hook disconnects.
  // Also null when sessions haven't loaded yet (selectedSession_ not found) —
  // avoids a wasted SSE connection attempt before we know the status.
  const selectedSession_ = sessions.find(s => s.sessionId === selectedSessionId);
  const sseSessionId = (() => {
    if (!selectedSessionId) return null;
    if (sessions.length > 0 && !selectedSession_) return null;
    if (!shouldConnectSSE(fetchMode, selectedSession_?.status, TERMINAL_STATUSES)) return null;
    return selectedSessionId;
  })();
  useSessionEventStream(viUserId, sseSessionId, { onEvent: sessionSseOnEvent });

  // ── On-demand session detail (fetched when switching to a session) ──
  const [sessionDetailCache, setSessionDetailCache] = useState({});

  // Load session list from API — lightweight sidebar data only.
  const loadSessions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let apiSessions;
      try {
        apiSessions = await api.getSessions({ summary: true });
      } catch {
        apiSessions = await api.getSessionsByDevice(null, { summary: true });
      }
      // Normalize API sessions — sidebar data only (no result/timeline/artifacts).
      // Full detail is fetched on-demand via sessionDetailCache.
      const normalized = (apiSessions || []).map(s => {
        const ts = new Date(s.started_at || s.dispatched_at || 0).getTime();
        return {
          sessionId: s.id,
          tasks: s.prompt ? [{ prompt: s.prompt, ts, sessionId: s.id, skillSlug: s.context?.skill_slug || null }] : [],
          latestTs: ts,
          status: s.status,
          title: s.title,
          thumbnail_url: s.thumbnail_url || null,
          metadata: s.metadata || null,
          result_html: s.result_html || '',
          result_summary: s.result_summary || '',
          context: s.context || null,
        };
      });
      setSessions(normalized.sort((a, b) => b.latestTs - a.latestTs));
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (viUserId) loadSessions(); }, [loadSessions, viUserId]);

  // Auto-refresh session list when session_update SSE events arrive.
  // This ensures Flutter-initiated sessions appear in the sidebar without manual refresh,
  // and selected session data updates when a session completes.
  const lastSessionUpdateRef = useRef(0);
  useEffect(() => {
    if (!sseEvents || sseEvents.length === 0) return;
    const latest = sseEvents[sseEvents.length - 1];
    if (!latest || latest._ts <= lastSessionUpdateRef.current) return;
    if (latest.type !== 'session_update') return;
    lastSessionUpdateRef.current = latest._ts;
    // Invalidate detail cache for this session — status changed, need fresh data
    const updatedSid = latest.session_id;
    if (updatedSid && detailCacheRef.current[updatedSid]) {
      setSessionDetailCache(prev => {
        const next = { ...prev };
        delete next[updatedSid];
        return next;
      });
    }
    // Debounce: avoid rapid reloads for burst events
    const timer = setTimeout(() => loadSessions(), 500);
    return () => clearTimeout(timer);
  }, [sseEvents, loadSessions]);

  // Sync URL sessionId → local state
  useEffect(() => {
    setSelectedSessionId(urlSessionId || null);
  }, [urlSessionId]);

  // Fetch full session detail on demand (for completed sessions — no SSE replay).
  // Uses a ref for the cache check to avoid recreating selectSession on every cache update.
  const detailCacheRef = useRef(sessionDetailCache);
  useEffect(() => { detailCacheRef.current = sessionDetailCache; }, [sessionDetailCache]);

  const fetchSessionDetail = useCallback(async (sid) => {
    if (!sid || detailCacheRef.current[sid]) return detailCacheRef.current[sid];
    try {
      const detail = await api.getSession(sid);
      setSessionDetailCache(prev => ({ ...prev, [sid]: detail }));
      return detail;
    } catch (err) {
      console.warn('[session-detail] fetch failed:', sid, err.message);
      return null;
    }
  }, []);

  // Navigate helper — sync local state + URL + switch nanoClaw session scope
  const selectSession = useCallback((sid) => {
    setSelectedSessionId(sid);
    // Read model from session context when selecting an existing session
    if (sid) {
      const sess = sessions.find(s => s.sessionId === sid);
      setSelectedModel(sess?.context?.model || DEFAULT_MODEL);
      // For terminal sessions, fetch full detail (no SSE will replay cards)
      if (sess && TERMINAL_STATUSES.has(sess.status)) {
        fetchSessionDetail(sid);
      }
    } else {
      try { setSelectedModel(localStorage.getItem('playground-model') || DEFAULT_MODEL); } catch (_) { setSelectedModel(DEFAULT_MODEL); }
    }
    // Switch nanoClaw session scope: keeps cards/tasks belonging to the new session
    // (preserves live thinking cards from cross-client sessions like Flutter),
    // drops cards from other sessions to prevent leakage.
    if (nanoClaw?.switchSession) nanoClaw.switchSession(sid);
    else if (nanoClaw?.reset) nanoClaw.reset(sid);
    if (sid) {
      navigate(`/playground/chat/${sid}`);
    } else {
      navigate('/playground/chat');
    }
  }, [navigate, nanoClaw, sessions, TERMINAL_STATUSES, fetchSessionDetail]);

  // ── Send message via NanoClaw ──
  const handleSend = useCallback(async (text, mediaUrls, overrideSessionId) => {
    // `overrideSessionId` lets callers (e.g. card-reply bubble-up) target the
    // card's OWN session instead of the currently-selected one — guards
    // against stale React state after rapid navigation.
    const targetSessionId = overrideSessionId || selectedSessionId;
    // Routing hashtags (#exec_sdk / #exec_fast) are backend directives, not
    // user content. Strip them from the displayed user bubble so the UI
    // shows a clean message. The full text (with routing tags) still goes
    // to dispatchExec so server-side routing works.
    const displayText = (text || '').replace(/\s*#exec_(?:sdk|fast)\b/gi, '').replace(/\s{2,}/g, ' ').trim();
    setLiveBlocks(prev => [...prev, {
      type: 'user', text: displayText, mediaUrls: mediaUrls || [], ts: Date.now(),
    }]);

    // If a task is actively running, inject input instead of dispatching new exec.
    // The input is buffered and applied at the next SDK turn boundary.
    const running = (() => {
      if (!nanoClaw?.tasks) return null;
      const entries = Object.entries(nanoClaw.tasks);
      for (let i = entries.length - 1; i >= 0; i--) {
        const [id, t] = entries[i];
        if (t.status === 'loading' || t.status === 'streaming') return { taskId: id, sessionId: t.sessionId };
      }
      return null;
    })();

    if (running && targetSessionId) {
      try {
        await api.injectInput(targetSessionId, running.taskId, text, mediaUrls);
      } catch (err) {
        console.error('injectInput failed:', err);
      }
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
      return;
    }

    setSending(true);
    try {
      const opts = {
        prompt: text,
        mediaUrls: mediaUrls?.length ? mediaUrls : undefined,
      };
      if (targetSessionId) opts.sessionId = targetSessionId;
      // Always pass selected model — ensures existing sessions use the UI-selected model
      if (selectedModel) opts.model = selectedModel;
      const result = await api.dispatchExec(opts);
      // Set active session so nanoClaw card events get scoped
      if (result?.sessionId && nanoClaw?.setActiveSessionId) {
        const newSid = result.sessionId;
        nanoClaw.setActiveSessionId(newSid);
        if (newSid !== selectedSessionId) {
          setSelectedSessionId(newSid);
          navigate(`/playground/chat/${newSid}`);
        }
      }
      // Refresh session list from API — the session exists in DB immediately.
      setTimeout(() => loadSessions(), 1000);
    } catch (err) {
      console.error('dispatchExec failed:', err);
      setLiveBlocks(prev => [...prev, { type: 'error', text: err.message, ts: Date.now() }]);
    } finally {
      setSending(false);
    }
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  }, [selectedSessionId, selectedModel, nanoClaw, selectSession, navigate, loadSessions]);

  // Listen for ask-user-question Submit replies bubbled up by TemplateCardPreview.
  // Submit is treated like the user typed the answer + the option's hashtags
  // and pressed Send.
  //   option   →  "<label> <hashtags space-joined>"   (e.g. "Build site #exec_sdk")
  //   custom   →  "<text>"
  // Multi-tab answers concatenate with newlines.
  useEffect(() => {
    const onCardReply = (e) => {
      const detail = e?.detail || {};
      if (detail.template !== 'ask-user-question') return;
      const answers = detail.answers || {};
      const parts = [];
      for (const tabId of Object.keys(answers)) {
        const a = answers[tabId];
        if (!a) continue;
        if (a.kind === 'option') {
          const label = a.label || a.optionId || '';
          const tags = Array.isArray(a.hashtags) && a.hashtags.length > 0
            ? a.hashtags.filter((h) => typeof h === 'string' && h.startsWith('#')).join(' ')
            : '';
          parts.push(tags ? `${label} ${tags}` : label);
        } else if (a.kind === 'custom') {
          parts.push(a.text);
        }
      }
      const text = parts.filter(Boolean).join('\n');
      if (!text) return;
      // Pass the card's OWN sessionId so the follow-up dispatch lands on
      // the right session — guards against stale selectedSessionId after
      // rapid navigate().
      const cardSessionId = detail.sessionId || selectedSessionId;
      try { handleSend(text, [], cardSessionId); } catch (err) { console.warn('vi-card-reply send failed:', err); }

      // Persist the answered state. Optimistic local write first so the UI
      // flips to the highlighted/answered state immediately — no wait for
      // the SSE echo of the POST.
      const replyCardId = detail.cardId;
      const cardState = nanoClaw?.cards?.[replyCardId];
      if (replyCardId && cardSessionId && cardState?.template) {
        const fullData = { ...(cardState.data || {}), status: 'answered', answers };

        nanoClaw.processEvent?.({
          op: 'replace_card',
          cardId: replyCardId,
          sessionId: cardSessionId,
          template: cardState.template,
          data: fullData,
        });
        nanoClaw.processEvent?.({
          op: 'finalize_card',
          cardId: replyCardId,
          sessionId: cardSessionId,
        });

        // Persist to server (fire-and-forget). Its SSE echo is idempotent
        // vs useSessionStore's replace_card handler — no flicker.
        api.updateSessionCard(cardSessionId, replyCardId, cardState.template, fullData)
          .catch((err) => console.warn('[vi-card-reply] card update failed:', err));
      }
    };
    window.addEventListener('vi-card-reply', onCardReply);
    return () => window.removeEventListener('vi-card-reply', onCardReply);
  }, [handleSend, selectedSessionId, nanoClaw]);

  // ── Execution state — detect running tasks for stop button ──
  const activeTask = useMemo(() => {
    if (!nanoClaw?.tasks) return null;
    const entries = Object.entries(nanoClaw.tasks);
    // Include 'queued' + 'error' in addition to 'loading'/'streaming' so
    // Stop stays reachable when a task is waiting to run or already
    // failed. Especially important on errors: if FAST aborts → SDK
    // hand-off also fails, the server may keep the task row alive and
    // the user needs a way to explicitly cancel without reloading. Also
    // catches tasks stuck queued behind a failed dispatch.
    for (let i = entries.length - 1; i >= 0; i--) {
      const [id, t] = entries[i];
      if (t.status === 'loading' || t.status === 'streaming' ||
          t.status === 'queued' || t.status === 'error') {
        return { taskId: id, sessionId: t.sessionId, status: t.status };
      }
    }
    return null;
  }, [nanoClaw?.tasks]);

  const handleStop = useCallback(async () => {
    if (!activeTask) return;
    try {
      await api.stopSession(activeTask.sessionId || selectedSessionId, activeTask.taskId);
    } catch (err) {
      console.error('stopSession failed:', err);
    }
    // Locally mark the task as 'stopped' so the UI clears its spinner /
    // processing pill immediately even if the server's stop signal
    // doesn't round-trip a `exec_stopped` event (e.g., FAST already
    // finished erroring; manager.abortTask is a no-op). Without this,
    // tasks stuck in 'error' would stay showing a Stop button that
    // never goes away.
    nanoClaw?.markTaskStopped?.(activeTask.taskId);
  }, [activeTask, selectedSessionId, nanoClaw]);

  // ── Stream live cards from nanoClaw (append new, update existing, keep all) ──
  const seenCardIdsRef = useRef(new Set());
  useEffect(() => {
    if (!nanoClaw?.cards) return;
    const orderedIds = nanoClaw.orderedCardIds || [];
    const newBlocks = [];
    for (const cardId of orderedIds) {
      const card = nanoClaw.cards[cardId];
      if (!card) continue;
      if (!seenCardIdsRef.current.has(cardId)) {
        seenCardIdsRef.current.add(cardId);
        if (!(typeof card.createdAt === 'number' && card.createdAt > 0)) {
          console.warn(
            `[Playground:ts] live card cardId=${cardId} template=${card.template} ` +
            `has no createdAt — falling back to Date.now() (will display as now). ` +
            `Upstream store failed to resolve a WAL timestamp; check useSessionStore.js logs.`,
          );
        }
        const blockTs = (typeof card.createdAt === 'number' && card.createdAt > 0) ? card.createdAt : Date.now();
        newBlocks.push({ type: 'card', card: { cardId, ...card }, ts: blockTs });
      }
    }
    // Append new cards (don't replace existing ones — preserves thinking cards + order)
    if (newBlocks.length > 0) {
      setLiveBlocks(prev => [...prev, ...newBlocks]);
    }
    // Update existing card data in-place (for streaming updates).
    // Also refresh ts if the card's createdAt was corrected by a WAL
    // merge (e.g. eager thinking card overwritten by authoritative ts).
    setLiveBlocks(prev => prev.map(b => {
      if (b.type !== 'card') return b;
      const updated = nanoClaw.cards[b.card.cardId];
      if (!updated) return b;
      const newTs = (typeof updated.createdAt === 'number' && updated.createdAt > 0) ? updated.createdAt : b.ts;
      return { ...b, ts: newTs, card: { ...b.card, ...updated } };
    }));
  }, [nanoClaw?.cards, nanoClaw?.orderedCardIds]);

  // ── Delete a session (called from the per-row × button) ──
  const handleDeleteSession = useCallback(async (sid, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    if (!sid) return;
    if (!window.confirm('Delete this session? This cannot be undone.')) return;
    try {
      await api.deleteSession(sid);
    } catch (err) {
      window.alert(`Delete failed: ${err?.message || err}`);
      return;
    }
    setSessions(prev => prev.filter(s => s.sessionId !== sid));
    if (selectedSessionId === sid) {
      setSelectedSessionId(null);
      setLiveBlocks([]);
      seenCardIdsRef.current.clear();
      if (nanoClaw?.reset) nanoClaw.reset();
      navigate('/playground/chat');
    }
  }, [selectedSessionId, nanoClaw, navigate]);

  // Clear live blocks when switching between sessions (not on initial assignment).
  // "null → sid" (new chat dispatch) keeps blocks; "sidA → sidB" clears them.
  const prevSessionIdRef = useRef(null);
  useEffect(() => {
    const prev = prevSessionIdRef.current;
    prevSessionIdRef.current = selectedSessionId;
    if (prev && prev !== selectedSessionId) {
      setLiveBlocks([]);
      if (seenCardIdsRef?.current) seenCardIdsRef.current.clear();
    }
  }, [selectedSessionId]);

  const selectedSession = sessions.find(s => s.sessionId === selectedSessionId);

  // Merge sidebar summary with detail cache to get full session data.
  // For terminal sessions: detail comes from GET /sessions/{id} (cached).
  // For active sessions: SSE replays cards live, detail not needed.
  const selectedSessionFull = useMemo(() => {
    if (!selectedSession) return null;
    const detail = sessionDetailCache[selectedSession.sessionId];
    if (!detail) return selectedSession; // not yet fetched — sidebar data only
    // Merge: sidebar fields + detail fields (detail has result, timeline, artifacts)
    return {
      ...selectedSession,
      result: detail.result || null,
      timeline: detail.timeline || [],
      artifacts: detail.artifacts || [],
      context: detail.context || selectedSession.context,
      metadata: detail.metadata || selectedSession.metadata || null,
    };
  }, [selectedSession, sessionDetailCache]);

  // Auto-fetch detail. Default (auto): only terminal sessions. Manual override
  // (get / get+sse): fetch regardless of status. SSE-only: skip entirely.
  useEffect(() => {
    if (!selectedSessionId || !selectedSession) return;
    if (!shouldFetchGet(fetchMode, selectedSession.status, TERMINAL_STATUSES)) return;
    if (!sessionDetailCache[selectedSessionId]) {
      fetchSessionDetail(selectedSessionId);
    }
  }, [selectedSessionId, selectedSession, TERMINAL_STATUSES, sessionDetailCache, fetchSessionDetail, fetchMode]);

  // On fetchMode change, invalidate cached snapshot + clear SSE-derived state
  // for the current session so each mode renders from a fresh fetch/replay.
  useEffect(() => {
    if (!selectedSessionId) return;
    setSessionDetailCache(prev => {
      if (!prev[selectedSessionId]) return prev;
      const next = { ...prev };
      delete next[selectedSessionId];
      return next;
    });
    setLiveBlocks([]);
    if (seenCardIdsRef?.current) seenCardIdsRef.current.clear();
    if (nanoClaw?.reset) nanoClaw.reset(selectedSessionId);
    else if (nanoClaw?.switchSession) nanoClaw.switchSession(selectedSessionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchMode]);

  // Derive session content from API session data — ONLY for completed sessions.
  // If we have live blocks (user is actively chatting), skip historical data to avoid
  // dual-source conflicts (different timestamps, ordering issues, duplication).
  const [sessionTasks, setSessionTasks] = useState([]);
  useEffect(() => {
    if (!selectedSessionId || !selectedSessionFull) { setSessionTasks([]); return; }
    const s = selectedSessionFull;
    const ts = s.latestTs || 0;

    // Timeline-based rendering: one task per round
    if (s.timeline && s.timeline.length > 0) {
      // Compute per-round boundaries to filter accumulated cards.
      // The API accumulates ALL cards across rounds (round N has cards from rounds 1..N).
      // Use previous round's completion ts as lower bound to isolate this round's cards.
      const roundBoundaries = s.timeline.map((r, i) => {
        if (i === 0) return 0;
        const prevTs = s.timeline[i - 1].ts ? new Date(s.timeline[i - 1].ts).getTime() : 0;
        return prevTs;
      });

      const tasks = s.timeline.map((round, idx) => {
        const roundTs = round.ts ? new Date(round.ts).getTime() : (ts + idx);
        const requestTs = round.request_ts ? new Date(round.request_ts).getTime() : null;
        const photos = (round.photos || []).map(p => typeof p === 'string' ? p : p?.url).filter(Boolean);
        const lowerBound = roundBoundaries[idx]; // cards before this belong to earlier rounds

        // Collect cards — filter out accumulated cards from previous rounds
        const cardEntries = round.cards ? Object.entries(round.cards) : [];
        const parsedCards = {};
        let earliestCardTs = roundTs;
        for (const [id, card] of cardEntries) {
          const cardTs = card.ts ? (typeof card.ts === 'string' ? new Date(card.ts).getTime() : card.ts) : roundTs;
          // Skip cards that belong to a previous round (ts before this round's boundary)
          if (lowerBound > 0 && cardTs < lowerBound) continue;
          parsedCards[id] = { ...card, status: card.status || 'finalized', ts: cardTs };
          if (cardTs < earliestCardTs) earliestCardTs = cardTs;
        }
        // Prompt timestamp priority: request_ts > earliest card - 1 > round completion
        const promptTs = requestTs || (Object.keys(parsedCards).length > 0 ? earliestCardTs - 1 : roundTs);

        const task = {
          taskId: `${s.sessionId}-round-${round.round || idx + 1}`,
          sessionId: s.sessionId,
          prompt: round.prompt || '',
          skillSlug: s.tasks[0]?.skillSlug || null,
          ts: promptTs,
          requestTs: promptTs,
          mediaUrls: photos,
          cards: parsedCards,
          result: null,
          durationMs: null,
        };
        // If no cards, show summary as text result
        if (!hasContentCards(task.cards) && round.summary) {
          task.result = round.summary;
        }
        return task;
      });
      setSessionTasks(tasks);
      return;
    }

    // Legacy flat rendering (single-round or no timeline)
    const contextPhotos = s.context?.photos || [];
    const mediaUrls = contextPhotos.map(p => typeof p === 'string' ? p : p?.url).filter(Boolean);

    // Collect card timestamps first to place prompt before earliest card
    const legacyCards = {};
    let legacyEarliestCardTs = ts;
    if (s.result?.cards) {
      for (const [id, card] of Object.entries(s.result.cards)) {
        const cardTs = card.ts ? (typeof card.ts === 'string' ? new Date(card.ts).getTime() : card.ts) : ts;
        legacyCards[id] = { ...card, status: card.status || 'finalized', ts: cardTs };
        if (cardTs < legacyEarliestCardTs) legacyEarliestCardTs = cardTs;
      }
    }
    const legacyPromptTs = Object.keys(legacyCards).length > 0 ? legacyEarliestCardTs - 1 : ts;

    const syntheticTask = {
      taskId: s.sessionId,
      sessionId: s.sessionId,
      prompt: s.tasks[0]?.prompt || s.title || '',
      skillSlug: s.tasks[0]?.skillSlug || null,
      ts: legacyPromptTs,
      requestTs: legacyPromptTs,
      mediaUrls,
      cards: legacyCards,
      result: null,
      durationMs: null,
    };
    // If result_html exists and no cards, create a freeform-html card
    if (s.result_html && !hasContentCards(syntheticTask.cards)) {
      syntheticTask.cards['result-html'] = {
        template: 'freeform-html',
        data: { html: s.result_html },
        status: 'finalized',
        ts,
      };
    }
    // If no cards at all, use result_summary as text result
    if (!hasContentCards(syntheticTask.cards) && s.result_summary) {
      syntheticTask.result = s.result_summary;
    }
    setSessionTasks([syntheticTask]);
  }, [selectedSessionId, selectedSessionFull]);

  const { allCards, cardTaskMap, sortedTasks } = sessionTasks.length > 0
    ? deduplicateCards(sessionTasks)
    : { allCards: {}, cardTaskMap: {}, sortedTasks: [] };

  return (
    <div style={{ display: 'flex', width: '100%', height: '100%' }}>
      {/* ── Session Sidebar (chat-sidebar) ── */}
      <div style={{
        width: 220, background: V.bgCard, borderRight: `1px solid ${V.border}`,
        display: 'flex', flexDirection: 'column', flexShrink: 0,
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 12px', borderBottom: `1px solid ${V.border}`,
        }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: V.text }}>Sessions</span>
          <div style={{ display: 'flex', gap: 4 }}>
            <button onClick={() => { selectSession(null); setLiveBlocks([]); seenCardIdsRef.current.clear(); if (nanoClaw?.reset) nanoClaw.reset(); }} style={{
              background: V.accent, border: 'none', borderRadius: 4,
              color: '#fff', cursor: 'pointer', fontSize: 13, padding: '2px 8px', fontWeight: 600,
            }} title="New session">+</button>
            <button onClick={loadSessions} style={{
              background: 'none', border: `1px solid ${V.border}`, borderRadius: 4,
              color: V.textDim, cursor: 'pointer', fontSize: 13, padding: '2px 6px',
            }} title="Refresh">↻</button>
          </div>
        </div>

        {/* Search by session ID */}
        <div style={{ padding: '8px 12px', borderBottom: `1px solid ${V.border}` }}>
          <input
            type="text"
            value={sessionSearch}
            onChange={(e) => setSessionSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                const q = sessionSearch.trim();
                if (!q) return;
                const lowered = q.toLowerCase();
                const hit = sessions.find(s => s.sessionId.toLowerCase().includes(lowered));
                if (hit) selectSession(hit.sessionId);
                else if (q.length >= 8) selectSession(q);
              } else if (e.key === 'Escape') {
                setSessionSearch('');
              }
            }}
            placeholder="Search session ID..."
            style={{
              width: '100%', boxSizing: 'border-box',
              padding: '4px 8px', fontSize: 11,
              background: V.bg, color: V.text,
              border: `1px solid ${V.border}`, borderRadius: 4,
              outline: 'none',
            }}
          />
        </div>

        {/* Session list */}
        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          {loading && <div style={{ padding: 16, fontSize: 12, color: V.textDim }}>Loading...</div>}
          {error && <div style={{ padding: 16, fontSize: 12, color: V.red }}>Error: {error}</div>}
          {!loading && !error && sessions.length === 0 && (
            <div style={{ padding: 16, fontSize: 12, color: V.textDim }}>No sessions found</div>
          )}
          {(() => {
            const q = sessionSearch.trim().toLowerCase();
            const filtered = q ? sessions.filter(s => s.sessionId.toLowerCase().includes(q)) : sessions;
            if (q && filtered.length === 0 && !loading) {
              return (
                <div style={{ padding: 16, fontSize: 12, color: V.textDim }}>
                  <div style={{ marginBottom: 8 }}>No match in the loaded list.</div>
                  {sessionSearch.trim().length >= 8 && (
                    <button
                      onClick={() => selectSession(sessionSearch.trim())}
                      style={{
                        background: V.accent, border: 'none', borderRadius: 4,
                        color: '#fff', cursor: 'pointer', fontSize: 11,
                        padding: '4px 10px', fontWeight: 600,
                      }}
                    >Jump to this ID</button>
                  )}
                </div>
              );
            }
            return filtered.map(session => {
            const isActive = session.sessionId === selectedSessionId;
            const firstPrompt = session.tasks[0]?.prompt || '(no prompt)';
            const firstSkill = session.tasks[0]?.skillSlug;
            return (
              <div
                key={session.sessionId}
                onClick={() => selectSession(session.sessionId)}
                style={{
                  padding: '8px 12px', cursor: 'pointer', position: 'relative',
                  borderBottom: `1px solid ${V.border}`,
                  background: isActive ? V.accentDim : 'transparent',
                  transition: 'background 0.1s',
                }}
                onMouseEnter={e => {
                  if (!isActive) e.currentTarget.style.background = V.bgHover;
                  const btn = e.currentTarget.querySelector('[data-delete-btn]');
                  if (btn) btn.style.opacity = '1';
                }}
                onMouseLeave={e => {
                  if (!isActive) e.currentTarget.style.background = isActive ? V.accentDim : 'transparent';
                  const btn = e.currentTarget.querySelector('[data-delete-btn]');
                  if (btn) btn.style.opacity = '0';
                }}
              >
                <div style={{
                  fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap', color: isActive ? V.accent : V.text,
                  paddingRight: 22,
                }}>
                  {firstPrompt}
                </div>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  marginTop: 3, fontSize: 10, color: V.textDim,
                }}>
                  {firstSkill && (
                    <span style={{
                      background: 'rgba(175,82,222,0.12)', color: V.purple,
                      padding: '0 5px', borderRadius: 3, fontSize: 10,
                    }}>
                      {formatSkillTag(firstSkill)}
                    </span>
                  )}
                  <span>{session.tasks.length} task{session.tasks.length > 1 ? 's' : ''}</span>
                  <span>{formatTime(session.latestTs)}</span>
                </div>
                <button
                  data-delete-btn
                  onClick={(e) => handleDeleteSession(session.sessionId, e)}
                  title="Delete session"
                  style={{
                    position: 'absolute', top: 6, right: 6,
                    width: 20, height: 20, padding: 0,
                    border: 'none', background: 'transparent',
                    color: V.textDim, cursor: 'pointer',
                    fontSize: 14, lineHeight: '20px', borderRadius: 3,
                    opacity: 0, transition: 'opacity 0.1s, background 0.1s, color 0.1s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255,59,48,0.12)';
                    e.currentTarget.style.color = V.red;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = V.textDim;
                  }}
                >×</button>
              </div>
            );
          });
          })()}
        </div>

        {/* Session info panel */}
        <div style={{
          minHeight: 180, maxHeight: usageOpen ? 400 : 180, borderTop: `1px solid ${V.border}`, overflowY: 'auto',
          padding: '10px 12px', fontSize: 12, flexShrink: 0, color: V.text,
          transition: 'max-height 0.2s',
        }}>
          {!selectedSession ? (
            <div style={{ color: V.textDim, fontSize: 11, padding: '8px 0', textAlign: 'center' }}>
              Select a session for details
            </div>
          ) : (
            <>
              <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em', color: V.textDim, marginBottom: 2 }}>
                Session ID
              </div>
              <div style={{ wordBreak: 'break-all', fontSize: 12 }}>
                {selectedSession.sessionId}
              </div>
              <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em', color: V.textDim, marginTop: 8, marginBottom: 2 }}>
                Tasks ({selectedSession.tasks.length})
              </div>
              {selectedSession.tasks.map((t, i) => (
                <div key={i} style={{
                  padding: '4px 0', borderBottom: `1px solid ${V.border}`,
                  fontSize: 11, color: V.textDim,
                }}>
                  {(t.prompt || '').slice(0, 40)}{(t.prompt || '').length > 40 ? '…' : ''}
                  {t.durationMs ? ` (${formatDuration(t.durationMs)})` : ''}
                </div>
              ))}
              {/* Usage details — opens a modal */}
              <button
                onClick={async () => {
                  setUsageOpen(true);
                  setUsageLoading(true);
                  try {
                    const data = await api.request(`/api/users/me/usage/sessions/${selectedSession.sessionId}?page_size=100`);
                    setUsageData(data);
                  } catch { setUsageData(null); }
                  setUsageLoading(false);
                }}
                style={{
                  marginTop: 10, width: '100%', padding: '6px 0', border: `1px solid ${V.border}`,
                  borderRadius: 6, background: 'transparent',
                  color: V.text, fontSize: 11, fontWeight: 500,
                  cursor: 'pointer', transition: 'all 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = V.accent; e.currentTarget.style.color = V.accent; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = V.border; e.currentTarget.style.color = V.text; }}
              >
                View Usage
              </button>
              {/* Usage Modal */}
              {usageOpen && (
                <div style={{
                  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                  background: 'rgba(0,0,0,0.5)', zIndex: 9999,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }} onClick={() => setUsageOpen(false)}>
                  <div style={{
                    background: V.bgCard, borderRadius: 12, padding: 24, width: '90%', maxWidth: 800, maxHeight: '80vh',
                    overflow: 'auto', border: `1px solid ${V.border}`,
                  }} onClick={e => e.stopPropagation()}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                      <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Session Usage</h3>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <button onClick={async () => {
                          setUsageLoading(true);
                          try {
                            const data = await api.request(`/api/users/me/usage/sessions/${selectedSession.sessionId}?page_size=100`);
                            setUsageData(data);
                          } catch { /* ignore */ }
                          setUsageLoading(false);
                        }} style={{ background: 'none', border: `1px solid ${V.border}`, borderRadius: 4, color: V.textDim, cursor: 'pointer', fontSize: 11, padding: '2px 8px' }}>Refresh</button>
                        <button onClick={() => setUsageOpen(false)} style={{ background: 'none', border: 'none', color: V.textDim, cursor: 'pointer', fontSize: 18, lineHeight: 1 }}>×</button>
                      </div>
                    </div>
                    {usageLoading && <div style={{ color: V.textDim }}>Loading...</div>}
                    {usageData && !usageLoading && (
                      <>
                        <div style={{ fontSize: 13, marginBottom: 12 }}>
                          <span style={{ fontWeight: 600, fontSize: 20 }}>${Number(usageData.total_cost).toFixed(4)}</span>
                          <span style={{ color: V.textDim, marginLeft: 12 }}>{usageData.total} calls</span>
                          {usageData.details?.length > 0 && (() => {
                            const byModel = {};
                            usageData.details.forEach(d => {
                              byModel[d.model_name] = (byModel[d.model_name] || 0) + Number(d.cost || 0);
                            });
                            return (
                              <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                {Object.entries(byModel).sort((a, b) => b[1] - a[1]).map(([m, c]) => (
                                  <span key={m} style={{
                                    padding: '2px 8px', borderRadius: 4, fontSize: 11,
                                    background: V.bgCard, border: `1px solid ${V.border}`,
                                  }}>
                                    {m} <span style={{ fontWeight: 600 }}>${c.toFixed(4)}</span>
                                  </span>
                                ))}
                              </div>
                            );
                          })()}
                        </div>
                        <div style={{ overflowX: 'auto' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                            <thead>
                              <tr style={{ color: V.textDim, borderBottom: `2px solid ${V.border}` }}>
                                <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600 }}>Time</th>
                                <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600 }}>Model</th>
                                <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600 }}>Provider</th>
                                <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600 }}>Input</th>
                                <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600 }}>Output</th>
                                <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600 }}>Thinking</th>
                                <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600 }}>Cache R</th>
                                <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600 }}>Cache W</th>
                                <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600 }}>Cost</th>
                                <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600 }}>Latency</th>
                                <th style={{ textAlign: 'center', padding: '6px 8px', fontWeight: 600 }}>Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {usageData.details.map((d, i) => (
                                <tr key={i} style={{ borderBottom: `1px solid ${V.border}33` }}>
                                  <td style={{ padding: '4px 8px', whiteSpace: 'nowrap', fontSize: 11, color: V.textDim }}>{new Date(d.created_at).toLocaleString('zh-CN')}</td>
                                  <td style={{ padding: '4px 8px', whiteSpace: 'nowrap' }}>{d.model_name}</td>
                                  <td style={{ padding: '4px 8px', color: V.textDim }}>{d.provider_type || '-'}</td>
                                  <td style={{ padding: '4px 8px', textAlign: 'right' }}>{d.input_tokens}</td>
                                  <td style={{ padding: '4px 8px', textAlign: 'right' }}>{d.output_tokens}</td>
                                  <td style={{ padding: '4px 8px', textAlign: 'right', color: V.textDim }}>{d.thinking_tokens || '-'}</td>
                                  <td style={{ padding: '4px 8px', textAlign: 'right', color: V.textDim }}>{d.cache_read_tokens || '-'}</td>
                                  <td style={{ padding: '4px 8px', textAlign: 'right', color: V.textDim }}>{d.cache_write_tokens || '-'}</td>
                                  <td style={{ padding: '4px 8px', textAlign: 'right', fontWeight: 600 }}>${Number(d.cost).toFixed(6)}</td>
                                  <td style={{ padding: '4px 8px', textAlign: 'right', color: V.textDim }}>{d.latency_ms != null ? `${d.latency_ms}ms` : '-'}</td>
                                  <td style={{ padding: '4px 8px', textAlign: 'center' }}>{d.error_message ? <span title={d.error_message} style={{ color: '#ef4444', cursor: 'help' }}>error</span> : <span style={{ color: '#22c55e' }}>ok</span>}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    )}
                    {!usageData && !usageLoading && (
                      <div style={{ color: V.textDim }}>No usage data</div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Chat Area (chat-area) ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Session info header */}
        {selectedSessionFull && <SessionInfoHeader session={selectedSessionFull} rawMode={rawMode} onToggleRaw={() => setRawMode(r => !r)} fetchMode={fetchMode} onChangeFetchMode={onChangeFetchMode} />}
        {/* Messages */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
          {!selectedSession && liveBlocks.length === 0 ? (
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              height: '100%', color: V.textDim, fontSize: 14, gap: 8,
            }}>
              <span style={{ fontSize: 28, opacity: 0.3 }}>💬</span>
              <span>Start a new conversation or select a session</span>
            </div>
          ) : sortedTasks.length === 0 && liveBlocks.length === 0 ? (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              height: '100%', color: V.textDim, fontSize: 14,
            }}>
              No messages in this session
            </div>
          ) : (
            <UnifiedTimeline
              sortedTasks={sortedTasks}
              allCards={allCards}
              cardTaskMap={cardTaskMap}
              liveBlocks={liveBlocks}
              sending={sending}
              rawMode={rawMode}
            />
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* ── Chat Input Bar — with inline model selector ── */}
        <ChatInputBar
          onSend={handleSend} disabled={sending} isExecuting={!!activeTask} onStop={handleStop}
          selectedModel={selectedModel}
          onModelChange={setSelectedModel}
          models={availableModels}
        />
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// CARDS PAGE — mirrors dashboard tpl-layout (templates page)
// ══════════════════════════════════════════════════════════════════════

const CARD_ICONS = {
  'freeform-html': '\u{1F310}', 'debug-html': '\u{1F6E0}', 'nutrition-card': '\u{1F957}', 'shopping-list': '\u{1F6D2}',
  'comparison-table': '\u2696\uFE0F', 'hero-image': '\u{1F5BC}\uFE0F', 'image-analysis': '\u{1F50D}',
  'calendar-event': '\u{1F4C5}', 'map-pins': '\u{1F4CD}', 'quiz': '\u2753',
  'conversation': '\u{1F4AC}', 'thinking-process': '\u{1F9E0}', 'place-card': '\u{1F3EA}',
  'info-card': '\u2139\uFE0F', 'checklist': '\u2705', 'recipe': '\u{1F373}',
  'weather': '\u2600\uFE0F', 'steps-guide': '\u{1F4CB}', 'image-gallery': '\u{1F5BC}\uFE0F',
  'text-result': '\u{1F4DD}',
};

/** Compare date-based versions: returns 'push' | 'pull' | 'synced' | null */
function cmpVersion(lv, rv) {
  if (!lv && !rv) return 'synced';
  if (!lv) return 'pull';
  if (!rv) return null; // local only, no remote
  if (lv === rv) return 'synced';
  const lParts = lv.split('.').map(Number);
  const rParts = rv.split('.').map(Number);
  for (let i = 0; i < Math.max(lParts.length, rParts.length); i++) {
    if ((lParts[i] || 0) > (rParts[i] || 0)) return 'push';
    if ((lParts[i] || 0) < (rParts[i] || 0)) return 'pull';
  }
  return 'synced';
}

function CardsPage() {
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [testData, setTestData] = useState('');
  const [testCard, setTestCard] = useState(null);
  const [serverExamples, setServerExamples] = useState({});
  const [renderKey, setRenderKey] = useState(0);
  const [remoteTemplates, setRemoteTemplates] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [syncingId, setSyncingId] = useState(null); // which template is currently syncing

  // Load templates from NanoClaw dashboard API
  const loadData = useCallback(() => {
    setLoading(true);
    setError(null);
    clearTemplateCache(); // ensure hot-updated HTML templates are re-fetched
    api.authFetch('/api/nanoclaw/dashboard/templates')
      .then(r => r.json())
      .then(d => { setTemplates(d.templates || []); })
      .catch(e => { setError(e.message); })
      .finally(() => { setLoading(false); });
    api.authFetch('/api/nanoclaw/dashboard/templates/examples')
      .then(r => r.ok ? r.json() : { examples: {} })
      .then(d => setServerExamples(d.examples || {}))
      .catch(() => {}); // silently fall back to local data
    // Load remote template versions for sync status
    api.authFetch('/api/nanoclaw/dashboard/templates/remote?env=staging')
      .then(r => r.json())
      .then(d => setRemoteTemplates(d.templates || []))
      .catch(() => {});
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Filter templates
  const filtered = filter
    ? templates.filter(t =>
        (t.$id || '').toLowerCase().includes(filter.toLowerCase()) ||
        (t.description || '').toLowerCase().includes(filter.toLowerCase()) ||
        (t.category || '').toLowerCase().includes(filter.toLowerCase())
      )
    : templates;

  // Group by source (shared/builtin/domain:shared → "Shared", others → package name)
  const groups = {};
  for (const t of filtered) {
    const src = t.source || 'unknown';
    const groupKey = (src === 'shared' || src === 'builtin' || src === 'domain:shared') ? 'Shared' : src;
    if (!groups[groupKey]) groups[groupKey] = [];
    groups[groupKey].push(t);
  }
  const groupEntries = Object.entries(groups).sort(([a], [b]) => a === 'Shared' ? -1 : b === 'Shared' ? 1 : a.localeCompare(b));

  const selectedTpl = templates.find(t => t.$id === selectedId);

  // Generate sample data when template selected
  useEffect(() => {
    if (!selectedTpl) return;
    const data = generateSampleData(selectedTpl, serverExamples);
    setTestData(JSON.stringify(data, null, 2));
    setTestCard({
      cardId: 'preview-' + selectedTpl.$id,
      template: selectedTpl.$id,
      data: data,
      status: 'finalized',
    });
  }, [selectedId, serverExamples]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleRender() {
    if (!selectedTpl) return;
    try {
      const data = JSON.parse(testData);
      setRenderKey(k => k + 1); // force re-render even with same data
      setTestCard({
        cardId: 'preview-' + selectedTpl.$id,
        template: selectedTpl.$id,
        data: data,
        status: 'finalized',
      });
    } catch (e) {
      alert('Invalid JSON: ' + e.message);
    }
  }

  return (
    <div style={{ display: 'flex', width: '100%', height: '100%' }}>
      {/* ── Template Sidebar (tpl-sidebar) ── */}
      <div style={{
        width: 260, background: V.bgCard, borderRight: `1px solid ${V.border}`,
        display: 'flex', flexDirection: 'column', flexShrink: 0,
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 12px', borderBottom: `1px solid ${V.border}`,
        }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: V.text }}>Templates</span>
          <button onClick={loadData} style={{
            background: 'none', border: `1px solid ${V.border}`, borderRadius: 4,
            color: V.textDim, cursor: 'pointer', fontSize: 13, padding: '2px 6px',
          }} title="Refresh">↻</button>
        </div>
        <div style={{ padding: '8px 10px' }}>
          <input
            type="text"
            placeholder="Filter..."
            value={filter}
            onChange={e => setFilter(e.target.value)}
            style={{
              width: '100%', padding: '6px 10px', background: V.bg,
              border: `1px solid ${V.border}`, borderRadius: 6,
              color: V.text, fontSize: 12, outline: 'none',
            }}
          />
        </div>
        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          {loading && <div style={{ padding: 16, fontSize: 12, color: V.textDim }}>Loading...</div>}
          {error && <div style={{ padding: 16, fontSize: 12, color: V.red }}>Error: {error}</div>}
          {groupEntries.map(([group, items]) => (
            <div key={group}>
              <div style={{
                padding: '10px 12px 4px', fontSize: 10, fontWeight: 600,
                textTransform: 'uppercase', letterSpacing: '0.06em', color: V.textDim,
              }}>
                {group}
              </div>
              {items.map(t => {
                const isActive = t.$id === selectedId;
                return (
                  <div
                    key={t.$id}
                    onClick={() => setSelectedId(t.$id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      padding: '8px 12px', cursor: 'pointer',
                      background: isActive ? V.accentDim : 'transparent',
                      transition: 'background 0.1s',
                    }}
                    onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = V.bgHover; }}
                    onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = isActive ? V.accentDim : 'transparent'; }}
                  >
                    <span style={{ fontSize: 16, width: 20, textAlign: 'center' }}>
                      {CARD_ICONS[t.$id] || '\u{1F4C4}'}
                    </span>
                    <div style={{ overflow: 'hidden', flex: 1 }}>
                      <div style={{ fontSize: 12, fontWeight: 500, color: isActive ? V.accent : V.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {t.$id}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 1 }}>
                        {t.version && (
                          <span style={{ fontSize: 10, color: V.accent, fontFamily: 'monospace' }}>v{t.version}</span>
                        )}
                        {(() => {
                          const rt = remoteTemplates.find(r => r.id === t.$id);
                          const status = cmpVersion(t.version, rt?.version);
                          if (!status || status === 'synced') return status === 'synced' ? <span style={{ fontSize: 9, color: '#4ade80' }}>synced</span> : null;
                          return <span style={{ fontSize: 9, color: '#f87171' }}>{status === 'push' ? '\u2191 push' : '\u2193 pull'}</span>;
                        })()}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* ── Template Main (tpl-main) ── */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 24, color: V.text }}>
        {!selectedTpl ? (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            height: '100%', color: V.textDim, fontSize: 14,
          }}>
            Select a card to view details
          </div>
        ) : (
          <div>
            {/* Header */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <h3 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>{selectedTpl.$id}</h3>
                  {selectedTpl.version && (
                    <span style={{ fontSize: 11, fontFamily: 'monospace', padding: '1px 8px', borderRadius: 4, background: 'rgba(88,86,214,0.12)', color: V.accent }}>v{selectedTpl.version}</span>
                  )}
                </div>
                {/* Pull/Push buttons for this template */}
                {(() => {
                  const rt = remoteTemplates.find(r => r.id === selectedTpl.$id);
                  const status = cmpVersion(selectedTpl.version, rt?.version);
                  const isSyncing = syncingId === selectedTpl.$id;
                  const btnStyle = { background: 'none', border: `1px solid ${V.border}`, borderRadius: 4, padding: '3px 10px', fontSize: 11, cursor: 'pointer', opacity: isSyncing ? 0.6 : 1, transition: 'opacity 0.2s' };
                  return (
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      {status === 'pull' && (
                        <button disabled={isSyncing} onClick={async () => {
                          setSyncingId(selectedTpl.$id);
                          try { await api.authFetch(`/api/nanoclaw/dashboard/templates/${encodeURIComponent(selectedTpl.$id)}/pull`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ env: 'staging' }) }); loadData(); } catch { /* ignore sync errors */ }
                          setSyncingId(null);
                        }} style={{ ...btnStyle, color: '#4ade80', borderColor: '#4ade80' }}>
                          {isSyncing ? <span style={{ display: 'inline-block', animation: 'spin 1s linear infinite' }}>{'\u21BB'}</span> : '\u2B07'} Pull
                        </button>
                      )}
                      {status === 'push' && (
                        <button disabled={isSyncing} onClick={async () => {
                          setSyncingId(selectedTpl.$id);
                          try { await api.authFetch(`/api/nanoclaw/dashboard/templates/${encodeURIComponent(selectedTpl.$id)}/push`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ env: 'staging' }) }); loadData(); } catch { /* ignore sync errors */ }
                          setSyncingId(null);
                        }} style={{ ...btnStyle, color: V.accent, borderColor: V.accent }}>
                          {isSyncing ? <span style={{ display: 'inline-block', animation: 'spin 1s linear infinite' }}>{'\u21BB'}</span> : '\u2B06'} Push
                        </button>
                      )}
                      {status === 'synced' && rt && (
                        <span style={{ fontSize: 10, color: '#4ade80', padding: '4px 8px' }}>synced</span>
                      )}
                    </div>
                  );
                })()}
              </div>
              <div style={{ marginTop: 4, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <span style={{ background: 'rgba(0,122,255,0.1)', color: V.blue, padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 500 }}>
                  {selectedTpl.category || '-'}
                </span>
                <span style={{ background: 'rgba(175,82,222,0.1)', color: V.purple, padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 500 }}>
                  {selectedTpl.renderer || 'html'}
                </span>
                {selectedTpl.streamable && (
                  <span style={{ background: 'rgba(52,199,89,0.1)', color: V.green, padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 500 }}>streamable</span>
                )}
              </div>
              {selectedTpl.description && (
                <p style={{ marginTop: 8, color: V.textDim, fontSize: 13 }}>{selectedTpl.description}</p>
              )}
            </div>

            {/* 2-column layout: Example Data | Rendered */}
            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12,
              minHeight: 300,
            }}>
              {/* Column 1: Example Data (editable) */}
              <div style={{
                background: V.bgCard, border: `1px solid ${V.border}`,
                borderRadius: 8, overflow: 'hidden', display: 'flex', flexDirection: 'column',
              }}>
                <div style={{
                  padding: '8px 12px', fontSize: 11, fontWeight: 600,
                  textTransform: 'uppercase', letterSpacing: '0.04em',
                  color: V.textDim, borderBottom: `1px solid ${V.border}`,
                }}>
                  Example Data
                </div>
                <div style={{ padding: 8, flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <textarea
                    value={testData}
                    onChange={e => setTestData(e.target.value)}
                    style={{
                      flex: 1, width: '100%', minHeight: 200, resize: 'vertical',
                      background: V.bg, border: `1px solid ${V.border}`, borderRadius: 6,
                      color: V.text, padding: 8, fontSize: 12, fontFamily: V.font,
                      lineHeight: 1.5, outline: 'none',
                    }}
                  />
                  <button
                    onClick={handleRender}
                    style={{
                      marginTop: 6, width: '100%', padding: '6px 12px',
                      background: V.accent, color: '#fff', border: 'none',
                      borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 500,
                    }}
                  >
                    Render
                  </button>
                </div>
              </div>

              {/* Column 2: Rendered preview */}
              <div style={{
                background: V.bgCard, border: `1px solid ${V.border}`,
                borderRadius: 8, overflow: 'hidden', display: 'flex', flexDirection: 'column',
              }}>
                <div style={{
                  padding: '8px 12px', fontSize: 11, fontWeight: 600,
                  textTransform: 'uppercase', letterSpacing: '0.04em',
                  color: V.textDim, borderBottom: `1px solid ${V.border}`,
                }}>
                  Rendered
                </div>
                <div style={{ flex: 1, overflowY: 'auto', background: '#fff', color: '#111' }}>
                  {testCard ? <CardRenderer key={renderKey} card={testCard} /> : (
                    <div style={{ padding: 16, color: '#999', fontSize: 13 }}>Click Render to preview</div>
                  )}
                </div>
              </div>
            </div>

            {/* Slots info */}
            {selectedTpl.slots && Object.keys(selectedTpl.slots).length > 0 && (
              <div style={{ marginTop: 16 }}>
                <div style={{
                  fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
                  letterSpacing: '0.05em', color: V.textDim, marginBottom: 8,
                }}>
                  Slots ({Object.keys(selectedTpl.slots).length})
                </div>
                <div style={{
                  background: V.bgCard, border: `1px solid ${V.border}`,
                  borderRadius: 8, overflow: 'hidden',
                }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left', padding: '10px 14px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: V.textDim, borderBottom: `1px solid ${V.border}`, fontWeight: 500 }}>Name</th>
                        <th style={{ textAlign: 'left', padding: '10px 14px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: V.textDim, borderBottom: `1px solid ${V.border}`, fontWeight: 500 }}>Type</th>
                        <th style={{ textAlign: 'left', padding: '10px 14px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: V.textDim, borderBottom: `1px solid ${V.border}`, fontWeight: 500 }}>Required</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(selectedTpl.slots).map(([name, slot]) => (
                        <tr key={name}>
                          <td style={{ padding: '8px 14px', borderBottom: `1px solid ${V.border}`, color: V.text, fontFamily: V.font, fontSize: 12 }}>{name}</td>
                          <td style={{ padding: '8px 14px', borderBottom: `1px solid ${V.border}`, color: V.textDim }}>{slot.type || '-'}</td>
                          <td style={{ padding: '8px 14px', borderBottom: `1px solid ${V.border}`, color: V.textDim }}>{slot.required ? 'yes' : 'no'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Sample data per template (matches what each React component expects) ──

const SAMPLE_DATA = {
  'weather': {
    location: 'San Francisco, CA',
    current: { temp: 18, condition: 'Partly Cloudy', icon: '⛅', humidity: 65, wind: '12 km/h', unit: 'C' },
    forecast: [
      { day: 'Mon', high: 20, low: 13, condition: 'Sunny', icon: '☀️' },
      { day: 'Tue', high: 17, low: 11, condition: 'Cloudy', icon: '☁️' },
      { day: 'Wed', high: 15, low: 10, condition: 'Rain', icon: '🌧️' },
      { day: 'Thu', high: 19, low: 12, condition: 'Partly Cloudy', icon: '⛅' },
      { day: 'Fri', high: 22, low: 14, condition: 'Sunny', icon: '☀️' },
    ],
  },
  'recipe': {
    title: 'Classic Margherita Pizza',
    image_url: 'https://images.unsplash.com/photo-1604068549290-dea0e4a305ca?w=600&q=80',
    prep_time: '20 min',
    cook_time: '12 min',
    servings: 4,
    ingredients: [
      { amount: '500g', item: 'Pizza dough' },
      { amount: '200ml', item: 'San Marzano tomato sauce' },
      { amount: '250g', item: 'Fresh mozzarella' },
      { amount: '10', item: 'Fresh basil leaves' },
      { amount: '2 tbsp', item: 'Extra virgin olive oil' },
    ],
    steps: [
      'Preheat oven to 250°C (480°F) with a pizza stone inside.',
      'Stretch dough into a 12-inch round on floured surface.',
      'Spread tomato sauce evenly, leaving a 1-inch border.',
      'Tear mozzarella into pieces and distribute over sauce.',
      'Bake for 10-12 minutes until crust is golden and cheese bubbles.',
      'Top with fresh basil and drizzle with olive oil. Serve immediately.',
    ],
  },
  'checklist': {
    title: 'Weekend Trip Packing',
    items: [
      { text: 'Passport & ID', checked: true, category: 'Documents' },
      { text: 'Travel insurance printout', checked: false, category: 'Documents' },
      { text: 'T-shirts (×3)', checked: true, category: 'Clothing' },
      { text: 'Jacket', checked: false, category: 'Clothing' },
      { text: 'Phone charger', checked: true, category: 'Electronics' },
      { text: 'Camera', checked: false, category: 'Electronics' },
      { text: 'Toothbrush', checked: false, category: 'Toiletries' },
      { text: 'Sunscreen', checked: false, category: 'Toiletries' },
    ],
  },
  'info-card': {
    title: 'Golden Gate Bridge',
    subtitle: 'Iconic suspension bridge',
    icon: '🌉',
    fields: [
      { label: 'Location', value: 'San Francisco, CA' },
      { label: 'Opened', value: 'May 27, 1937' },
      { label: 'Total Length', value: '2,737 m (8,981 ft)', highlight: true },
      { label: 'Architect', value: 'Joseph Strauss & Irving Morrow' },
      { label: 'Daily Traffic', value: '~100,000 vehicles' },
    ],
    footer: 'Designated as a National Historic Landmark in 1987.',
    accent_color: '#e3513c',
  },
  'image-gallery': {
    title: 'Mountain Landscapes',
    images: [
      { url: 'https://images.unsplash.com/photo-1506744038136-46273834b3fb?w=600&q=80', caption: 'Yosemite Valley at sunrise', alt: 'Yosemite Valley' },
      { url: 'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=600&q=80', caption: 'Alpine peaks in morning light', alt: 'Alpine mountains' },
      { url: 'https://images.unsplash.com/photo-1519681393784-d120267933ba?w=600&q=80', caption: 'Starry night over mountains', alt: 'Mountain night sky' },
    ],
  },
  'nutrition-card': {
    food_name: 'Grilled Salmon Bowl',
    photo_url: 'https://images.unsplash.com/photo-1467003909585-2f8a72700288?w=600&q=80',
    calories: 520,
    protein_g: 38,
    carbs_g: 45,
    fat_g: 18,
    fiber_g: 6,
    serving_size: '1 bowl (350g)',
    health_score: 8.5,
    recommendation: 'Excellent source of omega-3 fatty acids and lean protein. Great post-workout meal.',
    daily_percent: { calories: 26, protein: 76, carbs: 15, fat: 23 },
  },
  'shopping-list': {
    title: 'Weekly Groceries',
    items: [
      { name: 'Organic eggs', quantity: '1 dozen', category: 'Dairy', price_estimate: '$5.99' },
      { name: 'Whole milk', quantity: '1 gallon', category: 'Dairy', price_estimate: '$4.49' },
      { name: 'Chicken breast', quantity: '2 lbs', category: 'Meat', price_estimate: '$8.99' },
      { name: 'Salmon fillet', quantity: '1 lb', category: 'Meat', price_estimate: '$12.99' },
      { name: 'Broccoli', quantity: '2 heads', category: 'Produce', price_estimate: '$3.49' },
      { name: 'Avocados', quantity: '4', category: 'Produce', price_estimate: '$5.99' },
      { name: 'Sourdough bread', quantity: '1 loaf', category: 'Bakery', price_estimate: '$4.99' },
    ],
    total_estimate: '$46.93',
    store_suggestion: 'Whole Foods Market',
  },
  'place-card': {
    name: 'Tartine Bakery',
    category: 'Bakery & Cafe',
    rating: 4.6,
    price_level: 2,
    address: '600 Guerrero St, San Francisco, CA 94110',
    phone: '+1 (415) 487-2600',
    hours: 'Mon-Sun: 7:30 AM - 7:00 PM',
    image_url: 'https://images.unsplash.com/photo-1509440159596-0249088772ff?w=600&q=80',
    coordinates: { lat: 37.7614, lng: -122.4241 },
    tags: ['Bakery', 'Coffee', 'Pastries', 'Brunch'],
    url: 'https://www.tartinebakery.com',
  },
  'map-pins': {
    title: 'Best Coffee Shops Nearby',
    markers: [
      { id: '1', lat: 37.7749, lng: -122.4194, label: 'Blue Bottle Coffee', description: 'Third-wave coffee roaster', rating: 4.5, icon: '☕' },
      { id: '2', lat: 37.7694, lng: -122.4262, label: 'Sightglass Coffee', description: 'Artisan roaster with open loft', rating: 4.4, icon: '☕' },
      { id: '3', lat: 37.7614, lng: -122.4241, label: 'Ritual Coffee Roasters', description: 'Single-origin specialists', rating: 4.3, icon: '☕' },
    ],
  },
  'quiz': {
    question: 'What is the tallest mountain in the solar system?',
    image_url: 'https://images.unsplash.com/photo-1614728263952-84ea256f9679?w=600&q=80',
    options: [
      { id: 'a', text: 'Mount Everest (Earth)', correct: false },
      { id: 'b', text: 'Olympus Mons (Mars)', correct: true },
      { id: 'c', text: 'Maxwell Montes (Venus)', correct: false },
      { id: 'd', text: 'Boösaule Montes (Io)', correct: false },
    ],
    explanation: 'Olympus Mons on Mars stands at approximately 21.9 km (13.6 mi) tall, making it nearly 2.5 times the height of Mount Everest.',
  },
  'conversation': {
    topic: 'Planning a weekend hike',
    messages: [
      { role: 'user', content: 'Can you suggest a good day hike near San Francisco?' },
      { role: 'ai', content: 'I recommend the Dipsea Trail in Marin County! It\'s about 7 miles one-way from Mill Valley to Stinson Beach, with beautiful ocean views and redwood groves.' },
      { role: 'user', content: 'How difficult is it?' },
      { role: 'ai', content: 'It\'s rated moderate to strenuous. The first section has ~700 stairs, but the views from the ridgeline are spectacular. Allow 3-4 hours and bring plenty of water.' },
    ],
    input_placeholder: 'Ask a follow-up question...',
  },
  'comparison-table': {
    title: 'Laptop Comparison',
    items: [
      { name: 'MacBook Air M3', highlight: true, verdict: 'Best Overall', rating: 4.7, price: '$1,099', pros: ['Fanless design', 'All-day battery', 'Great display'], cons: ['Only 2 ports', 'Base model 8GB RAM'], attributes: { Chip: 'Apple M3', RAM: '8-24 GB', Storage: '256GB-2TB', Weight: '1.24 kg' } },
      { name: 'ThinkPad X1 Carbon', verdict: 'Best for Business', rating: 4.5, price: '$1,399', pros: ['Amazing keyboard', 'Multiple ports', 'MIL-SPEC durable'], cons: ['Shorter battery', 'Heavier'], attributes: { Chip: 'Intel Ultra 7', RAM: '16-32 GB', Storage: '512GB-2TB', Weight: '1.12 kg' } },
      { name: 'Dell XPS 13', verdict: 'Most Compact', rating: 4.3, price: '$999', pros: ['Ultra-compact', 'Great price', 'OLED option'], cons: ['Limited ports', 'Webcam angle'], attributes: { Chip: 'Intel Ultra 5', RAM: '16 GB', Storage: '512GB-1TB', Weight: '1.17 kg' } },
    ],
  },
  'thinking-process': {
    title: 'Analyzing your photo...',
    steps: [
      { title: 'Image Recognition', description: 'Identifying objects, text, and scene context in the photo.' },
      { title: 'Scene Analysis', description: 'Determining the environment: indoor office with natural lighting and multiple monitors.' },
      { title: 'Detail Extraction', description: 'Reading visible text, identifying brands, and noting spatial relationships.' },
      { title: 'Context Synthesis', description: 'Combining observations into a coherent understanding of the scene.' },
    ],
    conclusion: 'The photo shows a modern workspace with dual monitors, a mechanical keyboard, and natural light from a nearby window.',
  },
  'calendar-event': {
    title: 'Team Standup Meeting',
    start_time: 'Mon, March 11 · 9:30 AM',
    end_time: '10:00 AM PST',
    location: 'Zoom (link in calendar invite)',
    description: 'Daily sync — Engineering team reviews progress, blockers, and plans for the day.',
    attendees: ['SC', 'JW', 'ML', 'KR', 'AP'],
    status: 'pending',
    status_label: 'Pending',
  },
  'image-analysis': {
    photo_url: 'https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?w=800&q=80',
    title: 'Urban Street Scene',
    description: 'Outdoor urban street with commercial storefronts during golden hour.',
    detected_objects: [
      { label: 'Car', confidence_pct: 97 },
      { label: 'Pedestrian', confidence_pct: 94 },
      { label: 'Traffic Light', confidence_pct: 91 },
      { label: 'Storefront', confidence_pct: 88 },
      { label: 'Sign', confidence_pct: 85 },
    ],
    tags: ['urban', 'street', 'golden-hour', 'commercial', 'outdoor'],
  },
  'document-scan': {
    title: 'Business Card Scan',
    photo_url: 'https://images.unsplash.com/photo-1611532736597-de2d4265fba3?w=600&q=80',
    fields: [
      { label: 'Name', value: 'Sarah Chen' },
      { label: 'Title', value: 'Senior Product Designer' },
      { label: 'Company', value: 'Acme Corp' },
      { label: 'Email', value: 'sarah@acme.co' },
      { label: 'Phone', value: '+1 (415) 555-0123' },
    ],
    raw_text: 'Sarah Chen\nSenior Product Designer\nAcme Corp\nsarah@acme.co\n+1 (415) 555-0123',
  },
  'evolution-suggestion': {
    skill_name: 'Interior Design Assistant',
    suggestion: 'Add color palette extraction from room photos to suggest complementary furniture and accent colors.',
    reason: 'Users frequently ask about color coordination after receiving room analysis results.',
    impact: 'high',
  },
  'file-download': {
    filename: 'room-analysis-report.pdf',
    size: '2.4 MB',
    type: 'pdf',
    url: '#',
    description: 'Detailed room analysis with measurements and recommendations.',
  },
  'memory-update': {
    items: [
      { field: 'Preferred style', value: 'Modern minimalist with warm accents' },
      { field: 'Budget range', value: '$5,000 - $10,000' },
      { field: 'Room focus', value: 'Living room renovation' },
    ],
  },
  'plant-animal-id': {
    title: 'Monstera Deliciosa',
    subtitle: 'Swiss Cheese Plant',
    icon: '🌿',
    fields: [
      { label: 'Family', value: 'Araceae' },
      { label: 'Origin', value: 'Central America' },
      { label: 'Light', value: 'Bright indirect', highlight: true },
      { label: 'Water', value: 'Weekly, let soil dry between' },
      { label: 'Toxicity', value: 'Toxic to pets' },
    ],
  },
  'session-summary': {
    title: 'Session Complete',
    subtitle: 'Room analysis finished',
    icon: '✅',
    fields: [
      { label: 'Duration', value: '3 min 42 sec' },
      { label: 'Cards generated', value: '5', highlight: true },
      { label: 'Photos analyzed', value: '3' },
      { label: 'Recommendations', value: '8 items' },
    ],
    footer: 'Results saved to your history.',
  },
  'step-guide': {
    title: 'How to Measure Your Room',
    steps: [
      { title: 'Clear the space', description: 'Remove furniture from walls for accurate measurements.' },
      { title: 'Measure wall lengths', description: 'Use a tape measure along each wall, noting doors and windows.' },
      { title: 'Note ceiling height', description: 'Measure floor to ceiling in at least two spots.' },
      { title: 'Photograph each wall', description: 'Take straight-on photos of each wall for reference.' },
    ],
    conclusion: 'With these measurements, we can generate accurate furniture placement suggestions.',
  },
  'text-result': {
    title: 'Analysis Summary',
    content: 'Your living room has excellent natural light from the south-facing windows. The 4.2m × 5.8m dimensions allow for a generous seating arrangement. Consider a sectional sofa along the east wall with a coffee table centered in the space.',
  },
  'video-player': {
    title: 'Room Walkthrough',
    url: 'https://www.w3schools.com/html/mov_bbb.mp4',
    poster: 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=600&q=80',
  },
  'hero-image': {
    image_url: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=800&q=80',
    title: 'Sunset at Ocean Beach',
    subtitle: 'San Francisco, CA',
    description: 'Golden hour light over the Pacific, captured during an evening walk along the shoreline.',
    actions: [
      { label: 'Save', action: 'download_file', icon: '💾' },
      { label: 'Share', action: 'share', icon: '🔗' },
    ],
  },
  'ai-generation': {
    type: 'image',
    title: 'Generating artwork from drawing...',
    status: 'generating',
    detail: 'Sending to Gemini API...',
    startedAt: Date.now() - 12000,
  },
  'freeform-html': {
    html: '<div style="font-family: system-ui, sans-serif;">' +
      '<h2 style="margin: 0 0 12px; font-size: 20px;">Custom HTML Content</h2>' +
      '<p style="color: #555; line-height: 1.6;">This is a <strong>freeform HTML</strong> card. It renders arbitrary HTML content streamed from the agent — useful for rich formatting that doesn\'t fit a structured template.</p>' +
      '<ul style="color: #444; padding-left: 20px; line-height: 1.8;">' +
        '<li>Supports <em>any</em> valid HTML</li>' +
        '<li>Tables, lists, images, styled text</li>' +
        '<li>Used as fallback when no template matches</li>' +
      '</ul>' +
      '<table style="width: 100%; border-collapse: collapse; margin-top: 12px; font-size: 13px;">' +
        '<tr style="background: #f5f5f5;"><th style="text-align: left; padding: 8px; border: 1px solid #ddd;">Feature</th><th style="text-align: left; padding: 8px; border: 1px solid #ddd;">Status</th></tr>' +
        '<tr><td style="padding: 8px; border: 1px solid #ddd;">Streaming</td><td style="padding: 8px; border: 1px solid #ddd;">✅ Supported</td></tr>' +
        '<tr><td style="padding: 8px; border: 1px solid #ddd;">Interactivity</td><td style="padding: 8px; border: 1px solid #ddd;">❌ Static only</td></tr>' +
      '</table>' +
    '</div>',
  },
  // Sibling of freeform-html: same renderer (PersistentHtmlRenderer), same HTML payload shape,
  // but frontend buckets these into a collapsible Debug panel. Used by the nanoclaw agent-speak
  // hook to publish tool I/O without cluttering the main card list. See Issue #1007.
  'debug-html': {
    html: '<div style="font-family: ui-monospace, monospace; font-size: 12px; padding: 10px 12px; border-left: 3px solid #4a90e2; background: #fafafa; border-radius: 4px;">' +
      '<div style="font-weight: 600; margin-bottom: 6px;">🛠 mcp__shared_tools__vlm_analyze</div>' +
      '<div style="color: #888; margin-bottom: 4px;">INPUT</div>' +
      '<pre style="white-space: pre-wrap; margin: 0 0 8px 0;">{"image_url":"...","prompt":"describe outfit"}</pre>' +
      '<div style="color: #888; margin-bottom: 4px;">OUTPUT <span style="font-weight: normal;">⏱ 1.2s · 312 chars</span></div>' +
      '<pre style="white-space: pre-wrap; margin: 0;">{"description":"A person wearing a blue jacket..."}</pre>' +
    '</div>',
  },
};

function generateSampleData(tpl, serverExamples = {}) {
  const id = tpl.$id || '';
  // 1. Prefer server-provided example data (hot-updatable via /package-drive)
  if (serverExamples[id]) return serverExamples[id];
  // 2. Fallback to local hardcoded examples
  if (SAMPLE_DATA[id]) return SAMPLE_DATA[id];
  // 3. Final fallback: generate from slot types
  const data = {};
  const slots = tpl.slots || {};
  for (const [name, slot] of Object.entries(slots)) {
    if (slot.type === 'string') data[name] = 'Sample ' + name;
    else if (slot.type === 'number') data[name] = 42;
    else if (slot.type === 'boolean') data[name] = true;
    else if (slot.type === 'array') data[name] = [];
    else data[name] = null;
  }
  return data;
}

// ── PackageConfigView — shows manifest config: instruction, tools, apis, skills ──

function ConfigSection({ title, children }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{
        fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em',
        color: V.textDim, marginBottom: 8,
      }}>{title}</div>
      {children}
    </div>
  );
}

function ConfigRow({ label, value, mono }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
      padding: '6px 0', borderBottom: `1px solid ${V.border}`,
    }}>
      <span style={{ fontSize: 12, color: V.textDim }}>{label}</span>
      <span style={{
        fontSize: 12, fontWeight: 500, color: V.text,
        fontFamily: mono ? V.font : 'inherit',
      }}>{value || '—'}</span>
    </div>
  );
}

function ConfigChips({ items, color = V.accent, statusMap, chipType }) {
  const [hoveredChip, setHoveredChip] = useState(null);
  if (!items?.length) return <span style={{ fontSize: 12, color: V.textDim }}>None</span>;

  const typeLabels = { api: 'API Key', tool: 'Tool', skill: 'Skill', card: 'Card Template' };
  const remediations = { api: 'Add API key in Settings', tool: 'Install tool package', skill: 'Install skill package', card: 'Add card template' };

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {items.map((item, i) => {
        const status = statusMap?.[item];
        const isMissing = status && status.available === false;
        const chipColor = isMissing ? V.red : color;
        return (
          <span key={i} style={{ position: 'relative', display: 'inline-block' }}
            onMouseEnter={() => setHoveredChip(i)}
            onMouseLeave={() => setHoveredChip(null)}>
            <span style={{
              padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 500,
              background: `${chipColor}18`, color: chipColor, border: `1px solid ${chipColor}30`,
              display: 'inline-block',
            }}>{item}</span>
            {hoveredChip === i && (
              <div style={{
                position: 'absolute', bottom: 'calc(100% + 8px)', left: '50%', transform: 'translateX(-50%)',
                background: V.text, color: '#fff', borderRadius: 8, padding: '8px 12px',
                fontSize: 11, maxWidth: 240, zIndex: 1000, whiteSpace: 'nowrap', pointerEvents: 'none',
                fontFamily: V.fontUi,
              }}>
                <div><b>Name:</b> {item}</div>
                {chipType && <div><b>Type:</b> {typeLabels[chipType] || chipType}</div>}
                {status && (
                  <div><b>Status:</b> <span style={{ color: status.available ? V.green : V.red }}>
                    {status.available ? 'Available \u2713' : 'Missing \u2717'}
                  </span></div>
                )}
                {status && !status.available && chipType && (
                  <div><b>Action:</b> {remediations[chipType]}</div>
                )}
                {status?.detail && <div><b>Detail:</b> {status.detail}</div>}
              </div>
            )}
          </span>
        );
      })}
    </div>
  );
}

function TemplateChipWithVersion({ name, allTemplates, onDelete }) {
  const tpl = allTemplates.find(t => t.$id === name);
  const version = tpl?.version;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 0' }}>
      <span style={{
        padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 500,
        background: `${V.green}18`, color: V.green, border: `1px solid ${V.green}30`,
      }}>{name}</span>
      {version && (
        <span style={{ fontSize: 10, color: V.textDim, fontFamily: 'monospace' }}>v{version}</span>
      )}
      {onDelete && (
        <button onClick={() => onDelete(name)} title={`Delete ${name}`}
          style={{ background: 'none', border: `1px solid ${V.border}`, borderRadius: 4, padding: '1px 5px', fontSize: 10, cursor: 'pointer', color: '#f87171', marginLeft: 'auto' }}>
          ✕
        </button>
      )}
    </div>
  );
}

function PackageConfigView({ manifest, healthData, localTemplates = [], onTemplateDelete }) {
  const inst = manifest?.instruction || {};
  const tools = manifest?.tools || {};
  const apis = manifest?.apis || {};
  const output = manifest?.output || {};
  const trigger = manifest?.trigger || {};
  const sharedTools = tools.shared || [];
  const bundledTools = tools.bundled || [];
  const builtinTools = tools.builtin || [];
  const apiKeys = Object.keys(apis);
  const sharedSkills = manifest?.shared_skills || [];

  // Build a lookup for template versions from localTemplates
  const allTemplatesLookup = localTemplates.map(t => ({ $id: t.id, version: t.version }));
  const templates = manifest?.templates || {};
  const bundledTemplates = templates.bundled || [];
  const sharedTemplates = templates.shared || [];

  // Build statusMap for API keys from health data
  const apiStatusMap = useMemo(() => {
    if (!healthData?.apis) return null;
    const map = {};
    apiKeys.forEach(key => {
      const entry = healthData.apis.find(a => a.name === key);
      if (entry) {
        map[key] = {
          available: entry.status === 'ok',
          detail: entry.envVars?.map(e => `${e.envVar}: ${e.set ? 'set' : 'not set'}`).join(', ') || '',
        };
      } else {
        map[key] = { available: false, detail: 'Not found in health check' };
      }
    });
    return map;
  }, [healthData, apiKeys]);

  if (!manifest) return <div style={{ color: V.textDim, fontSize: 13 }}>No manifest data available.</div>;

  return (
    <div style={{ maxWidth: 640 }}>
      {/* Parent Package (Issue #1169 — dual-level scheme) */}
      {manifest.parentPackage && (
        <ConfigSection title="Parent Package">
          <ConfigRow label="Parent" value={manifest.parentPackage} mono />
          <div style={{ fontSize: 11, color: V.textDim, marginTop: 4 }}>
            This is a specialized (L1) package — hidden from top-level CLAUDE.md listing, revealed when parent is activated.
          </div>
        </ConfigSection>
      )}

      {/* Instruction */}
      <ConfigSection title="Instruction">
        <ConfigRow label="Model" value={inst.model} mono />
        <ConfigRow label="File" value={inst.file} mono />
        <ConfigRow label="Max Turns" value={inst.max_turns} />
        <ConfigRow label="Max Tokens" value={inst.max_tokens?.toLocaleString()} />
      </ConfigSection>

      {/* Tools */}
      <ConfigSection title="Tools">
        {sharedTools.length > 0 && (
          <div style={{ marginBottom: 6 }}>
            <span style={{ fontSize: 11, color: V.textDim, marginRight: 8 }}>Shared:</span>
            <ConfigChips items={sharedTools} color={V.blue} chipType="tool" />
          </div>
        )}
        {bundledTools.length > 0 && (
          <div style={{ marginBottom: 6 }}>
            <span style={{ fontSize: 11, color: V.textDim, marginRight: 8 }}>Bundled:</span>
            <ConfigChips items={bundledTools} color={V.blue} chipType="tool" />
          </div>
        )}
        {builtinTools.length > 0 && (
          <div>
            <span style={{ fontSize: 11, color: V.textDim, marginRight: 8 }}>Builtin:</span>
            <ConfigChips items={builtinTools} color={V.textDim} chipType="tool" />
          </div>
        )}
        {sharedTools.length === 0 && bundledTools.length === 0 && builtinTools.length === 0 && (
          <span style={{ fontSize: 12, color: V.textDim }}>No custom tools — uses standard tools</span>
        )}
      </ConfigSection>

      {/* API Keys */}
      <ConfigSection title="API Keys">
        {apiKeys.length > 0 ? (
          <ConfigChips items={apiKeys} color={V.yellow} statusMap={apiStatusMap} chipType="api" />
        ) : (
          <span style={{ fontSize: 12, color: V.textDim }}>No external API keys required</span>
        )}
      </ConfigSection>

      {/* Shared Skills */}
      <ConfigSection title="Shared Skills">
        <ConfigChips items={sharedSkills} color={V.purple} chipType="skill" />
      </ConfigSection>

      {/* Templates */}
      <ConfigSection title="Card Templates">
        {bundledTemplates.length > 0 && (
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: V.textDim, textTransform: 'uppercase', marginBottom: 4 }}>Bundled</div>
            {bundledTemplates.map(name => (
              <TemplateChipWithVersion key={name} name={name} allTemplates={allTemplatesLookup} />
            ))}
          </div>
        )}
        {sharedTemplates.length > 0 && (
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, color: V.textDim, textTransform: 'uppercase', marginBottom: 4 }}>Shared</div>
            {sharedTemplates.map(name => (
              <TemplateChipWithVersion key={name} name={name} allTemplates={allTemplatesLookup} onDelete={onTemplateDelete} />
            ))}
          </div>
        )}
        {bundledTemplates.length === 0 && sharedTemplates.length === 0 && (
          <span style={{ fontSize: 12, color: V.textDim }}>None</span>
        )}
      </ConfigSection>

      {/* Output */}
      <ConfigSection title="Output">
        <ConfigRow label="Primary Template" value={output.template} mono />
        <ConfigRow label="Card Sequence" value={(output.card_sequence || []).join(' → ')} />
        <ConfigRow label="Auto Publish" value={output.auto_publish ? 'Yes' : 'No'} />
        <ConfigRow label="Est. Time" value={output.estimated_time} />
        <ConfigRow label="Est. Cost" value={output.estimated_cost} />
      </ConfigSection>

      {/* Trigger */}
      {(trigger.visual_cues?.length > 0 || trigger.voice_keywords?.length > 0) && (
        <ConfigSection title="Trigger">
          {trigger.intention_level && (
            <ConfigRow label="Intention Level" value={trigger.intention_level} />
          )}
          {trigger.visual_cues?.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 11, color: V.textDim, marginBottom: 4 }}>Visual Cues</div>
              <ConfigChips items={trigger.visual_cues} color={V.yellow} />
            </div>
          )}
          {trigger.voice_keywords?.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 11, color: V.textDim, marginBottom: 4 }}>Voice Keywords</div>
              <ConfigChips items={trigger.voice_keywords} color={V.blue} />
            </div>
          )}
        </ConfigSection>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// PACKAGES PAGE — list packages, select, chat-test with logged-in user
// ══════════════════════════════════════════════════════════════════════

function PackagesPage({ nanoClaw }) {
  let renderPkgItem; // hoisted — assigned in JSX IIFE, used across domain/other sections
  let globalChildMap = {}; // Issue #1169: parent→children map, assigned in IIFE
  let childPkgIds = new Set(); // IDs of packages that have a parentPackage
  const [packages, setPackages] = useState([]);
  const [domains, setDomains] = useState([]); // domain-grouped view
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState('skill'); // 'skill' | 'config' | 'testbench'
  const [loading, setLoading] = useState(true);

  // S3 sync state
  const [s3Env, setS3Env] = useState(null); // null until env info loaded
  const [syncStatus, setSyncStatus] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalAction, setModalAction] = useState(''); // 'promote' | 'revert'
  const [diffItems, setDiffItems] = useState([]);
  const [diffLoading, setDiffLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [promoting, setPromoting] = useState(false);

  // Fetch environment info to set default s3Env from the deployment environment
  // (dev/staging/production), not the backend's s3Env field — see S3ManagerPage
  // for the rationale.
  useEffect(() => {
    const envToS3 = (env) => {
      if (env === 'production') return 'prod';
      if (env === 'staging') return 'staging';
      return 'dev'; // dev / development / unset
    };
    api.authFetch('/api/nanoclaw/dashboard/environment')
      .then(r => r.json())
      .then(d => { if (!s3Env) setS3Env(envToS3(d.environment) || d.s3Env || 'dev'); })
      .catch(() => { if (!s3Env) setS3Env('dev'); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // SFTP Manager state
  const [viewMode, setViewMode] = useState('detail'); // 'detail' | 'manager'
  const [localPackages, setLocalPackages] = useState([]);
  const [remotePackages, setRemotePackages] = useState([]);
  const [archivedIds, setArchivedIds] = useState(new Set());

  // Template Manager state (individual card sync)
  const [localTemplates, setLocalTemplates] = useState([]);
  const [remoteTemplates, setRemoteTemplates] = useState([]);
  const [archivedTemplateIds, setArchivedTemplateIds] = useState(new Set());

  // Testbench state — mirrors ChatPage pattern
  const [sessions, setSessions] = useState([]);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [liveBlocks, setLiveBlocks] = useState([]);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const seenCardIdsRef = useRef(new Set());

  // CLAUDE.md state
  const [claudeMd, setClaudeMd] = useState('');
  const [claudeMdVersion, setClaudeMdVersion] = useState(null);
  const [claudeMdChangelog, setClaudeMdChangelog] = useState('');
  const [claudeMdTab, setClaudeMdTab] = useState('content'); // 'content' | 'changelog'
  const [claudeMdSelected, setClaudeMdSelected] = useState(false);
  const [claudeMdOnS3, setClaudeMdOnS3] = useState(false);
  const [claudeMdS3Version, setClaudeMdS3Version] = useState(null);
  const [s3Enabled, setS3Enabled] = useState(null); // null = unknown, true/false = known

  // Shared resources (_shared overview)
  const [sharedOverview, setSharedOverview] = useState(null); // { manifest, templates, tools, claudeMd }
  const [sharedSelected, setSharedSelected] = useState(null); // 'templates' | 'tools' | null

  // Health data for config status
  const [healthData, setHealthData] = useState(null);

  const loadPackages = useCallback(() => {
    api.authFetch(`/api/nanoclaw/dashboard/package-skills?_t=${Date.now()}`)
      .then(r => r.json())
      .then(d => {
        const pkgSkills = (d.skills || []).filter(s => s.source === 'package');
        setPackages(pkgSkills);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const loadDomains = useCallback(() => {
    api.authFetch(`/api/nanoclaw/dashboard/domains?_t=${Date.now()}`)
      .then(r => r.json())
      .then(d => setDomains(d.domains || []))
      .catch(() => {});
  }, []);

  const loadLocalPackages = useCallback(() => {
    api.authFetch(`/api/nanoclaw/dashboard/packages?_t=${Date.now()}`)
      .then(r => r.json())
      .then(d => setLocalPackages(d.packages || []))
      .catch(() => setLocalPackages([]));
  }, []);

  const loadRemotePackages = useCallback(() => {
    api.authFetch(`/api/nanoclaw/dashboard/packages/remote?env=${s3Env}`)
      .then(r => r.json())
      .then(d => {
        const pkgs = d.packages || [];
        setRemotePackages(pkgs);
        // Derive CLAUDE.md S3 status from _system package in remote list
        const systemPkg = pkgs.find(p => p.id === '_system');
        setClaudeMdOnS3(!!systemPkg);
        setClaudeMdS3Version(systemPkg?.version || null);
        setS3Enabled(d.s3Enabled ?? null);
      })
      .catch(() => setRemotePackages([]));
  }, [s3Env]);

  const loadClaudeMd = useCallback(() => {
    api.authFetch('/api/nanoclaw/dashboard/claude-md')
      .then(r => r.json())
      .then(d => {
        setClaudeMd(d.content || '');
        setClaudeMdVersion(d.version || null);
        setClaudeMdChangelog(d.changelog || '');
      })
      .catch(() => { setClaudeMd(''); setClaudeMdVersion(null); setClaudeMdChangelog(''); });
  }, []);

  const loadArchived = useCallback(async () => {
    if (!s3Env) return;
    try {
      const res = await api.authFetch(`/api/nanoclaw/dashboard/packages/archived?env=${s3Env}`);
      const d = await res.json();
      setArchivedIds(new Set(d.archived || []));
    } catch { /* ignore */ }
  }, [s3Env]);

  const loadLocalTemplates = useCallback(() => {
    api.authFetch(`/api/nanoclaw/dashboard/templates/local?_t=${Date.now()}`)
      .then(r => r.json())
      .then(d => setLocalTemplates(d.templates || []))
      .catch(() => setLocalTemplates([]));
  }, []);

  const loadRemoteTemplates = useCallback(() => {
    api.authFetch(`/api/nanoclaw/dashboard/templates/remote?env=${s3Env}`)
      .then(r => r.json())
      .then(d => setRemoteTemplates(d.templates || []))
      .catch(() => setRemoteTemplates([]));
  }, [s3Env]);

  const loadArchivedTemplates = useCallback(async () => {
    if (!s3Env) return;
    try {
      const res = await api.authFetch(`/api/nanoclaw/dashboard/templates/archived?env=${s3Env}`);
      const d = await res.json();
      setArchivedTemplateIds(new Set(d.archived || []));
    } catch { /* ignore */ }
  }, [s3Env]);

  const loadSharedOverview = useCallback(() => {
    api.authFetch('/api/nanoclaw/dashboard/packages/_shared/overview')
      .then(r => r.json())
      .then(d => setSharedOverview(d))
      .catch(() => setSharedOverview(null));
  }, []);

  const loadHealth = useCallback(() => {
    api.authFetch(`/api/nanoclaw/dashboard/health/all?_t=${Date.now()}`)
      .then(r => r.json())
      .then(d => setHealthData(d))
      .catch(() => setHealthData(null));
  }, []);

  // Canonical "refresh everything" — every mutation handler must call this so
  // the package list, domain mapping, and archived set stay consistent across
  // both the S3 and Packages tabs (issue #959). Stable identity via useCallback.
  const refreshAllPackages = useCallback(() => {
    loadPackages();
    loadDomains();
    loadLocalPackages();
    loadRemotePackages();
    loadArchived();
    loadLocalTemplates();
    loadRemoteTemplates();
    loadArchivedTemplates();
    loadClaudeMd();
  }, [loadPackages, loadDomains, loadLocalPackages, loadRemotePackages, loadArchived, loadLocalTemplates, loadRemoteTemplates, loadArchivedTemplates, loadClaudeMd]);

  useEffect(() => { refreshAllPackages(); loadSharedOverview(); loadHealth(); }, [refreshAllPackages, loadSharedOverview, loadHealth]);

  // S3 sync status
  useEffect(() => {
    api.authFetch(`/api/nanoclaw/dashboard/packages/sync-status?env=${s3Env}`)
      .then(r => r.json())
      .then(d => {
        if (!d.enabled) setSyncStatus('S3 sync disabled');
        else if (d.lastSync === 'never') setSyncStatus('Never synced');
        else setSyncStatus(`Last: ${new Date(d.lastSync).toLocaleString()} (${d.fileCount} files)`);
      })
      .catch(() => setSyncStatus(''));
  }, [s3Env]);

  const handleSync = useCallback(async () => {
    setSyncing(true);
    setSyncStatus('Syncing...');
    try {
      const res = await api.authFetch('/api/nanoclaw/dashboard/packages/sync', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ env: s3Env })
      });
      const d = await res.json();
      setSyncStatus(`Synced: ${d.sync?.downloaded || 0} downloaded, ${d.sync?.unchanged || 0} unchanged`);
      refreshAllPackages();
    } catch (err) {
      setSyncStatus(`Sync failed: ${err.message}`);
    }
    setSyncing(false);
  }, [s3Env, refreshAllPackages]);

  const handlePush = useCallback(async (pkgId) => {
    setSyncing(true);
    setSyncStatus(`Pushing ${pkgId}...`);
    try {
      // CLAUDE.md: save locally via claude-md/push, then push _system package to S3
      if (pkgId === 'CLAUDE.md') {
        // Save locally first
        await api.authFetch('/api/nanoclaw/dashboard/claude-md/push', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ content: claudeMd })
        });
        // Push _system package to S3 (CLAUDE.md lives inside _system)
        const res = await api.authFetch(`/api/nanoclaw/dashboard/packages/_system/push`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ env: s3Env })
        });
        const d = await res.json();
        if (d.error || d.conflict) {
          setSyncStatus(`❌ ${d.error || 'Version conflict'}`);
        } else {
          setSyncStatus('✓ Pushed CLAUDE.md (_system) to S3');
          refreshAllPackages();
        }
        return;
      }
      const res = await api.authFetch(`/api/nanoclaw/dashboard/packages/${encodeURIComponent(pkgId)}/push`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ env: s3Env })
      });
      const d = await res.json();
      if (res.status === 409) {
        setSyncStatus(`⚠ Conflict: remote v${d.remoteVersion} > local v${d.localVersion}`);
      } else if (d.errors?.length) {
        setSyncStatus(`❌ ${d.errors[0]}`);
      } else if (d.uploaded === 0) {
        setSyncStatus(`⚠ Push ${pkgId}: 0 files uploaded (S3 may be disabled)`);
      } else {
        setSyncStatus(`✓ Pushed ${pkgId}: ${d.uploaded} files`);
        refreshAllPackages();
      }
    } catch (err) { setSyncStatus(`❌ Push failed: ${err.message}`); }
    setSyncing(false);
  }, [s3Env, claudeMd, refreshAllPackages]);

  const handlePushAll = useCallback(async () => {
    if (!localPackages.length) { setSyncStatus('No local packages to push'); return; }
    setSyncing(true);
    setSyncStatus('Pushing all local packages...');
    let pushed = 0;
    let errors = 0;
    let firstError = '';
    for (const pkg of localPackages) {
      try {
        const res = await api.authFetch(`/api/nanoclaw/dashboard/packages/${encodeURIComponent(pkg.id)}/push`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ env: s3Env })
        });
        const d = await res.json();
        if (d.errors?.length) { errors++; if (!firstError) firstError = d.errors[0]; }
        else if (d.uploaded > 0) pushed++;
        else errors++;
      } catch { errors++; }
    }
    if (errors && !pushed) {
      setSyncStatus(`❌ Push failed: ${firstError || 'all packages errored'}`);
    } else {
      setSyncStatus(`${pushed ? '✓' : '⚠'} Pushed ${pushed}/${localPackages.length} packages${errors ? `, ${errors} errors` : ''}`);
    }
    refreshAllPackages();
    setSyncing(false);
  }, [localPackages, s3Env, refreshAllPackages]);

  const handlePull = useCallback(async () => {
    setSyncing(true);
    setSyncStatus('Pulling from S3...');
    try {
      const res = await api.authFetch('/api/nanoclaw/dashboard/packages/sync', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ env: s3Env })
      });
      const d = await res.json();
      setSyncStatus(`Synced: ${d.sync?.downloaded || 0} downloaded`);
      refreshAllPackages();
    } catch (err) { setSyncStatus(`Pull failed: ${err.message}`); }
    setSyncing(false);
  }, [s3Env, refreshAllPackages]);

  const handleArchive = useCallback(async (ids) => {
    setSyncStatus(`Archiving ${ids.length} package(s) in ${s3Env}...`);
    try {
      const res = await api.authFetch('/api/nanoclaw/dashboard/packages/archive', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids, env: s3Env })
      });
      const d = await res.json();
      setSyncStatus(`✓ Archived ${d.archived?.length || 0} package(s) in ${s3Env}`);
      refreshAllPackages();
    } catch (err) { setSyncStatus(`❌ Archive failed: ${err.message}`); }
  }, [s3Env, refreshAllPackages]);

  const handleUnarchive = useCallback(async (ids) => {
    setSyncStatus(`Unarchiving ${ids.length} package(s) in ${s3Env}...`);
    try {
      const res = await api.authFetch('/api/nanoclaw/dashboard/packages/unarchive', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids, env: s3Env })
      });
      const d = await res.json();
      setSyncStatus(`✓ Unarchived ${d.unarchived?.length || 0} package(s) in ${s3Env}`);
      refreshAllPackages();
    } catch (err) { setSyncStatus(`❌ Unarchive failed: ${err.message}`); }
  }, [s3Env, refreshAllPackages]);

  const handleSinglePull = useCallback(async (pkgId) => {
    setSyncing(true);
    setSyncStatus(`Pulling ${pkgId}...`);
    try {
      const res = await api.authFetch(`/api/nanoclaw/dashboard/packages/${encodeURIComponent(pkgId)}/pull`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ env: s3Env })
      });
      const d = await res.json();
      if (d.errors?.length) {
        setSyncStatus(`❌ ${d.errors[0]}`);
      } else {
        setSyncStatus(`✓ Pulled ${pkgId}: ${d.downloaded || 0} files downloaded`);
        refreshAllPackages();
      }
    } catch (err) { setSyncStatus(`❌ Pull failed: ${err.message}`); }
    setSyncing(false);
  }, [s3Env, refreshAllPackages]);

  const handleBatchPull = useCallback(async (ids) => {
    setSyncing(true);
    setSyncStatus(`Pulling ${ids.length} package(s)...`);
    try {
      const res = await api.authFetch('/api/nanoclaw/dashboard/packages/batch-pull', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids, env: s3Env })
      });
      const d = await res.json();
      const s = d.summary;
      setSyncStatus(`✓ Pulled ${s.succeeded}/${s.total}${s.errors ? `, ${s.errors} errors` : ''}`);
      refreshAllPackages();
    } catch (err) { setSyncStatus(`❌ Batch pull failed: ${err.message}`); }
    setSyncing(false);
  }, [s3Env, refreshAllPackages]);

  const handleBatchPush = useCallback(async (ids) => {
    setSyncing(true);
    setSyncStatus(`Pushing ${ids.length} package(s)...`);
    try {
      const res = await api.authFetch('/api/nanoclaw/dashboard/packages/batch-push', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids, env: s3Env })
      });
      const d = await res.json();
      const s = d.summary;
      setSyncStatus(`✓ Pushed ${s.succeeded}/${s.total}${s.conflicts ? `, ${s.conflicts} conflicts` : ''}${s.errors ? `, ${s.errors} errors` : ''}`);
      refreshAllPackages();
    } catch (err) { setSyncStatus(`❌ Batch push failed: ${err.message}`); }
    setSyncing(false);
  }, [s3Env, refreshAllPackages]);

  // ── Template action handlers ──
  const handleTemplatePush = useCallback(async (tplId) => {
    setSyncStatus(`Pushing template ${tplId}...`);
    try {
      const res = await api.authFetch(`/api/nanoclaw/dashboard/templates/${encodeURIComponent(tplId)}/push`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ env: s3Env })
      });
      const d = await res.json();
      if (res.status === 409) {
        setSyncStatus(`⚠ Conflict: remote v${d.remoteVersion} > local v${d.localVersion}`);
      } else if (d.error) {
        setSyncStatus(`❌ ${d.error}`);
      } else {
        setSyncStatus(`✓ Pushed template ${tplId}`);
        refreshAllPackages();
      }
    } catch (err) { setSyncStatus(`❌ Push failed: ${err.message}`); }
  }, [s3Env, refreshAllPackages]);

  const handleTemplateSinglePull = useCallback(async (tplId) => {
    setSyncStatus(`Pulling template ${tplId}...`);
    try {
      const res = await api.authFetch(`/api/nanoclaw/dashboard/templates/${encodeURIComponent(tplId)}/pull`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ env: s3Env })
      });
      const d = await res.json();
      if (d.error) {
        setSyncStatus(`❌ ${d.error}`);
      } else {
        setSyncStatus(`✓ Pulled template ${tplId}: ${d.downloaded || 0} files`);
        refreshAllPackages();
      }
    } catch (err) { setSyncStatus(`❌ Pull failed: ${err.message}`); }
  }, [s3Env, refreshAllPackages]);

  const handleTemplateBatchPull = useCallback(async (ids) => {
    setSyncing(true);
    setSyncStatus(`Pulling ${ids.length} template(s)...`);
    try {
      const res = await api.authFetch('/api/nanoclaw/dashboard/templates/batch-pull', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids, env: s3Env })
      });
      const d = await res.json();
      const ok = (d.results || []).filter(r => r.status === 'ok').length;
      const err = (d.results || []).filter(r => r.status === 'error').length;
      setSyncStatus(`✓ Pulled ${ok}/${ids.length} template(s)${err ? `, ${err} errors` : ''}`);
      refreshAllPackages();
    } catch (err) { setSyncStatus(`❌ Batch pull failed: ${err.message}`); }
    setSyncing(false);
  }, [s3Env, refreshAllPackages]);

  const handleTemplateBatchPush = useCallback(async (ids) => {
    setSyncing(true);
    setSyncStatus(`Pushing ${ids.length} template(s)...`);
    try {
      const res = await api.authFetch('/api/nanoclaw/dashboard/templates/batch-push', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids, env: s3Env })
      });
      const d = await res.json();
      const ok = (d.results || []).filter(r => r.status === 'ok').length;
      const conflicts = (d.results || []).filter(r => r.status === 'conflict').length;
      setSyncStatus(`✓ Pushed ${ok}/${ids.length} template(s)${conflicts ? `, ${conflicts} conflicts` : ''}`);
      refreshAllPackages();
    } catch (err) { setSyncStatus(`❌ Batch push failed: ${err.message}`); }
    setSyncing(false);
  }, [s3Env, refreshAllPackages]);

  const handleTemplateArchive = useCallback(async (ids) => {
    setSyncStatus(`Archiving ${ids.length} template(s) in ${s3Env}...`);
    try {
      const res = await api.authFetch('/api/nanoclaw/dashboard/templates/archive', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids, env: s3Env })
      });
      const d = await res.json();
      setSyncStatus(`✓ Archived ${d.archived?.length || 0} template(s) in ${s3Env}`);
      refreshAllPackages();
    } catch (err) { setSyncStatus(`❌ Archive failed: ${err.message}`); }
  }, [s3Env, refreshAllPackages]);

  const handleTemplateUnarchive = useCallback(async (ids) => {
    setSyncStatus(`Unarchiving ${ids.length} template(s) in ${s3Env}...`);
    try {
      const res = await api.authFetch('/api/nanoclaw/dashboard/templates/unarchive', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids, env: s3Env })
      });
      const d = await res.json();
      setSyncStatus(`✓ Unarchived ${d.unarchived?.length || 0} template(s) in ${s3Env}`);
      refreshAllPackages();
    } catch (err) { setSyncStatus(`❌ Unarchive failed: ${err.message}`); }
  }, [s3Env, refreshAllPackages]);

  const openModal = useCallback(async (action) => {
    setModalAction(action);
    setModalOpen(true);
    setDiffLoading(true);
    setSelectedItems(new Set());
    setActionsOpen(false);
    try {
      const res = await api.authFetch('/api/nanoclaw/dashboard/packages/diff');
      const d = await res.json();
      setDiffItems((d.items || []).filter(i => i.status !== 'unchanged'));
    } catch { setDiffItems([]); }
    setDiffLoading(false);
  }, []);

  const handlePromoteRevert = useCallback(async () => {
    if (selectedItems.size === 0) return;
    const items = Array.from(selectedItems);
    const action = modalAction;
    const msg = action === 'promote'
      ? `Push ${items.length} item(s) from staging to PROD?`
      : `Revert ${items.length} item(s) from PROD to staging?`;
    if (!window.confirm(msg)) return;

    setPromoting(true);
    try {
      const endpoint = action === 'promote' ? '/api/nanoclaw/dashboard/packages/promote' : '/api/nanoclaw/dashboard/packages/revert';
      const res = await api.authFetch(endpoint, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });
      const d = await res.json();
      window.alert(`${action === 'promote' ? 'Promoted' : 'Reverted'}: ${d.copied || 0} files copied, ${d.deleted || 0} deleted.${d.errors?.length ? `\nErrors: ${d.errors.length}` : ''}`);
      setModalOpen(false);
      loadPackages();
    } catch (err) {
      window.alert(`Failed: ${err.message}`);
    }
    setPromoting(false);
  }, [selectedItems, modalAction, loadPackages]);

  // Load sessions filtered by selected package skill (uses shared api.getSessions)
  const loadSessions = useCallback(async () => {
    if (!selected) return;
    setSessionsLoading(true);
    try {
      let apiSessions;
      try { apiSessions = await api.getSessions(); }
      catch { apiSessions = await api.getSessionsByDevice(); }
      // Normalize to internal session shape and filter by package skill
      const normalized = (apiSessions || []).map(s => {
        const ts = new Date(s.started_at || s.dispatched_at || 0).getTime();
        return {
          sessionId: s.id,
          tasks: s.prompt ? [{ prompt: s.prompt, ts, sessionId: s.id, skillSlug: s.context?.skill_slug || null }] : [],
          latestTs: ts,
          status: s.status,
          title: s.title,
          result: s.result || null,
          result_html: s.result_html || '',
          result_summary: s.result_summary || '',
          timeline: s.timeline || [],
          artifacts: s.artifacts || [],
          thumbnail_url: s.thumbnail_url || null,
          context: s.context || null,
        };
      });
      const filtered = normalized
        .filter(s => s.tasks.some(t => t.skillSlug === selected.id))
        .sort((a, b) => b.latestTs - a.latestTs);
      setSessions(filtered);
    } catch { /* ignore */ }
    finally { setSessionsLoading(false); }
  }, [selected]);

  // Reload sessions when switching to testbench or selecting a new package
  useEffect(() => {
    if (tab === 'testbench' && selected) loadSessions();
  }, [tab, selected, loadSessions]);

  // Create package handler
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newPkgId, setNewPkgId] = useState('');
  const [newPkgName, setNewPkgName] = useState('');
  const [creating, setCreating] = useState(false);

  const handleCreatePackage = useCallback(async () => {
    if (!newPkgId.trim() || !newPkgName.trim()) return;
    setCreating(true);
    try {
      const res = await api.authFetch('/api/nanoclaw/dashboard/packages', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: newPkgId.trim(), name: newPkgName.trim() }),
      });
      const d = await res.json();
      if (!res.ok) { alert(d.error || 'Create failed'); return; }
      setCreateDialogOpen(false);
      setNewPkgId('');
      setNewPkgName('');
      refreshAllPackages();
    } catch (err) { alert(`Create failed: ${err.message}`); }
    finally { setCreating(false); }
  }, [newPkgId, newPkgName, refreshAllPackages]);

  // Collect live cards
  const liveCards = useMemo(() => {
    if (!nanoClaw?.cards) return [];
    return (nanoClaw.orderedCardIds || [])
      .map(id => nanoClaw.cards[id])
      .filter(Boolean);
  }, [nanoClaw?.cards, nanoClaw?.orderedCardIds]);

  // Send with package skill slug
  const handleSend = useCallback(async (text, mediaUrls) => {
    if (!selected) return;
    if (!text?.trim() && (!mediaUrls || mediaUrls.length === 0)) return;

    setLiveBlocks(prev => [...prev, {
      type: 'user', text, mediaUrls: mediaUrls || [], ts: Date.now(),
    }]);

    // If a task is actively running, inject input instead of dispatching new exec
    const running = (() => {
      if (!nanoClaw?.tasks) return null;
      const entries = Object.entries(nanoClaw.tasks);
      for (let i = entries.length - 1; i >= 0; i--) {
        const [id, t] = entries[i];
        if (t.status === 'loading' || t.status === 'streaming') return { taskId: id, sessionId: t.sessionId };
      }
      return null;
    })();

    if (running && selectedSessionId) {
      try {
        await api.injectInput(selectedSessionId, running.taskId, text);
      } catch (err) {
        console.error('injectInput failed:', err);
      }
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
      return;
    }

    setSending(true);
    try {
      const opts = {
        prompt: text,
        skillSlug: selected.id,
        mediaUrls: mediaUrls?.length ? mediaUrls : undefined,
      };
      if (selectedSessionId) opts.sessionId = selectedSessionId;
      const result = await api.dispatchExec(opts);
      // Set active session so nanoClaw card events get scoped
      if (result?.sessionId && nanoClaw?.setActiveSessionId) {
        nanoClaw.setActiveSessionId(result.sessionId);
        if (!selectedSessionId) setSelectedSessionId(result.sessionId);
      }
      // Refresh sessions
      setTimeout(() => loadSessions(), 2000);
    } catch (err) {
      setLiveBlocks(prev => [...prev, { type: 'error', text: err.message, ts: Date.now() }]);
    } finally {
      setSending(false);
    }
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  }, [selected, selectedSessionId, loadSessions, nanoClaw]);

  // ── Execution state — detect running tasks for stop button ──
  const pkgActiveTask = useMemo(() => {
    if (!nanoClaw?.tasks) return null;
    const entries = Object.entries(nanoClaw.tasks);
    for (let i = entries.length - 1; i >= 0; i--) {
      const [id, t] = entries[i];
      if (t.status === 'loading' || t.status === 'streaming') return { taskId: id, sessionId: t.sessionId };
    }
    return null;
  }, [nanoClaw?.tasks]);

  const handlePkgStop = useCallback(async () => {
    if (!pkgActiveTask) return;
    try {
      await api.stopSession(pkgActiveTask.sessionId || selectedSessionId, pkgActiveTask.taskId);
    } catch (err) {
      console.error('stopSession failed:', err);
    }
  }, [pkgActiveTask, selectedSessionId]);

  // Stream live cards from nanoClaw (append new, update existing, keep all)
  useEffect(() => {
    if (!nanoClaw?.cards) return;
    const orderedIds = nanoClaw.orderedCardIds || [];
    const newBlocks = [];
    for (const cardId of orderedIds) {
      const card = nanoClaw.cards[cardId];
      if (!card) continue;
      if (!seenCardIdsRef.current.has(cardId)) {
        seenCardIdsRef.current.add(cardId);
        newBlocks.push({ type: 'card', card: { cardId, ...card }, ts: card.createdAt || Date.now() });
      }
    }
    if (newBlocks.length > 0) {
      setLiveBlocks(prev => [...prev, ...newBlocks]);
    }
    setLiveBlocks(prev => prev.map(b => {
      if (b.type !== 'card') return b;
      const updated = nanoClaw.cards[b.card.cardId];
      return updated ? { ...b, card: { ...b.card, ...updated } } : b;
    }));
  }, [nanoClaw?.cards, nanoClaw?.orderedCardIds]);

  // Clear live blocks when switching sessions
  useEffect(() => { setLiveBlocks([]); if (seenCardIdsRef?.current) seenCardIdsRef.current.clear(); }, [selectedSessionId]);

  const selectedSession = sessions.find(s => s.sessionId === selectedSessionId);
  const { allCards, cardTaskMap, sortedTasks } = selectedSession
    ? deduplicateCards(selectedSession.tasks)
    : { allCards: {}, cardTaskMap: {}, sortedTasks: [] };

  const S = {
    sidebar: { width: 260, borderRight: `1px solid ${V.border}`, background: V.bgCard, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
    item: (active) => ({
      padding: '10px 14px', cursor: 'pointer', borderBottom: `1px solid ${V.border}`,
      background: active ? V.accentDim : 'transparent', transition: 'background 0.15s',
    }),
    main: { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
    header: { padding: '12px 16px', borderBottom: `1px solid ${V.border}`, background: V.bgCard, flexShrink: 0 },
    content: { flex: 1, overflow: 'auto', padding: 16 },
    tab: (active) => ({
      padding: '6px 14px', fontSize: 12, border: 'none', borderRadius: 6, cursor: 'pointer',
      background: active ? V.accent : V.bgHover, color: active ? '#fff' : V.textDim,
    }),
    btn: { background: V.bgHover, color: V.text, border: `1px solid ${V.border}`, borderRadius: 4, padding: '4px 10px', cursor: 'pointer', fontSize: 12 },
  };

  if (loading) return <div style={{ padding: 40, color: V.textDim }}>Loading packages...</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
      {/* Sidebar — package list */}
      <div style={S.sidebar}>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {/* AGENT section */}
          <div style={{ padding: '8px 14px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: V.textDim, borderBottom: `1px solid ${V.border}` }}>
            Agent
          </div>
          <div style={S.item(claudeMdSelected && !sharedSelected)}
            onClick={() => { setClaudeMdSelected(true); setSelected(null); setSharedSelected(null); }}
            onMouseEnter={e => { if (!claudeMdSelected || sharedSelected) e.currentTarget.style.background = V.bgHover; }}
            onMouseLeave={e => { if (!claudeMdSelected || sharedSelected) e.currentTarget.style.background = 'transparent'; }}
          >
            <div style={{ fontSize: 13, fontWeight: 500 }}>📄 CLAUDE.md</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
              <span style={{ fontSize: 11, color: V.textDim }}>Agent instructions</span>
              {claudeMdVersion ? (
                <span style={{ fontSize: 10, color: V.green || '#4ade80', fontFamily: 'monospace' }}>v{claudeMdVersion}</span>
              ) : (
                <span style={{ fontSize: 10, color: V.yellow || '#facc15', fontFamily: 'monospace' }}>no version</span>
              )}
            </div>
          </div>
          {/* SHARED RESOURCES section */}
          <div style={{ padding: '8px 14px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: V.textDim, borderBottom: `1px solid ${V.border}`, borderTop: `1px solid ${V.border}` }}>
            Shared {sharedOverview?.manifest?.version ? `v${sharedOverview.manifest.version}` : ''}
          </div>
          <div style={S.item(sharedSelected === 'templates')}
            onClick={() => { setSharedSelected('templates'); setClaudeMdSelected(true); setSelected(null); }}
            onMouseEnter={e => { if (sharedSelected !== 'templates') e.currentTarget.style.background = V.bgHover; }}
            onMouseLeave={e => { if (sharedSelected !== 'templates') e.currentTarget.style.background = 'transparent'; }}
          >
            <div style={{ fontSize: 13, fontWeight: 500 }}>🎴 Templates</div>
            <div style={{ fontSize: 11, color: V.textDim, marginTop: 2 }}>{sharedOverview?.templates?.length || 0} shared card schemas</div>
          </div>
          <div style={S.item(sharedSelected === 'tools')}
            onClick={() => { setSharedSelected('tools'); setClaudeMdSelected(true); setSelected(null); }}
            onMouseEnter={e => { if (sharedSelected !== 'tools') e.currentTarget.style.background = V.bgHover; }}
            onMouseLeave={e => { if (sharedSelected !== 'tools') e.currentTarget.style.background = 'transparent'; }}
          >
            <div style={{ fontSize: 13, fontWeight: 500 }}>🔧 Tools</div>
            <div style={{ fontSize: 11, color: V.textDim, marginTop: 2 }}>{sharedOverview?.tools?.length || 0} shared scripts</div>
          </div>
          {/* PACKAGES — grouped by domain */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 14px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: V.textDim, borderBottom: `1px solid ${V.border}`, borderTop: `1px solid ${V.border}` }}>
            <span>Packages ({packages.length})</span>
            <button onClick={() => setCreateDialogOpen(true)}
              title="Create new package"
              style={{ background: 'none', border: 'none', color: V.accent, cursor: 'pointer', fontSize: 14, padding: '0 2px', lineHeight: 1 }}>
              +
            </button>
          </div>
          {/* Create package dialog */}
          {createDialogOpen && (
            <div style={{ padding: '10px 14px', borderBottom: `1px solid ${V.border}`, background: V.bgHover }}>
              <input value={newPkgId} onChange={e => setNewPkgId(e.target.value)}
                placeholder="package-id" style={{ width: '100%', marginBottom: 6, padding: '4px 8px', fontSize: 12, background: V.bg, color: V.text, border: `1px solid ${V.border}`, borderRadius: 4, boxSizing: 'border-box' }} />
              <input value={newPkgName} onChange={e => setNewPkgName(e.target.value)}
                placeholder="Display Name" style={{ width: '100%', marginBottom: 8, padding: '4px 8px', fontSize: 12, background: V.bg, color: V.text, border: `1px solid ${V.border}`, borderRadius: 4, boxSizing: 'border-box' }}
                onKeyDown={e => { if (e.key === 'Enter') handleCreatePackage(); }} />
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={handleCreatePackage} disabled={creating || !newPkgId.trim() || !newPkgName.trim()}
                  style={{ ...S.btn, fontSize: 11, background: V.accent, color: '#fff', opacity: (creating || !newPkgId.trim() || !newPkgName.trim()) ? 0.4 : 1 }}>
                  {creating ? 'Creating...' : 'Create'}
                </button>
                <button onClick={() => { setCreateDialogOpen(false); setNewPkgId(''); setNewPkgName(''); }}
                  style={{ ...S.btn, fontSize: 11 }}>Cancel</button>
              </div>
            </div>
          )}
          {/* Reusable package item renderer for nested lists (Issue #1169) */}
          {(() => { renderPkgItem = (pkg, indent) => {
            const localPkg = localPackages.find(p => p.id === pkg.id);
            const version = localPkg?.version || pkg.manifest?.version || '';
            return (
              <div key={pkg.id} style={{ ...S.item(!claudeMdSelected && selected?.id === pkg.id), display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingLeft: indent ? 28 : undefined }}
                onClick={() => { setClaudeMdSelected(false); setSelected(pkg); setTab('skill'); setSelectedSessionId(null); setLiveBlocks([]); }}
                onMouseEnter={e => { if (claudeMdSelected || selected?.id !== pkg.id) e.currentTarget.style.background = V.bgHover; }}
                onMouseLeave={e => { if (claudeMdSelected || selected?.id !== pkg.id) e.currentTarget.style.background = 'transparent'; }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: indent ? 12 : 13, fontWeight: 500, opacity: indent ? 0.85 : 1 }}>{indent ? '\u2514 ' : ''}{pkg.icon} {pkg.name}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                    {version && <span style={{ fontSize: 10, color: V.accent, fontFamily: 'monospace' }}>v{version}</span>}
                    {(() => {
                      const remotePkg = remotePackages.find(r => r.id === pkg.id || r.id?.endsWith(`/${pkg.id}`));
                      const status = cmpVersion(version, remotePkg?.version);
                      if (!status) return <span style={{ fontSize: 9, color: V.textDim, opacity: 0.5 }}>local only</span>;
                      if (status === 'synced') return <span style={{ fontSize: 9, color: '#4ade80' }}>synced</span>;
                      return <span style={{ fontSize: 9, color: '#f87171' }}>{status === 'push' ? '\u2191 push' : '\u2193 pull'}</span>;
                    })()}
                  </div>
                </div>
              </div>
            );
          };
          // Issue #1169: build GLOBAL childMap across all packages so children
          // nest under their parent regardless of which domain they're in
          globalChildMap = {};
          childPkgIds = new Set();
          packages.filter(pkg => pkg.manifest?.parentPackage).forEach(pkg => {
            const parent = pkg.manifest.parentPackage;
            if (!globalChildMap[parent]) globalChildMap[parent] = [];
            globalChildMap[parent].push(pkg);
            childPkgIds.add(pkg.id);
          });
          return null; })()}
          {/* Domain-grouped package list */}
          {domains.length > 0 ? (<>
            {domains.map(domain => {
              // Match loaded package-skill objects to this domain's package IDs, excluding children (rendered under their parent)
              const domainPkgIds = new Set((domain.packages || []).map(p => p.id));
              const domainL0 = packages.filter(pkg => domainPkgIds.has(pkg.id) && !childPkgIds.has(pkg.id));
              if (domainL0.length === 0 && !domainPkgIds.size) return null;

              return (
                <div key={domain.id}>
                  <div style={{ padding: '6px 14px', fontSize: 10, fontWeight: 700, color: V.textDim, letterSpacing: '0.04em', borderBottom: `1px solid ${V.border}`, background: V.bg }}>
                    {domain.icon || '\u{1F4C1}'} {domain.name}
                  </div>
                  {domainL0.map(pkg => (
                    <Fragment key={pkg.id}>
                      {renderPkgItem(pkg, false)}
                      {(globalChildMap[pkg.id] || []).map(child => renderPkgItem(child, true))}
                    </Fragment>
                  ))}
                </div>
              );
            })}
            {/* "Other" section — packages not in any domain */}
            {(() => {
              const allDomainPkgIds = new Set(domains.flatMap(d => (d.packages || []).map(p => p.id)));
              const ungrouped = packages.filter(pkg => !allDomainPkgIds.has(pkg.id) && !childPkgIds.has(pkg.id));
              if (ungrouped.length === 0) return null;
              return (
                <div>
                  <div style={{ padding: '6px 14px', fontSize: 10, fontWeight: 700, color: V.textDim, letterSpacing: '0.04em', borderBottom: `1px solid ${V.border}`, background: V.bg }}>
                    {'\u{1F4E6}'} Other
                  </div>
                  {ungrouped.map(pkg => (
                    <Fragment key={pkg.id}>
                      {renderPkgItem(pkg, false)}
                      {(globalChildMap[pkg.id] || []).map(child => renderPkgItem(child, true))}
                    </Fragment>
                  ))}
                </div>
              );
            })()}
          </>) : /* Fallback: flat list when domains not loaded */ packages.map(pkg => {
            const localPkg = localPackages.find(p => p.id === pkg.id);
            const version = localPkg?.version || pkg.manifest?.version || '';
            return (
              <div key={pkg.id} style={{ ...S.item(!claudeMdSelected && selected?.id === pkg.id), display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                onClick={() => { setClaudeMdSelected(false); setSelected(pkg); setTab('skill'); setSelectedSessionId(null); setLiveBlocks([]); }}
                onMouseEnter={e => { if (claudeMdSelected || selected?.id !== pkg.id) e.currentTarget.style.background = V.bgHover; }}
                onMouseLeave={e => { if (claudeMdSelected || selected?.id !== pkg.id) e.currentTarget.style.background = 'transparent'; }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{pkg.icon} {pkg.name}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                    {version && <span style={{ fontSize: 10, color: V.accent, fontFamily: 'monospace' }}>v{version}</span>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Main — info or testbench */}
      <div style={S.main}>
        {claudeMdSelected && sharedSelected === 'templates' ? (
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div style={S.header}>
              <div style={{ fontSize: 15, fontWeight: 600 }}>🎴 Shared Templates</div>
              <div style={{ fontSize: 11, color: V.textDim, marginTop: 2 }}>{sharedOverview?.templates?.length || 0} card schemas available to all packages</div>
            </div>
            <div style={S.content}>
              {(sharedOverview?.templates || []).length === 0 ? (
                <div style={{ color: V.textDim, fontSize: 13 }}>No shared templates found.</div>
              ) : (
                <div style={{ display: 'grid', gap: 8 }}>
                  {(sharedOverview?.templates || []).map(t => (
                    <div key={t.$id} style={{ background: V.bg, border: `1px solid ${V.border}`, borderRadius: 6, padding: '10px 14px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, fontFamily: 'monospace', color: V.accent }}>{t.$id}</span>
                        <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 3, background: V.bgHover, color: V.textDim }}>{t.renderer}</span>
                        <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 3, background: V.bgHover, color: V.textDim }}>{t.category}</span>
                      </div>
                      <div style={{ fontSize: 12, color: V.textDim, marginTop: 4 }}>{t.description || 'No description'}</div>
                      <div style={{ fontSize: 11, color: V.textDim, marginTop: 4 }}>{t.slotCount} slots &middot; {t.file}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : claudeMdSelected && sharedSelected === 'tools' ? (
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div style={S.header}>
              <div style={{ fontSize: 15, fontWeight: 600 }}>🔧 Shared Tools</div>
              <div style={{ fontSize: 11, color: V.textDim, marginTop: 2 }}>{sharedOverview?.tools?.length || 0} scripts available to all packages</div>
            </div>
            <div style={S.content}>
              {(sharedOverview?.tools || []).length === 0 ? (
                <div style={{ color: V.textDim, fontSize: 13 }}>No shared tools found.</div>
              ) : (
                <div style={{ display: 'grid', gap: 8 }}>
                  {(sharedOverview?.tools || []).map(t => (
                    <div key={t.name} style={{ background: V.bg, border: `1px solid ${V.border}`, borderRadius: 6, padding: '10px 14px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, fontFamily: 'monospace', color: V.accent }}>{t.name}</span>
                        <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 3, background: V.bgHover, color: V.textDim }}>{t.language}</span>
                      </div>
                      <div style={{ fontSize: 12, color: V.textDim, marginTop: 4 }}>{t.file}</div>
                      {t.usedBy?.length > 0 && (
                        <div style={{ fontSize: 11, color: V.textDim, marginTop: 4 }}>Used by: {t.usedBy.join(', ')}</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : claudeMdSelected ? (
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div style={S.header}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 600 }}>📄 CLAUDE.md</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
                    <span style={{ fontSize: 11, color: V.textDim }}>Agent instructions</span>
                    {claudeMdVersion && (
                      <span style={{ fontSize: 11, fontFamily: 'monospace', padding: '1px 6px', borderRadius: 3, background: '#1a2e1a', color: '#4ade80' }}>v{claudeMdVersion}</span>
                    )}
                    {sharedOverview?.claudeMd && (
                      <span style={{ fontSize: 11, fontFamily: 'monospace', color: V.textDim }}>
                        {Math.round(sharedOverview.claudeMd.size / 1024)}KB &middot; {sharedOverview.claudeMd.hash?.slice(0, 8)}
                      </span>
                    )}
                  </div>
                </div>
                {/* Pull/Push buttons for CLAUDE.md */}
                {(() => {
                  const status = cmpVersion(claudeMdVersion, claudeMdS3Version);
                  const isSyncing = syncing;
                  return (
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0, alignItems: 'center' }}>
                      {status === 'pull' && (
                        <button onClick={() => handlePull()} disabled={isSyncing}
                          style={{ ...S.btn, fontSize: 11, color: '#4ade80', borderColor: '#4ade80', opacity: isSyncing ? 0.6 : 1 }}>
                          {isSyncing ? <span style={{ display: 'inline-block', animation: 'spin 1s linear infinite' }}>{'\u21BB'}</span> : '\u2B07'} Pull
                        </button>
                      )}
                      {(status === 'push' || (!claudeMdS3Version && claudeMdVersion)) && (
                        <button onClick={() => handlePush('CLAUDE.md')} disabled={isSyncing}
                          style={{ ...S.btn, fontSize: 11, color: V.accent, borderColor: V.accent, opacity: isSyncing ? 0.6 : 1 }}>
                          {isSyncing ? <span style={{ display: 'inline-block', animation: 'spin 1s linear infinite' }}>{'\u21BB'}</span> : '\u2B06'} Push
                        </button>
                      )}
                      {status === 'synced' && claudeMdS3Version && (
                        <span style={{ fontSize: 10, color: '#4ade80', padding: '4px 8px' }}>synced</span>
                      )}
                    </div>
                  );
                })()}
              </div>
              <div style={{ marginTop: 8, display: 'flex', gap: 6 }}>
                <button style={S.tab(claudeMdTab === 'content')} onClick={() => setClaudeMdTab('content')}>CLAUDE.md</button>
                <button style={S.tab(claudeMdTab === 'changelog')} onClick={() => setClaudeMdTab('changelog')}>Changelog</button>
              </div>
            </div>
            <div style={S.content}>
              {claudeMdTab === 'content' ? (
                claudeMd ? (
                  <MarkdownRenderer style={{ fontSize: 13, lineHeight: 1.6, maxWidth: 720 }}>{claudeMd}</MarkdownRenderer>
                ) : (
                  <div style={{ color: V.textDim, fontSize: 13 }}>No CLAUDE.md found. Click Sync to pull from S3.</div>
                )
              ) : (
                claudeMdChangelog ? (
                  <MarkdownRenderer style={{ fontSize: 13, lineHeight: 1.6, maxWidth: 720 }}>{claudeMdChangelog}</MarkdownRenderer>
                ) : (
                  <div style={{ color: V.textDim, fontSize: 13 }}>No changelog found. Add a CLAUDE-CHANGELOG.md alongside CLAUDE.md.</div>
                )
              )}
            </div>
          </div>
        ) : selected ? (<>
          <div style={S.header}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 600 }}>{selected.icon} {selected.name} <span style={{ fontSize: 11, fontWeight: 400, color: V.textDim, fontFamily: 'monospace', userSelect: 'all' }}>{selected.id}</span></div>
                <div style={{ fontSize: 11, color: V.textDim, marginTop: 2 }}>{selected.description}</div>
              </div>
              {/* Pull/Push buttons — contextual based on version comparison */}
              {(() => {
                const localPkg = localPackages.find(p => p.id === selected.id);
                const lv = localPkg?.version || selected.manifest?.version || '';
                const remotePkg = remotePackages.find(r => r.id === selected.id || r.id?.endsWith(`/${selected.id}`));
                const status = cmpVersion(lv, remotePkg?.version);
                const isSyncing = syncing;
                return (
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0, alignItems: 'center' }}>
                    {status === 'pull' && (
                      <button onClick={() => handleSinglePull(selected.id)} disabled={isSyncing}
                        style={{ ...S.btn, fontSize: 11, color: '#4ade80', borderColor: '#4ade80', opacity: isSyncing ? 0.6 : 1 }}>
                        {isSyncing ? <span style={{ display: 'inline-block', animation: 'spin 1s linear infinite' }}>{'\u21BB'}</span> : '\u2B07'} Pull
                      </button>
                    )}
                    {status === 'push' && (
                      <button onClick={() => handlePush(selected.id)} disabled={isSyncing}
                        style={{ ...S.btn, fontSize: 11, color: V.accent, borderColor: V.accent, opacity: isSyncing ? 0.6 : 1 }}>
                        {isSyncing ? <span style={{ display: 'inline-block', animation: 'spin 1s linear infinite' }}>{'\u21BB'}</span> : '\u2B06'} Push
                      </button>
                    )}
                    {status === 'synced' && remotePkg && (
                      <span style={{ fontSize: 10, color: '#4ade80', padding: '4px 8px' }}>synced</span>
                    )}
                  </div>
                );
              })()}
            </div>
            <div style={{ marginTop: 8, display: 'flex', gap: 6 }}>
              <button style={S.tab(tab === 'skill')} onClick={() => setTab('skill')}>SKILL.md</button>
              <button style={S.tab(tab === 'config')} onClick={() => setTab('config')}>Config</button>
              <button style={S.tab(tab === 'changelog')} onClick={() => setTab('changelog')}>Changelog</button>
              <button style={S.tab(tab === 'testbench')} onClick={() => setTab('testbench')}>Testbench</button>
            </div>
          </div>
          {tab === 'skill' && (
            <div style={S.content}>
              {selected.skillContent ? (
                <MarkdownRenderer style={{ fontSize: 13, lineHeight: 1.6, maxWidth: 720 }}>{selected.skillContent}</MarkdownRenderer>
              ) : (
                <div style={{ color: V.textDim, fontSize: 13 }}>No SKILL.md found for this package.</div>
              )}
            </div>
          )}
          {tab === 'config' && (
            <div style={S.content}>
              <PackageConfigView manifest={selected.manifest} healthData={healthData} localTemplates={localTemplates} />
            </div>
          )}
          {tab === 'changelog' && (
            <div style={S.content}>
              {(() => {
                const localPkg = localPackages.find(p => p.id === selected.id);
                const changelog = localPkg?.changelog;
                if (!changelog) return <div style={{ color: V.textDim, fontSize: 13 }}>No CHANGELOG.md found for this package.</div>;
                return (
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                      <span style={{ fontSize: 12, color: V.textDim }}>Version:</span>
                      <span style={{ fontSize: 13, color: V.accent, fontFamily: 'monospace', fontWeight: 600 }}>v{localPkg.version || '?'}</span>
                    </div>
                    <pre style={{ background: V.bg, padding: 12, borderRadius: 6, fontSize: 12, lineHeight: 1.6, whiteSpace: 'pre-wrap', color: V.text, margin: 0, border: `1px solid ${V.border}` }}>{changelog}</pre>
                  </div>
                );
              })()}
            </div>
          )}
          {tab === 'testbench' && (
            <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
              {/* Session history sidebar */}
              <div style={{
                width: 220, borderRight: `1px solid ${V.border}`, background: V.bgCard,
                display: 'flex', flexDirection: 'column', flexShrink: 0,
              }}>
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '10px 12px', borderBottom: `1px solid ${V.border}`,
                }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: V.text }}>Test Sessions</span>
                  <button onClick={loadSessions} style={{
                    background: 'none', border: `1px solid ${V.border}`, borderRadius: 4,
                    color: V.textDim, cursor: 'pointer', fontSize: 12, padding: '2px 6px',
                  }} title="Refresh">↻</button>
                </div>
                <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                  {/* New test button */}
                  <div
                    onClick={() => { setSelectedSessionId(null); setLiveBlocks([]); }}
                    style={{
                      padding: '8px 12px', cursor: 'pointer', borderBottom: `1px solid ${V.border}`,
                      background: !selectedSessionId ? V.accentDim : 'transparent',
                      transition: 'background 0.1s', fontSize: 12, color: V.accent, fontWeight: 500,
                    }}
                    onMouseEnter={e => { if (selectedSessionId) e.currentTarget.style.background = V.bgHover; }}
                    onMouseLeave={e => { if (selectedSessionId) e.currentTarget.style.background = 'transparent'; }}
                  >
                    + New Test
                  </div>
                  {sessionsLoading && <div style={{ padding: 12, fontSize: 11, color: V.textDim }}>Loading...</div>}
                  {!sessionsLoading && sessions.length === 0 && (
                    <div style={{ padding: 12, fontSize: 11, color: V.textDim }}>No test sessions yet</div>
                  )}
                  {sessions.map(session => {
                    const isActive = session.sessionId === selectedSessionId;
                    const firstPrompt = session.tasks[0]?.prompt || '(no prompt)';
                    return (
                      <div
                        key={session.sessionId}
                        onClick={() => { setSelectedSessionId(session.sessionId); setLiveBlocks([]); }}
                        style={{
                          padding: '8px 12px', cursor: 'pointer', borderBottom: `1px solid ${V.border}`,
                          background: isActive ? V.accentDim : 'transparent', transition: 'background 0.1s',
                        }}
                        onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = V.bgHover; }}
                        onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = isActive ? V.accentDim : 'transparent'; }}
                      >
                        <div style={{
                          fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap', color: isActive ? V.accent : V.text,
                        }}>
                          {firstPrompt}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: V.textDim, marginTop: 2 }}>
                          <span style={{
                            background: 'rgba(88,86,214,0.12)', color: V.accent,
                            padding: '0 4px', borderRadius: 3, fontSize: 9,
                          }}>
                            {selected.id}
                          </span>
                          <span>{session.tasks.length} task{session.tasks.length > 1 ? 's' : ''}</span>
                          <span>· {formatTime(session.latestTs)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Chat area */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
                  {/* Historical messages from selected session */}
                  {selectedSession ? sortedTasks.map((task, i) => {
                    const taskCardIds = Object.keys(task.cards || {}).filter(id => cardTaskMap[id] === task);
                    const taskHasContentCards = hasContentCards(task.cards);
                    return (
                      <div key={task.taskId || i}>
                        {/* User bubble */}
                        <div style={{ marginBottom: 12, maxWidth: '80%', marginLeft: 'auto' }}>
                          <div style={{
                            padding: '10px 14px', borderRadius: 12, fontSize: 13, lineHeight: 1.5,
                            background: V.accent, color: '#fff', borderBottomRightRadius: 4, wordBreak: 'break-word',
                          }}>
                            {task.mediaUrls?.length > 0 && (
                              <div style={{ marginBottom: 6 }}>
                                {task.mediaUrls.map((url, j) => (
                                  _isVideoUrl(url) ? (
                                    <video key={j} src={url} controls playsInline preload="metadata" style={{ maxWidth: 160, maxHeight: 120, borderRadius: 8, margin: 2 }} />
                                  ) : (
                                    <img key={j} src={url} alt="" style={{ maxWidth: 160, maxHeight: 120, borderRadius: 8, margin: 2 }}
                                      onError={e => { e.target.style.display = 'none'; }} />
                                  )
                                ))}
                              </div>
                            )}
                            {task.prompt || ''}
                          </div>
                          <div style={{ fontSize: 10, color: V.textDim, marginTop: 3, textAlign: 'right' }}>
                            {formatDateTime(task.ts)}
                          </div>
                        </div>
                        {/* Cards */}
                        {taskCardIds.length > 0 && (
                          <div style={{ marginBottom: 12, maxWidth: '80%', marginRight: 'auto' }}>
                            {taskCardIds.map(id => {
                              const card = allCards[id];
                              return (
                                <div key={id} style={{
                                  background: V.bg, border: `1px solid ${V.border}`,
                                  borderRadius: 8, overflow: 'hidden', marginTop: 6,
                                }}>
                                  <div style={{
                                    display: 'flex', alignItems: 'center', gap: 8,
                                    padding: '6px 12px', fontSize: 10, fontWeight: 600,
                                    textTransform: 'uppercase', letterSpacing: '0.04em',
                                    color: V.textDim, borderBottom: `1px solid ${V.border}`, background: V.bg,
                                  }}>
                                    {card.template || 'card'}
                                    <span style={{
                                      background: card.status === 'finalized' ? 'rgba(52,199,89,0.12)' : 'rgba(255,149,0,0.12)',
                                      color: card.status === 'finalized' ? V.green : V.yellow,
                                      padding: '1px 6px', borderRadius: 4, fontSize: 9, marginLeft: 'auto',
                                    }}>
                                      {card.status === 'finalized' ? 'done' : 'streaming'}
                                    </span>
                                  </div>
                                  <div style={{ background: '#fff', color: '#111' }}>
                                    <CardRenderer card={card} />
                                  </div>
                                </div>
                              );
                            })}
                            <div style={{ fontSize: 10, color: V.textDim, marginTop: 3 }}>
                              {formatDuration(task.durationMs)}
                            </div>
                          </div>
                        )}
                        {/* Text-only result */}
                        {task.result && !taskHasContentCards && (
                          <div style={{ marginBottom: 12, maxWidth: '80%', marginRight: 'auto' }}>
                            <div style={{
                              padding: '10px 14px', borderRadius: 12, fontSize: 13, lineHeight: 1.5,
                              background: V.bgCard, border: `1px solid ${V.border}`,
                              borderBottomLeftRadius: 4, whiteSpace: 'pre-wrap',
                            }}>
                              {task.result}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  }) : (
                    /* Empty state for new test */
                    liveBlocks.length === 0 && (
                      <div style={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center',
                        justifyContent: 'center', height: '100%', color: V.textDim, gap: 8,
                      }}>
                        <span style={{ fontSize: 28, opacity: 0.3 }}>🧪</span>
                        <span style={{ fontSize: 13 }}>Send a message to test <strong>{selected.name}</strong></span>
                      </div>
                    )
                  )}

                  {/* Live blocks */}
                  {liveBlocks.map((block, i) => {
                    if (block.type === 'user') return (
                      <div key={`live-u-${i}`} style={{ marginBottom: 12, maxWidth: '80%', marginLeft: 'auto' }}>
                        <div style={{
                          padding: '10px 14px', borderRadius: 12, fontSize: 13, lineHeight: 1.5,
                          background: V.accent, color: '#fff', borderBottomRightRadius: 4,
                        }}>
                          {block.mediaUrls?.length > 0 && (
                            <div style={{ marginBottom: 6 }}>
                              {block.mediaUrls.map((url, j) => (
                                _isVideoUrl(url) ? (
                                  <video key={j} src={url} controls playsInline preload="metadata" style={{ maxWidth: 160, maxHeight: 120, borderRadius: 8, margin: 2 }} />
                                ) : (
                                  <img key={j} src={url} alt="" style={{ maxWidth: 160, maxHeight: 120, borderRadius: 8, margin: 2 }}
                                    onError={e => { e.target.style.display = 'none'; }} />
                                )
                              ))}
                            </div>
                          )}
                          {block.text}
                        </div>
                      </div>
                    );
                    if (block.type === 'card') return (
                      <div key={`live-c-${block.card.cardId}`} style={{ marginBottom: 12, maxWidth: '80%', marginRight: 'auto' }}>
                        <LiveCardBlock card={block.card} />
                      </div>
                    );
                    if (block.type === 'error') return (
                      <div key={`live-e-${i}`} style={{ marginBottom: 12, maxWidth: '80%', marginRight: 'auto' }}>
                        <div style={{ padding: '8px 12px', borderRadius: 8, background: 'rgba(255,59,48,0.1)', color: V.red, fontSize: 12 }}>
                          Error: {block.text}
                        </div>
                      </div>
                    );
                    return null;
                  })}

                  <div ref={messagesEndRef} />
                </div>
                <ChatInputBar onSend={handleSend} disabled={sending} isExecuting={!!pkgActiveTask} onStop={handlePkgStop} />
              </div>
            </div>
          )}
        </>) : (
          <div style={{ padding: 40, color: V.textDim, textAlign: 'center' }}>
            Select a package from the sidebar
          </div>
        )}
      </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// SFTP MANAGER VIEW — unified package sync status list
// ══════════════════════════════════════════════════════════════════════

function SftpManagerView({ localPackages, remotePackages, s3Env, onPush, onPull, onSinglePull, onBatchPull, onArchive, onUnarchive, onBatchPush, syncing, claudeMd, claudeMdOnS3, s3Enabled, claudeMdVersion, claudeMdS3Version, archivedIds, localTemplates = [], remoteTemplates = [], archivedTemplateIds = new Set(), onTemplatePush, onTemplateSinglePull, onTemplateBatchPull, onTemplateBatchPush, onTemplateArchive, onTemplateUnarchive, onDeleteArchivedFromS3, onDeleteArchivedTemplateFromS3, domains = [] }) {
  const [filter, setFilter] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [showArchived, setShowArchived] = useState(false);

  // Section collapse state
  const [sectionCollapsed, setSectionCollapsed] = useState({ agent: false, templates: false, packages: false });
  const toggleSection = (section) => setSectionCollapsed(prev => ({ ...prev, [section]: !prev[section] }));

  // Group collapse state for template source groups and package domain groups
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const toggleGroup = (groupKey) => setCollapsedGroups(prev => ({ ...prev, [groupKey]: !prev[groupKey] }));

  // Template selection state
  const [selectedTemplates, setSelectedTemplates] = useState(new Set());
  const [showArchivedTemplates, setShowArchivedTemplates] = useState(false);

  // Build unified rows: merge local + remote by ID, plus CLAUDE.md
  const rows = useMemo(() => {
    const localById = new Map(localPackages.map(p => [p.id, p]));
    const remoteById = new Map(remotePackages.map(p => [p.id, p]));
    const allIds = new Set([...localById.keys(), ...remoteById.keys()]);
    const result = [];

    // CLAUDE.md row — always first
    result.push({
      id: 'CLAUDE.md',
      icon: '📄',
      name: 'CLAUDE.md',
      localVersion: claudeMdVersion || (claudeMd ? 'no version' : null),
      remoteVersion: claudeMdS3Version || (claudeMdOnS3 ? 'no version' : null),
      hasLocal: !!claudeMd,
      hasRemote: claudeMdOnS3 || !!claudeMdS3Version,
      localInfo: claudeMd ? `${claudeMd.length} chars` : null,
      remoteInfo: (claudeMdOnS3 || claudeMdS3Version) ? 'on S3' : null,
      changelog: null,
      changelogSummary: null,
      isClaudeMd: true,
    });

    // Filter out infrastructure packages — they're not experience packages
    const infraIds = new Set(['_system', '_templates', '_tools', '_shared', '_template']);
    for (const id of Array.from(allIds).sort()) {
      if (infraIds.has(id)) continue;
      const local = localById.get(id);
      const remote = remoteById.get(id);
      result.push({
        id,
        icon: local?.icon || '📦',
        name: local?.name || id,
        localVersion: local?.version || null,
        remoteVersion: remote?.version || null,
        hasLocal: !!local,
        hasRemote: !!remote,
        archived: !!remote?.archived,
        localInfo: local ? `${local.templateCount || 0} tpl` : null,
        remoteInfo: remote ? `${remote.fileCount} files` : null,
        changelog: local?.changelog || null,
        changelogSummary: remote?.changelogSummary || null,
        isClaudeMd: false,
      });
    }
    return result;
  }, [localPackages, remotePackages, claudeMd, claudeMdOnS3, claudeMdVersion, claudeMdS3Version]);

  // Split into active and archived
  const activeRows = useMemo(() => rows.filter(r => r.isClaudeMd || !archivedIds.has(r.id)), [rows, archivedIds]);
  const archivedRows = useMemo(() => rows.filter(r => !r.isClaudeMd && archivedIds.has(r.id)), [rows, archivedIds]);

  // Template rows — same structure as package rows, with source for grouping
  const templateRows = useMemo(() => {
    const localById = new Map(localTemplates.map(t => [t.id, t]));
    const remoteById = new Map(remoteTemplates.map(t => [t.id, t]));
    const allIds = new Set([...localById.keys(), ...remoteById.keys()]);
    const result = [];
    for (const id of Array.from(allIds).sort()) {
      const local = localById.get(id);
      const remote = remoteById.get(id);
      result.push({
        id,
        icon: '\uD83C\uDFB4',
        name: id,
        localVersion: local?.version || null,
        remoteVersion: remote?.version || null,
        hasLocal: !!local,
        hasRemote: !!remote,
        archived: !!remote?.archived,
        localInfo: local ? (local.hasHtml ? 'json+html' : 'json') : null,
        remoteInfo: remote ? (remote.hasHtml ? 'json+html' : 'json') : null,
        changelog: null,
        changelogSummary: null,
        isClaudeMd: false,
        isTemplate: true,
        description: local?.description || null,
        source: local?.source || 'shared',
      });
    }
    return result;
  }, [localTemplates, remoteTemplates]);

  const activeTemplateRows = useMemo(() => templateRows.filter(r => !archivedTemplateIds.has(r.id)), [templateRows, archivedTemplateIds]);
  const archivedTemplateRows = useMemo(() => templateRows.filter(r => archivedTemplateIds.has(r.id)), [templateRows, archivedTemplateIds]);

  const filtered = filter
    ? activeRows.filter(r => r.name.toLowerCase().includes(filter.toLowerCase()) || r.id.toLowerCase().includes(filter.toLowerCase()))
    : activeRows;

  const filteredTemplateRows = filter
    ? activeTemplateRows.filter(r => r.name.toLowerCase().includes(filter.toLowerCase()) || r.id.toLowerCase().includes(filter.toLowerCase()))
    : activeTemplateRows;

  // Group templates by source for S3 tab display
  const groupedTemplateRows = useMemo(() => {
    const groups = [];
    const bySource = new Map();
    for (const row of filteredTemplateRows) {
      const src = row.source || 'shared';
      if (!bySource.has(src)) bySource.set(src, []);
      bySource.get(src).push(row);
    }
    // "Shared" group first, then domain groups sorted alphabetically
    if (bySource.has('shared')) {
      groups.push({ key: 'tpl:shared', label: 'Shared', rows: bySource.get('shared') });
      bySource.delete('shared');
    }
    for (const [src, rows] of Array.from(bySource.entries()).sort((a, b) => a[0].localeCompare(b[0]))) {
      // source is like "domain:design:product-card" → display as package name
      const parts = src.split(':');
      const label = parts.length >= 3 ? `${parts[1]}/${parts[2]}` : src;
      groups.push({ key: `tpl:${src}`, label, rows });
    }
    return groups;
  }, [filteredTemplateRows]);

  // Group packages by domain for S3 tab display
  const groupedPackageRows = useMemo(() => {
    const pkgRows = filtered.filter(r => !r.isClaudeMd);
    if (!domains || domains.length === 0) return [{ key: 'pkg:all', label: null, rows: pkgRows }];
    const groups = [];
    const assigned = new Set();
    for (const domain of domains) {
      const domainPkgIds = new Set((domain.packages || []).map(p => p.id));
      const domainRows = pkgRows.filter(r => domainPkgIds.has(r.id));
      if (domainRows.length === 0) continue;
      domainRows.forEach(r => assigned.add(r.id));
      groups.push({ key: `pkg:${domain.id}`, label: `${domain.icon || '\u{1F4C1}'} ${domain.name}`, rows: domainRows });
    }
    // Ungrouped packages
    const ungrouped = pkgRows.filter(r => !assigned.has(r.id));
    if (ungrouped.length > 0) {
      groups.push({ key: 'pkg:other', label: 'Other', rows: ungrouped });
    }
    return groups;
  }, [filtered, domains]);

  // Selectable rows (non-CLAUDE.md active packages)
  const selectableRows = filtered.filter(r => !r.isClaudeMd);
  const allSelected = selectableRows.length > 0 && selectableRows.every(r => selected.has(r.id));

  const allTemplatesSelected = filteredTemplateRows.length > 0 && filteredTemplateRows.every(r => selectedTemplates.has(r.id));

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleTemplateSelect = (id) => {
    setSelectedTemplates(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(selectableRows.map(r => r.id)));
    }
  };

  const toggleSelectAllTemplates = () => {
    if (allTemplatesSelected) {
      setSelectedTemplates(new Set());
    } else {
      setSelectedTemplates(new Set(filteredTemplateRows.map(r => r.id)));
    }
  };

  // Clear selection when archived packages change
  useEffect(() => {
    setSelected(new Set());
    setSelectedTemplates(new Set());
  }, [archivedIds]);

  // Synced = subtle/muted; out-of-sync states = prominent
  const SYNCED = { text: 'synced', color: 'rgba(74,222,128,0.55)', bg: 'transparent' };

  const getStatus = (row) => {
    if (row.isClaudeMd) {
      if (!row.hasLocal && !row.hasRemote) return { text: 'missing', color: '#f87171', bg: '#991b1b' };
      if (row.hasLocal && !row.hasRemote) return { text: 'local only', color: '#6ee7b7', bg: '#065f46' };
      if (!row.hasLocal && row.hasRemote) return { text: 'remote only', color: '#93c5fd', bg: '#1e3a5f' };
      const lv = row.localVersion && row.localVersion !== 'no version' ? row.localVersion : null;
      const rv = row.remoteVersion && row.remoteVersion !== 'no version' ? row.remoteVersion : null;
      if (!lv && !rv) return SYNCED;
      if (!rv) return { text: 'out of sync', color: '#fbbf24', bg: '#92400e' };
      if (!lv) return { text: 'out of sync', color: '#fbbf24', bg: '#92400e' };
      if (lv === rv) return SYNCED;
      const lvParts = lv.split('.').map(Number);
      const rvParts = rv.split('.').map(Number);
      if (lvParts.some(isNaN) || rvParts.some(isNaN)) return { text: 'out of sync', color: '#fbbf24', bg: '#92400e' };
      for (let i = 0; i < 4; i++) {
        if ((lvParts[i] || 0) > (rvParts[i] || 0)) return { text: 'local \u2191', color: '#fbbf24', bg: '#92400e' };
        if ((lvParts[i] || 0) < (rvParts[i] || 0)) return { text: 'remote \u2191', color: '#60a5fa', bg: '#1e3a5f' };
      }
      return SYNCED;
    }
    if (row.archived) return { text: 'archived', color: '#9ca3af', bg: '#374151' };
    if (row.hasLocal && !row.hasRemote) return { text: 'local only', color: '#6ee7b7', bg: '#065f46' };
    if (!row.hasLocal && row.hasRemote) return { text: 'remote only', color: '#93c5fd', bg: '#1e3a5f' };
    if (!row.localVersion && !row.remoteVersion) return SYNCED;
    if (!row.remoteVersion) return { text: 'local \u2191', color: '#fbbf24', bg: '#92400e' };
    if (!row.localVersion) return { text: 'remote \u2191', color: '#60a5fa', bg: '#1e3a5f' };
    if (row.localVersion === row.remoteVersion) return SYNCED;
    const lv = row.localVersion.split('.').map(Number);
    const rv = row.remoteVersion.split('.').map(Number);
    for (let i = 0; i < Math.max(lv.length, rv.length); i++) {
      if ((lv[i] || 0) > (rv[i] || 0)) return { text: 'local \u2191', color: '#fbbf24', bg: '#92400e' };
      if ((lv[i] || 0) < (rv[i] || 0)) return { text: 'remote \u2191', color: '#60a5fa', bg: '#1e3a5f' };
    }
    return SYNCED;
  };

  const getAction = (row) => {
    const status = getStatus(row);
    // CLAUDE.md: Push uses POST /api/dashboard/claude-md/push (handled in handlePush)
    if (row.isClaudeMd) {
      if (status.text === 'local only' || status.text === 'local ↑') return { label: 'Push →', action: 'push' };
      if (status.text === 'remote only' || status.text === 'remote ↑' || status.text === 'out of sync') return { label: '← Pull', action: 'pull' };
      if (status.text === 'synced') return { label: '⇄', action: null };
      return null;
    }
    if (status.text === 'local only' || status.text === 'local ↑') return { label: 'Push →', action: 'push' };
    if (status.text === 'remote only' || status.text === 'remote ↑') return { label: '← Pull', action: 'pull' };
    if (status.text === 'synced') return { label: '⇄', action: null };
    return { label: 'Push →', action: 'push' };
  };

  const cellStyle = { fontSize: 12, padding: '0 4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

  const checkboxStyle = {
    width: 15, height: 15, cursor: 'pointer', accentColor: V.accent,
  };

  const renderRow = (row, opts = {}) => {
    const { showCheckbox = true, isArchivedRow = false, isTemplateRow = false } = opts;
    const status = getStatus(row);
    const action = getAction(row);
    const isExpanded = expanded === (isTemplateRow ? `tpl:${row.id}` : row.id);
    const isSelected = isTemplateRow ? selectedTemplates.has(row.id) : selected.has(row.id);
    const expandKey = isTemplateRow ? `tpl:${row.id}` : row.id;
    const onToggleCheck = isTemplateRow ? toggleTemplateSelect : toggleSelect;
    const pushFn = isTemplateRow ? onTemplatePush : onPush;
    const pullFn = isTemplateRow ? onTemplateSinglePull : onSinglePull;
    const unarchiveFn = isTemplateRow ? onTemplateUnarchive : onUnarchive;
    const deleteFromS3Fn = isTemplateRow ? onDeleteArchivedTemplateFromS3 : onDeleteArchivedFromS3;

    return (
      <div key={row.id}>
        <div
          onClick={() => setExpanded(isExpanded ? null : expandKey)}
          style={{
            display: 'flex', alignItems: 'center', padding: '10px 14px',
            borderBottom: `1px solid ${V.border}`, cursor: 'pointer',
            background: isSelected ? 'rgba(88,86,214,0.08)' : isExpanded ? V.bgHover : 'transparent',
            transition: 'background 0.1s',
            opacity: isArchivedRow ? 0.7 : 1,
          }}
          onMouseEnter={e => { if (!isExpanded && !isSelected) e.currentTarget.style.background = V.bgHover; }}
          onMouseLeave={e => { if (!isExpanded && !isSelected) e.currentTarget.style.background = isSelected ? 'rgba(88,86,214,0.08)' : 'transparent'; }}
        >
          {/* Checkbox */}
          {showCheckbox && !row.isClaudeMd ? (
            <div style={{ width: 32, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
              <input type="checkbox" checked={isSelected}
                onChange={e => { e.stopPropagation(); onToggleCheck(row.id); }}
                onClick={e => e.stopPropagation()}
                style={checkboxStyle} />
            </div>
          ) : (
            <span style={{ width: 32, fontSize: 18, textAlign: 'center', flexShrink: 0 }}>{row.icon}</span>
          )}
          <div style={{ flex: 2, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {!row.isClaudeMd && showCheckbox && <span style={{ marginRight: 6 }}>{row.icon}</span>}
              {row.name}
            </div>
            {row.id !== row.name && !row.isClaudeMd && (
              <div style={{ fontSize: 10, color: V.textDim, fontFamily: 'monospace', marginTop: 1 }}>{row.id}</div>
            )}
          </div>
          <div style={{ width: 110, textAlign: 'center', ...cellStyle }}>
            {row.localVersion === 'no version' ? (
              <span style={{ color: '#f87171', fontSize: 10, fontWeight: 600 }}>no version</span>
            ) : row.localVersion ? (
              <span style={{ color: row.hasRemote && row.remoteVersion && row.remoteVersion !== 'no version' && row.localVersion !== row.remoteVersion ? '#fbbf24' : V.accent, fontFamily: 'monospace', fontSize: 11 }}>{row.localVersion}</span>
            ) : row.hasLocal ? (
              <span style={{ color: V.textDim, fontSize: 11 }}>✓</span>
            ) : (
              <span style={{ color: V.textDim }}>—</span>
            )}
          </div>
          <div style={{ width: 110, textAlign: 'center', ...cellStyle }}>
            {row.remoteVersion === 'no version' ? (
              <span style={{ color: '#f87171', fontSize: 10, fontWeight: 600 }}>no version</span>
            ) : row.remoteVersion ? (
              <span style={{ color: row.hasLocal && row.localVersion && row.localVersion !== 'no version' && row.localVersion !== row.remoteVersion ? '#fbbf24' : V.accent, fontFamily: 'monospace', fontSize: 11 }}>{row.remoteVersion}</span>
            ) : row.hasRemote ? (
              <span style={{ color: V.textDim, fontSize: 11 }}>✓</span>
            ) : (
              <span style={{ color: V.textDim }}>—</span>
            )}
          </div>
          <div style={{ width: 90, textAlign: 'center' }}>
            <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 3, fontWeight: 600, background: status.bg, color: status.color }}>{status.text}</span>
          </div>
          <div style={{ width: 120, textAlign: 'center' }}>
            {!isArchivedRow && action && action.action ? (
              <button onClick={e => { e.stopPropagation(); action.action === 'push' ? pushFn(row.id) : pullFn(row.id); }}
                disabled={syncing}
                style={{ background: 'none', border: `1px solid ${V.border}`, borderRadius: 4, padding: '3px 10px', fontSize: 11, cursor: 'pointer', color: action.action === 'push' ? V.accent : '#4ade80', opacity: syncing ? 0.4 : 1 }}>
                {action.label}
              </button>
            ) : isArchivedRow ? (
              <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                <button onClick={e => { e.stopPropagation(); unarchiveFn([row.id]); }}
                  style={{ background: 'none', border: `1px solid ${V.border}`, borderRadius: 4, padding: '3px 6px', fontSize: 11, cursor: 'pointer', color: '#4ade80' }}
                  title="Unarchive (restore visibility)">
                  ↩
                </button>
                {action && action.action ? (
                  <button onClick={e => { e.stopPropagation(); action.action === 'push' ? pushFn(row.id) : pullFn(row.id); }}
                    disabled={syncing}
                    style={{ background: 'none', border: `1px solid ${V.border}`, borderRadius: 4, padding: '3px 6px', fontSize: 11, cursor: 'pointer', color: action.action === 'push' ? V.accent : '#4ade80', opacity: syncing ? 0.4 : 1 }}
                    title={action.label}>
                    {action.action === 'push' ? '⬆' : '⬇'}
                  </button>
                ) : (
                  <span style={{ padding: '3px 6px', fontSize: 11, color: V.textDim }}>—</span>
                )}
                {deleteFromS3Fn && (
                  <button onClick={e => {
                    e.stopPropagation();
                    const label = isTemplateRow ? 'template' : 'package';
                    if (window.confirm(`Permanently delete ${label} "${row.id}" from ${s3Env || 'S3'}?\n\nThis hard-deletes the files on S3 and cannot be undone.`)) {
                      deleteFromS3Fn(row.id);
                    }
                  }}
                    style={{ background: 'none', border: `1px solid ${V.red}`, borderRadius: 4, padding: '3px 6px', fontSize: 11, cursor: 'pointer', color: V.red, fontWeight: 700 }}
                    title={`Permanently delete from ${s3Env || 'S3'} — cannot be undone`}>
                    ×
                  </button>
                )}
              </div>
            ) : action ? (
              <span style={{ fontSize: 14, color: V.textDim }}>{action.label}</span>
            ) : null}
          </div>
        </div>

        {/* Expanded detail */}
        {isExpanded && (
          <div style={{ padding: '10px 14px 10px 46px', borderBottom: `1px solid ${V.border}`, background: V.bg, fontSize: 12 }}>
            <div style={{ display: 'flex', gap: 24 }}>
              <div>
                <div style={{ fontWeight: 600, color: V.textDim, fontSize: 10, textTransform: 'uppercase', marginBottom: 4 }}>Local</div>
                <div>{row.localVersion && row.localVersion !== 'no version' ? <span style={{ color: V.accent }}>v{row.localVersion}</span> : row.localVersion === 'no version' ? <span style={{ color: '#f87171' }}>no version</span> : <span style={{ color: V.textDim }}>not loaded</span>}</div>
                {row.localInfo && <div style={{ color: V.textDim, marginTop: 2 }}>{row.localInfo}</div>}
              </div>
              <div>
                <div style={{ fontWeight: 600, color: V.textDim, fontSize: 10, textTransform: 'uppercase', marginBottom: 4 }}>S3 ({s3Env})</div>
                <div>{row.remoteVersion && row.remoteVersion !== 'no version' ? <span style={{ color: V.accent }}>v{row.remoteVersion}</span> : row.remoteVersion === 'no version' ? <span style={{ color: '#f87171' }}>no version</span> : <span style={{ color: V.textDim }}>not on S3</span>}</div>
                {row.remoteInfo && <div style={{ color: V.textDim, marginTop: 2 }}>{row.remoteInfo}</div>}
              </div>
            </div>
            {(row.changelog || row.changelogSummary) && (
              <div style={{ marginTop: 8 }}>
                <div style={{ fontWeight: 600, color: V.textDim, fontSize: 10, textTransform: 'uppercase', marginBottom: 4 }}>Changelog</div>
                {row.changelog ? (
                  <pre style={{ background: V.bgCard, padding: 8, borderRadius: 4, fontSize: 11, lineHeight: 1.5, maxHeight: 150, overflowY: 'auto', whiteSpace: 'pre-wrap', color: V.text, margin: 0 }}>{row.changelog}</pre>
                ) : (
                  <div style={{ color: V.textDim }}>{row.changelogSummary}</div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Section header helper
  const renderSectionHeader = (key, label, count, collapsed) => (
    <div onClick={() => toggleSection(key)}
      style={{ display: 'flex', alignItems: 'center', padding: '10px 14px', borderBottom: `1px solid ${V.border}`, background: V.bgCard, cursor: 'pointer', gap: 8 }}>
      <span style={{ fontSize: 12, color: V.textDim, transition: 'transform 0.2s', transform: collapsed ? 'rotate(0deg)' : 'rotate(90deg)', display: 'inline-block' }}>▶</span>
      <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: V.textDim }}>
        {label} ({count})
      </span>
    </div>
  );

  // Collapsible group sub-header within a section
  const renderGroupHeader = (groupKey, label, count) => (
    <div onClick={() => toggleGroup(groupKey)}
      style={{ display: 'flex', alignItems: 'center', padding: '6px 14px', borderBottom: `1px solid ${V.border}`, background: V.bg, cursor: 'pointer', gap: 6 }}>
      <span style={{ fontSize: 10, color: V.textDim, transition: 'transform 0.2s', transform: collapsedGroups[groupKey] ? 'rotate(0deg)' : 'rotate(90deg)', display: 'inline-block' }}>▶</span>
      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', color: V.textDim }}>
        {label}
      </span>
      <span style={{ fontSize: 10, color: V.textDim, opacity: 0.6 }}>({count})</span>
    </div>
  );

  // Table column header helper
  const renderTableHeader = (itemLabel, selectAllChecked, onToggleSelectAll) => (
    <div style={{ display: 'flex', alignItems: 'center', padding: '6px 14px', borderBottom: `1px solid ${V.border}`, background: V.bg, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: V.textDim, flexShrink: 0 }}>
      <div style={{ width: 32, display: 'flex', justifyContent: 'center' }}>
        <input type="checkbox" checked={selectAllChecked} onChange={onToggleSelectAll}
          style={checkboxStyle} title="Select all" />
      </div>
      <span style={{ flex: 2, minWidth: 0 }}>{itemLabel}</span>
      <span style={{ width: 110, textAlign: 'center' }}>Local</span>
      <span style={{ width: 110, textAlign: 'center' }}>S3</span>
      <span style={{ width: 90, textAlign: 'center' }}>Status</span>
      <span style={{ width: 120, textAlign: 'center' }}>Action</span>
    </div>
  );

  // Determine which toolbar to show
  const totalSelected = selected.size + selectedTemplates.size;

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {/* Filter + Toolbar — sticky at top of the scrolling container */}
      <div style={{ position: 'sticky', top: 0, zIndex: 10, padding: '8px 14px', borderBottom: `1px solid ${V.border}`, background: V.bgCard, display: 'flex', alignItems: 'center', gap: 10 }}>
        <input value={filter} onChange={e => setFilter(e.target.value)} placeholder="Filter..."
          style={{ width: 260, background: V.bg, color: V.text, border: `1px solid ${V.border}`, borderRadius: 4, padding: '5px 10px', fontSize: 12, outline: 'none' }} />

        {/* Package toolbar */}
        {selected.size > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
            <span style={{ fontSize: 11, color: V.textDim }}>{selected.size} pkg selected</span>
            <button onClick={() => onBatchPull(Array.from(selected))} disabled={syncing}
              style={{ background: 'none', border: `1px solid ${V.border}`, borderRadius: 4, padding: '4px 12px', fontSize: 11, cursor: 'pointer', color: '#4ade80', opacity: syncing ? 0.4 : 1, fontWeight: 500 }}>
              Pull ←
            </button>
            <button onClick={() => onBatchPush(Array.from(selected))} disabled={syncing}
              style={{ background: V.accent, color: '#fff', border: 'none', borderRadius: 4, padding: '4px 12px', fontSize: 11, cursor: 'pointer', opacity: syncing ? 0.4 : 1, fontWeight: 500 }}>
              Push →
            </button>
            <button onClick={() => onArchive(Array.from(selected))}
              style={{ background: 'none', border: `1px solid ${V.border}`, borderRadius: 4, padding: '4px 12px', fontSize: 11, cursor: 'pointer', color: '#f59e0b', fontWeight: 500 }}>
              Archive
            </button>
            <button onClick={() => setSelected(new Set())}
              style={{ background: 'none', border: 'none', padding: '4px 8px', fontSize: 14, cursor: 'pointer', color: V.textDim }}>
              ×
            </button>
          </div>
        )}

        {/* Template toolbar */}
        {selected.size === 0 && selectedTemplates.size > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
            <span style={{ fontSize: 11, color: V.textDim }}>{selectedTemplates.size} tpl selected</span>
            <button onClick={() => onTemplateBatchPull(Array.from(selectedTemplates))} disabled={syncing}
              style={{ background: 'none', border: `1px solid ${V.border}`, borderRadius: 4, padding: '4px 12px', fontSize: 11, cursor: 'pointer', color: '#4ade80', opacity: syncing ? 0.4 : 1, fontWeight: 500 }}>
              Pull ←
            </button>
            <button onClick={() => onTemplateBatchPush(Array.from(selectedTemplates))} disabled={syncing}
              style={{ background: V.accent, color: '#fff', border: 'none', borderRadius: 4, padding: '4px 12px', fontSize: 11, cursor: 'pointer', opacity: syncing ? 0.4 : 1, fontWeight: 500 }}>
              Push →
            </button>
            <button onClick={() => onTemplateArchive(Array.from(selectedTemplates))}
              style={{ background: 'none', border: `1px solid ${V.border}`, borderRadius: 4, padding: '4px 12px', fontSize: 11, cursor: 'pointer', color: '#f59e0b', fontWeight: 500 }}>
              Archive
            </button>
            <button onClick={() => setSelectedTemplates(new Set())}
              style={{ background: 'none', border: 'none', padding: '4px 8px', fontSize: 14, cursor: 'pointer', color: V.textDim }}>
              ×
            </button>
          </div>
        )}
      </div>

      {/* S3 disabled notice */}
      {s3Enabled === false && (
        <div style={{ padding: '6px 14px', background: '#92400e22', borderBottom: `1px solid ${V.border}`, fontSize: 11, color: '#fbbf24' }}>
          ⚠ S3 sync disabled (S3_PACKAGES_SYNC=false). S3 column will be empty. Enable in .env to use sync features.
        </div>
      )}

      {/* Scrollable content with 3 collapsible sections */}
      <div style={{ flex: 1, overflowY: 'auto' }}>

        {/* ── Section 1: Agent (CLAUDE.md) ── */}
        {renderSectionHeader('agent', 'Agent', 1, sectionCollapsed.agent)}
        {!sectionCollapsed.agent && (
          <>
            {filtered.filter(r => r.isClaudeMd).map(row => renderRow(row, { showCheckbox: false }))}
          </>
        )}

        {/* ── Section 2: Templates (grouped by source) ── */}
        {renderSectionHeader('templates', 'Templates', filteredTemplateRows.length, sectionCollapsed.templates)}
        {!sectionCollapsed.templates && (
          <>
            {renderTableHeader('Template', allTemplatesSelected, toggleSelectAllTemplates)}
            {filteredTemplateRows.length === 0 ? (
              <div style={{ padding: 20, color: V.textDim, textAlign: 'center', fontSize: 13 }}>No templates found</div>
            ) : groupedTemplateRows.map(group => (
              <div key={group.key}>
                {renderGroupHeader(group.key, group.label, group.rows.length)}
                {!collapsedGroups[group.key] && group.rows.map(row => renderRow(row, { isTemplateRow: true }))}
              </div>
            ))}
            {archivedTemplateRows.length > 0 && (
              <>
                <div onClick={() => setShowArchivedTemplates(!showArchivedTemplates)}
                  style={{ display: 'flex', alignItems: 'center', padding: '8px 14px', borderBottom: `1px solid ${V.border}`, borderTop: `1px solid ${V.border}`, background: V.bgCard, cursor: 'pointer', gap: 8 }}>
                  <span style={{ fontSize: 12, color: V.textDim, transition: 'transform 0.2s', transform: showArchivedTemplates ? 'rotate(90deg)' : 'rotate(0deg)', display: 'inline-block' }}>▶</span>
                  <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: V.textDim }}>
                    Archived ({archivedTemplateRows.length})
                  </span>
                </div>
                {showArchivedTemplates && archivedTemplateRows.map(row => renderRow(row, { showCheckbox: false, isArchivedRow: true, isTemplateRow: true }))}
              </>
            )}
          </>
        )}

        {/* ── Section 3: Packages (grouped by domain) ── */}
        {renderSectionHeader('packages', 'Packages', filtered.filter(r => !r.isClaudeMd).length, sectionCollapsed.packages)}
        {!sectionCollapsed.packages && (
          <>
            {renderTableHeader('Package', allSelected, toggleSelectAll)}
            {filtered.filter(r => !r.isClaudeMd).length === 0 ? (
              <div style={{ padding: 20, color: V.textDim, textAlign: 'center', fontSize: 13 }}>No packages found</div>
            ) : groupedPackageRows.map(group => (
              <div key={group.key}>
                {group.label && renderGroupHeader(group.key, group.label, group.rows.length)}
                {!collapsedGroups[group.key] && group.rows.map(row => renderRow(row))}
              </div>
            ))}
            {archivedRows.length > 0 && (
              <>
                <div onClick={() => setShowArchived(!showArchived)}
                  style={{ display: 'flex', alignItems: 'center', padding: '8px 14px', borderBottom: `1px solid ${V.border}`, borderTop: `1px solid ${V.border}`, background: V.bgCard, cursor: 'pointer', gap: 8 }}>
                  <span style={{ fontSize: 12, color: V.textDim, transition: 'transform 0.2s', transform: showArchived ? 'rotate(90deg)' : 'rotate(0deg)', display: 'inline-block' }}>▶</span>
                  <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: V.textDim }}>
                    Archived ({archivedRows.length})
                  </span>
                </div>
                {showArchived && archivedRows.map(row => renderRow(row, { showCheckbox: false, isArchivedRow: true }))}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// SKILLS PAGE — list all shared skills with SKILL.md content
// ══════════════════════════════════════════════════════════════════════
// S3 MANAGER PAGE — standalone sync management tab
// ══════════════════════════════════════════════════════════════════════

function S3ManagerPage() {
  const [s3Env, setS3Env] = useState(null); // null until env info loaded
  const [envLoaded, setEnvLoaded] = useState(false);
  const [syncStatus, setSyncStatus] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [localPackages, setLocalPackages] = useState([]);
  const [remotePackages, setRemotePackages] = useState([]);
  const [archivedIds, setArchivedIds] = useState(new Set());
  const [localTemplates, setLocalTemplates] = useState([]);
  const [remoteTemplates, setRemoteTemplates] = useState([]);
  const [archivedTemplateIds, setArchivedTemplateIds] = useState(new Set());
  const [claudeMd, setClaudeMd] = useState('');
  const [claudeMdOnS3, setClaudeMdOnS3] = useState(false);
  const [claudeMdVersion, setClaudeMdVersion] = useState(null);
  const [claudeMdS3Version, setClaudeMdS3Version] = useState(null);
  const [s3Enabled, setS3Enabled] = useState(null);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalAction, setModalAction] = useState('');
  const [diffItems, setDiffItems] = useState([]);
  const [diffLoading, setDiffLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [promoting, setPromoting] = useState(false);
  const [domains, setDomains] = useState([]);
  // Pull All Local (force-mirror S3 → local) — destructive confirm modal state
  const [pullModalOpen, setPullModalOpen] = useState(false);
  const [pullConfirmText, setPullConfirmText] = useState('');
  const [pulling, setPulling] = useState(false);
  // Push All Local (force-mirror local → S3) — destructive confirm modal state
  const [pushModalOpen, setPushModalOpen] = useState(false);
  const [pushConfirmText, setPushConfirmText] = useState('');
  const [pushing, setPushing] = useState(false);
  // Bidirectional sync dry-run preview modal state
  const [syncPreviewOpen, setSyncPreviewOpen] = useState(false);
  const [syncPreviewData, setSyncPreviewData] = useState(null);
  // Cross-env mirror (S3 env A → S3 env B) — destructive confirm modal state
  const [crossModalOpen, setCrossModalOpen] = useState(false);
  const [crossFrom, setCrossFrom] = useState(null);
  const [crossTo, setCrossTo] = useState(null);
  const [crossDirection, setCrossDirection] = useState('push'); // 'pull' | 'push'
  const [crossConfirmText, setCrossConfirmText] = useState('');
  const [crossMirroring, setCrossMirroring] = useState(false);

  // Fetch environment info first, then set default s3Env from the deployment
  // environment (dev/staging/production), not the backend's s3Env field — the
  // latter is the S3 bucket prefix the pod's startup-sync uses (a dev pod often
  // has S3_PACKAGES_ENV=staging to seed its packages from staging) and does not
  // match what the user expects the dropdown to default to.
  useEffect(() => {
    const envToS3 = (env) => {
      if (env === 'production') return 'prod';
      if (env === 'staging') return 'staging';
      return 'dev'; // dev / development / unset
    };
    api.authFetch('/api/nanoclaw/dashboard/environment')
      .then(r => r.json())
      .then(d => { setS3Env(envToS3(d.environment) || d.s3Env || 'dev'); setEnvLoaded(true); })
      .catch(() => { setS3Env('dev'); setEnvLoaded(true); });
  }, []);

  const loadLocalPackages = useCallback(() => {
    api.authFetch(`/api/nanoclaw/dashboard/packages?_t=${Date.now()}`).then(r => r.json())
      .then(d => setLocalPackages(d.packages || [])).catch(() => {});
  }, []);

  const loadRemotePackages = useCallback(() => {
    api.authFetch(`/api/nanoclaw/dashboard/packages/remote?env=${s3Env}`).then(r => r.json())
      .then(d => {
        const pkgs = d.packages || [];
        setRemotePackages(pkgs);
        // Derive CLAUDE.md S3 status from _system package in remote list
        const systemPkg = pkgs.find(p => p.id === '_system');
        setClaudeMdOnS3(!!systemPkg);
        setClaudeMdS3Version(systemPkg?.version || null);
        setS3Enabled(d.s3Enabled ?? null);
      }).catch(() => {});
  }, [s3Env]);

  const loadLocalTemplates = useCallback(() => {
    api.authFetch(`/api/nanoclaw/dashboard/templates/local?_t=${Date.now()}`).then(r => r.json())
      .then(d => setLocalTemplates(d.templates || [])).catch(() => {});
  }, []);

  const loadRemoteTemplates = useCallback(() => {
    api.authFetch(`/api/nanoclaw/dashboard/templates/remote?env=${s3Env}`).then(r => r.json())
      .then(d => setRemoteTemplates(d.templates || [])).catch(() => {});
  }, [s3Env]);

  const loadClaudeMd = useCallback(() => {
    api.authFetch('/api/nanoclaw/dashboard/claude-md').then(r => r.json())
      .then(d => { setClaudeMd(d.content || ''); setClaudeMdVersion(d.version || null); }).catch(() => {});
  }, []);

  const loadArchived = useCallback(async () => {
    if (!s3Env) return;
    try {
      const res = await api.authFetch(`/api/nanoclaw/dashboard/packages/archived?env=${s3Env}`);
      const d = await res.json();
      setArchivedIds(new Set(d.archived || []));
    } catch { /* ignore */ }
  }, [s3Env]);

  const loadArchivedTemplates = useCallback(async () => {
    if (!s3Env) return;
    try {
      const res = await api.authFetch(`/api/nanoclaw/dashboard/templates/archived?env=${s3Env}`);
      const d = await res.json();
      setArchivedTemplateIds(new Set(d.archived || []));
    } catch { /* ignore */ }
  }, [s3Env]);

  const loadDomains = useCallback(() => {
    api.authFetch(`/api/nanoclaw/dashboard/domains?_t=${Date.now()}`)
      .then(r => r.json())
      .then(d => setDomains(d.domains || []))
      .catch(() => {});
  }, []);

  // Canonical "refresh everything" — every mutation handler must call this so
  // domain grouping, archived state, and package lists stay consistent (issue #959).
  const refreshAllPackages = useCallback(() => {
    loadLocalPackages();
    loadRemotePackages();
    loadLocalTemplates();
    loadRemoteTemplates();
    loadClaudeMd();
    loadArchived();
    loadArchivedTemplates();
    loadDomains();
  }, [loadLocalPackages, loadRemotePackages, loadLocalTemplates, loadRemoteTemplates, loadClaudeMd, loadArchived, loadArchivedTemplates, loadDomains]);

  // Only load data once s3Env is set (after environment fetch)
  useEffect(() => {
    if (!s3Env) return;
    refreshAllPackages();
  }, [s3Env, refreshAllPackages]);

  // ── Bidirectional sync: classify → preview → execute ──
  const classifySyncItems = useCallback(() => {
    const cmpVer = (lv, rv) => {
      if (!lv && !rv) return 'synced';
      if (!rv) return 'local';
      if (!lv) return 'remote';
      if (lv === rv) return 'synced';
      const lp = lv.split('.').map(Number);
      const rp = rv.split('.').map(Number);
      if (lp.some(isNaN) || rp.some(isNaN)) return null;
      for (let i = 0; i < Math.max(lp.length, rp.length); i++) {
        if ((lp[i] || 0) > (rp[i] || 0)) return 'local';
        if ((lp[i] || 0) < (rp[i] || 0)) return 'remote';
      }
      return 'synced';
    };

    const pkgPull = [], pkgPush = [], pkgDelete = [], tplPull = [], tplPush = [], tplDelete = [];
    let claudeAction = null, skipped = 0;

    const localById = new Map(localPackages.map(p => [p.id, p]));
    const remoteById = new Map(remotePackages.map(p => [p.id, p]));
    const allPkgIds = new Set([...localById.keys(), ...remoteById.keys()]);
    for (const id of allPkgIds) {
      // Skip infrastructure packages (_system, _shared, _templates, _tools) —
      // _system syncs via claudeAction, others are managed separately
      if (id.startsWith('_')) { skipped++; continue; }
      const local = localById.get(id);
      const remote = remoteById.get(id);
      if (!local && remote && !remote.archived) { pkgPull.push(id); continue; }
      if (!local && remote?.archived) { skipped++; continue; }
      if (local && (!remote || remote.archived)) {
        if (!id.startsWith('_')) pkgDelete.push(id); else skipped++;
        continue;
      }
      const dir = cmpVer(local.version, remote.version);
      if (dir === 'remote') pkgPull.push(id);
      else if (dir === 'local') pkgPush.push(id);
      else skipped++;
    }

    const localTplById = new Map(localTemplates.map(t => [t.id, t]));
    const remoteTplById = new Map(remoteTemplates.map(t => [t.id, t]));
    const allTplIds = new Set([...localTplById.keys(), ...remoteTplById.keys()]);
    for (const id of allTplIds) {
      const local = localTplById.get(id);
      const remote = remoteTplById.get(id);
      if (!local && remote && !remote.archived) { tplPull.push(id); continue; }
      if (!local && remote?.archived) { skipped++; continue; }
      if (local && (!remote || remote.archived)) { tplDelete.push(id); continue; }
      const dir = cmpVer(local.version, remote.version);
      if (dir === 'remote') tplPull.push(id);
      else if (dir === 'local') tplPush.push(id);
      else skipped++;
    }

    const clHasLocal = !!claudeMd;
    const clHasRemote = claudeMdOnS3 || !!claudeMdS3Version;
    if (!clHasLocal && clHasRemote) {
      claudeAction = 'pull';
    } else if (clHasLocal && clHasRemote) {
      const lv = claudeMdVersion && claudeMdVersion !== 'no version' ? claudeMdVersion : null;
      const rv = claudeMdS3Version && claudeMdS3Version !== 'no version' ? claudeMdS3Version : null;
      const dir = cmpVer(lv, rv);
      if (dir === 'local') claudeAction = 'push';
      else if (dir === 'remote' || dir === null) claudeAction = 'pull';
      else skipped++;
    } else { skipped++; }

    return { pkgPull, pkgPush, pkgDelete, tplPull, tplPush, tplDelete, claudeAction, skipped };
  }, [localPackages, remotePackages, localTemplates, remoteTemplates, claudeMd, claudeMdOnS3, claudeMdVersion, claudeMdS3Version]);

  const executeSyncAll = useCallback(async (plan) => {
    const { pkgPull, pkgPush, pkgDelete, tplPull, tplPush, tplDelete, claudeAction, skipped } = plan;
    setSyncing(true);
    const pullCount = pkgPull.length + tplPull.length + (claudeAction === 'pull' ? 1 : 0);
    const pushCount = pkgPush.length + tplPush.length + (claudeAction === 'push' ? 1 : 0);
    const deleteCount = pkgDelete.length + tplDelete.length;
    setSyncStatus(`Syncing: ${pullCount} pull, ${pushCount} push, ${deleteCount} delete...`);

    let pulled = 0, pushed = 0, deleted = 0, errors = 0;
    const ops = [];

    if (pkgPull.length) ops.push(
      api.authFetch('/api/nanoclaw/dashboard/packages/batch-pull', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: pkgPull, env: s3Env })
      }).then(r => r.json()).then(d => { pulled += d.summary?.succeeded || 0; errors += d.summary?.errors || 0; })
        .catch(() => { errors += pkgPull.length; })
    );
    if (pkgPush.length) ops.push(
      api.authFetch('/api/nanoclaw/dashboard/packages/batch-push', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: pkgPush, env: s3Env })
      }).then(r => r.json()).then(d => { pushed += d.summary?.succeeded || pkgPush.length; errors += d.summary?.errors || 0; })
        .catch(() => { errors += pkgPush.length; })
    );
    if (pkgDelete.length) ops.push(
      api.authFetch('/api/nanoclaw/dashboard/packages/batch-delete-local', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: pkgDelete })
      }).then(r => r.json()).then(d => { deleted += d.summary?.succeeded || 0; errors += d.summary?.errors || 0; })
        .catch(() => { errors += pkgDelete.length; })
    );
    if (tplPull.length) ops.push(
      api.authFetch('/api/nanoclaw/dashboard/templates/batch-pull', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: tplPull, env: s3Env })
      }).then(r => r.json()).then(d => {
        const ok = (d.results || []).filter(r => r.status === 'ok').length;
        pulled += ok; errors += tplPull.length - ok;
      }).catch(() => { errors += tplPull.length; })
    );
    if (tplPush.length) ops.push(
      api.authFetch('/api/nanoclaw/dashboard/templates/batch-push', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: tplPush, env: s3Env })
      }).then(r => r.json()).then(d => {
        const ok = (d.results || []).filter(r => r.status === 'ok').length;
        pushed += ok; errors += tplPush.length - ok;
      }).catch(() => { errors += tplPush.length; })
    );
    if (tplDelete.length) ops.push(
      api.authFetch('/api/nanoclaw/dashboard/templates/batch-delete-local', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: tplDelete })
      }).then(r => r.json()).then(d => { deleted += d.summary?.succeeded || 0; errors += d.summary?.errors || 0; })
        .catch(() => { errors += tplDelete.length; })
    );
    if (claudeAction === 'push') ops.push(
      api.authFetch('/api/nanoclaw/dashboard/claude-md/push', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: claudeMd, env: s3Env })
      }).then(r => r.json()).then(d => { if (d.error) errors++; else pushed++; })
        .catch(() => { errors++; })
    );
    if (claudeAction === 'pull') ops.push(
      api.authFetch('/api/nanoclaw/dashboard/claude-md/pull', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ env: s3Env })
      }).then(r => r.json()).then(d => { if (d.error) errors++; else pulled++; })
        .catch(() => { errors++; })
    );

    await Promise.allSettled(ops);
    const parts = [];
    if (pulled) parts.push(`${pulled} pulled`);
    if (pushed) parts.push(`${pushed} pushed`);
    if (deleted) parts.push(`${deleted} deleted`);
    parts.push(`${skipped} unchanged`);
    if (errors) parts.push(`${errors} errors`);
    setSyncStatus(`\u2713 Synced: ${parts.join(', ')}`);
    refreshAllPackages();
    setSyncing(false);
  }, [s3Env, claudeMd, refreshAllPackages]);

  const handleSyncAll = useCallback(async () => {
    setSyncing(true);
    setSyncStatus('Analyzing versions...');
    const plan = classifySyncItems();
    const { pkgPull, pkgPush, pkgDelete, tplPull, tplPush, tplDelete, claudeAction, skipped } = plan;
    const pullCount = pkgPull.length + tplPull.length + (claudeAction === 'pull' ? 1 : 0);
    const pushCount = pkgPush.length + tplPush.length + (claudeAction === 'push' ? 1 : 0);
    const deleteCount = pkgDelete.length + tplDelete.length;
    if (pullCount + pushCount + deleteCount === 0) {
      setSyncStatus(`\u2713 All synced (${skipped} unchanged)`);
      setSyncing(false);
      return;
    }
    setSyncing(false);
    setSyncPreviewData(plan);
    setSyncPreviewOpen(true);
  }, [classifySyncItems]);

  // Handlers — same pattern as PackagesPage
  const handlePush = useCallback(async (pkgId) => {
    setSyncing(true); setSyncStatus(`Pushing ${pkgId}...`);
    try {
      // CLAUDE.md lives inside _system package
      const actualId = pkgId === 'CLAUDE.md' ? '_system' : pkgId;
      await api.authFetch(`/api/nanoclaw/dashboard/packages/${encodeURIComponent(actualId)}/push`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ env: s3Env }) });
      setSyncStatus(`Pushed ${pkgId}`); refreshAllPackages();
    } catch { setSyncStatus(`Push failed`); }
    setSyncing(false);
  }, [s3Env, refreshAllPackages]);

  const handleSinglePull = useCallback(async (pkgId) => {
    setSyncing(true); setSyncStatus(`Pulling ${pkgId}...`);
    try {
      // CLAUDE.md lives inside _system package
      const actualId = pkgId === 'CLAUDE.md' ? '_system' : pkgId;
      await api.authFetch(`/api/nanoclaw/dashboard/packages/${encodeURIComponent(actualId)}/pull`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ env: s3Env }) });
      setSyncStatus(`Pulled ${pkgId}`); refreshAllPackages();
    } catch { setSyncStatus(`Pull failed`); }
    setSyncing(false);
  }, [s3Env, refreshAllPackages]);

  const handleBatchPull = useCallback(async (ids) => {
    setSyncing(true); setSyncStatus(`Pulling ${ids.length} packages...`);
    try {
      await api.authFetch('/api/nanoclaw/dashboard/packages/batch-pull', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ids, env: s3Env }) });
      setSyncStatus(`Pulled ${ids.length} packages`); refreshAllPackages();
    } catch { setSyncStatus('Batch pull failed'); }
    setSyncing(false);
  }, [s3Env, refreshAllPackages]);

  const handleBatchPush = useCallback(async (ids) => {
    setSyncing(true); setSyncStatus(`Pushing ${ids.length} packages...`);
    try {
      await api.authFetch('/api/nanoclaw/dashboard/packages/batch-push', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ids, env: s3Env }) });
      setSyncStatus(`Pushed ${ids.length} packages`); refreshAllPackages();
    } catch { setSyncStatus('Batch push failed'); }
    setSyncing(false);
  }, [s3Env, refreshAllPackages]);

  const handleArchive = useCallback(async (ids) => {
    try {
      await api.authFetch('/api/nanoclaw/dashboard/packages/archive', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ids, env: s3Env }) });
      refreshAllPackages();
    } catch { /* ignore */ }
  }, [s3Env, refreshAllPackages]);

  const handleUnarchive = useCallback(async (ids) => {
    try {
      await api.authFetch('/api/nanoclaw/dashboard/packages/unarchive', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ids, env: s3Env }) });
      refreshAllPackages();
    } catch { /* ignore */ }
  }, [s3Env, refreshAllPackages]);

  const handleTemplatePush = useCallback(async (tplId) => {
    setSyncing(true);
    try {
      await api.authFetch(`/api/nanoclaw/dashboard/templates/${encodeURIComponent(tplId)}/push`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ env: s3Env }) });
      refreshAllPackages();
    } catch { /* ignore */ }
    setSyncing(false);
  }, [s3Env, refreshAllPackages]);

  const handleTemplateSinglePull = useCallback(async (tplId) => {
    setSyncing(true);
    try {
      await api.authFetch(`/api/nanoclaw/dashboard/templates/${encodeURIComponent(tplId)}/pull`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ env: s3Env }) });
      refreshAllPackages();
    } catch { /* ignore */ }
    setSyncing(false);
  }, [s3Env, refreshAllPackages]);

  const handleTemplateBatchPull = useCallback(async (ids) => {
    setSyncing(true);
    try {
      await api.authFetch('/api/nanoclaw/dashboard/templates/batch-pull', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ids, env: s3Env }) });
      refreshAllPackages();
    } catch { /* ignore */ }
    setSyncing(false);
  }, [s3Env, refreshAllPackages]);

  const handleTemplateBatchPush = useCallback(async (ids) => {
    setSyncing(true);
    try {
      await api.authFetch('/api/nanoclaw/dashboard/templates/batch-push', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ids, env: s3Env }) });
      refreshAllPackages();
    } catch { /* ignore */ }
    setSyncing(false);
  }, [s3Env, refreshAllPackages]);

  const handleTemplateArchive = useCallback(async (ids) => {
    try {
      await api.authFetch('/api/nanoclaw/dashboard/templates/archive', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ids, env: s3Env }) });
      refreshAllPackages();
    } catch { /* ignore */ }
  }, [s3Env, refreshAllPackages]);

  const handleTemplateUnarchive = useCallback(async (ids) => {
    try {
      await api.authFetch('/api/nanoclaw/dashboard/templates/unarchive', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ids, env: s3Env }) });
      refreshAllPackages();
    } catch { /* ignore */ }
  }, [s3Env, refreshAllPackages]);

  // Permanently delete an archived package from the target env's S3 prefix.
  // The backend safety-gates on "id must currently be archived on {env}".
  const handleDeleteArchivedFromS3 = useCallback(async (id) => {
    setSyncStatus(`Deleting archived package "${id}" from ${s3Env}...`);
    try {
      const res = await api.authFetch('/api/nanoclaw/dashboard/packages/archived/delete-from-s3', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, env: s3Env })
      });
      const d = await res.json();
      if (!res.ok || d.status === 'error') {
        setSyncStatus(`❌ Delete failed: ${d.error || (d.errors && d.errors[0]) || res.statusText}`);
      } else {
        setSyncStatus(`✓ Deleted "${id}" from ${s3Env}: ${d.deleted || 0} files${d.errors?.length ? ` (${d.errors.length} errors)` : ''}`);
      }
      refreshAllPackages();
    } catch (err) { setSyncStatus(`❌ Delete failed: ${err.message}`); }
  }, [s3Env, refreshAllPackages]);

  const handleDeleteArchivedTemplateFromS3 = useCallback(async (id) => {
    setSyncStatus(`Deleting archived template "${id}" from ${s3Env}...`);
    try {
      const res = await api.authFetch('/api/nanoclaw/dashboard/templates/archived/delete-from-s3', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, env: s3Env })
      });
      const d = await res.json();
      if (!res.ok || d.status === 'error') {
        setSyncStatus(`❌ Delete failed: ${d.error || (d.errors && d.errors[0]) || res.statusText}`);
      } else {
        setSyncStatus(`✓ Deleted template "${id}" from ${s3Env}: ${d.deleted || 0} files${d.errors?.length ? ` (${d.errors.length} errors)` : ''}`);
      }
      refreshAllPackages();
    } catch (err) { setSyncStatus(`❌ Delete failed: ${err.message}`); }
  }, [s3Env, refreshAllPackages]);

  const openModal = useCallback(async (action) => {
    setModalAction(action); setDiffLoading(true); setModalOpen(true);
    try {
      const res = await api.authFetch('/api/nanoclaw/dashboard/packages/diff');
      const d = await res.json();
      setDiffItems(d.items || []);
      setSelectedItems(new Set((d.items || []).filter(i => i.status !== 'unchanged').map(i => i.id)));
    } catch { setDiffItems([]); }
    setDiffLoading(false);
  }, []);

  const handlePromoteRevert = useCallback(async () => {
    if (selectedItems.size === 0) return;
    setPromoting(true);
    try {
      const endpoint = modalAction === 'promote' ? '/nanoclaw/api/dashboard/packages/promote' : '/nanoclaw/api/dashboard/packages/revert';
      await fetch(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: Array.from(selectedItems) }) });
      setSyncStatus(`${modalAction === 'promote' ? 'Promoted' : 'Reverted'} ${selectedItems.size} items`);
      setModalOpen(false);
    } catch { setSyncStatus(`${modalAction} failed`); }
    setPromoting(false);
  }, [modalAction, selectedItems]);

  const S = {
    btn: { background: V.bgHover, color: V.text, border: `1px solid ${V.border}`, borderRadius: 4, padding: '4px 10px', cursor: 'pointer', fontSize: 12 },
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* S3 Sync Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 14px', borderBottom: `1px solid ${V.border}`, background: V.bgCard, flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 14, fontWeight: 600 }}>S3 Manager</span>
          <span style={{ fontSize: 11, color: V.textDim }}>Env:</span>
          <select value={s3Env || ''} onChange={e => setS3Env(e.target.value)}
            style={{ background: V.bg, color: V.text, border: `1px solid ${V.border}`, borderRadius: 4, padding: '3px 8px', fontSize: 12 }}>
            <option value="dev">dev</option>
            <option value="staging">staging</option>
            <option value="prod">prod</option>
          </select>
          <button onClick={() => { refreshAllPackages(); setSyncStatus('Refreshed'); }}
            style={{ ...S.btn, fontSize: 12, padding: '3px 10px' }}>
            Refresh
          </button>
          <span style={{ fontSize: 11, color: V.textDim }}>{syncStatus}</span>
        </div>
        <div style={{ position: 'relative' }}>
          <button onClick={() => setActionsOpen(!actionsOpen)} style={{ ...S.btn, fontSize: 12, padding: '3px 10px' }}>Actions &#x25BE;</button>
          {actionsOpen && (<>
            <div onClick={() => setActionsOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 99 }} />
            <div style={{ position: 'absolute', right: 0, top: '100%', background: V.bgCard, border: `1px solid ${V.border}`, borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.4)', zIndex: 100, minWidth: 240, marginTop: 4, padding: '4px 0' }}>
              {[
                { icon: '\u21C4', label: `Sync Local \u21C4 ${s3Env || 'S3'}`, desc: 'Bidirectional: pull, push, & delete by version (preview first)', action: () => {
                  setActionsOpen(false);
                  handleSyncAll();
                }},
                { divider: true },
                { icon: '\u21E3', label: `Pull All Local \u2190 ${s3Env || 'S3'}`, desc: 'Force overwrite local from S3 \u2014 destroys local changes', danger: true, action: () => {
                  setActionsOpen(false);
                  setPullConfirmText('');
                  setPullModalOpen(true);
                }},
                { icon: '\u2B06', label: `Push All Local \u2192 ${s3Env || 'S3'}`, desc: 'Mirror local to S3 \u2014 stale remote content is archived', danger: true, action: () => {
                  setActionsOpen(false);
                  setPushConfirmText('');
                  setPushModalOpen(true);
                }},
                // Cross-env dev ↔ staging mirror items. Only dev and staging
                // see a counterpart here — staging ↔ prod is handled below by
                // Promote/Revert (renamed to reuse their familiar names with
                // the new full-mirror backend).
                ...(() => {
                  const pairs = {
                    dev: ['staging'],
                    staging: ['dev'],
                  };
                  const counterparts = pairs[s3Env] || [];
                  if (counterparts.length === 0) return [];
                  const items = [{ divider: true }];
                  for (const other of counterparts) {
                    items.push({
                      icon: '\u21E3',
                      label: `Pull all ${s3Env} \u2190 ${other}`,
                      desc: `Full mirror ${other} S3 \u2192 ${s3Env} S3 \u2014 overwrites + deletes stale`,
                      action: () => {
                        setActionsOpen(false);
                        setCrossFrom(other);
                        setCrossTo(s3Env);
                        setCrossDirection('pull');
                        setCrossConfirmText('');
                        setCrossModalOpen(true);
                      },
                    });
                    items.push({
                      icon: '\u2B06',
                      label: `Push all ${s3Env} \u2192 ${other}`,
                      desc: `Full mirror ${s3Env} S3 \u2192 ${other} S3 \u2014 overwrites + deletes stale`,
                      action: () => {
                        setActionsOpen(false);
                        setCrossFrom(s3Env);
                        setCrossTo(other);
                        setCrossDirection('push');
                        setCrossConfirmText('');
                        setCrossModalOpen(true);
                      },
                    });
                  }
                  return items;
                })(),
                // Promote / Revert — familiar staging ↔ prod names, now
                // backed by the same cross-env mirror function. Visible in
                // every env so prod users can also trigger them.
                { divider: true },
                { icon: '\uD83D\uDE80', label: 'Promote Staging \u2192 PROD', desc: 'Full mirror staging S3 \u2192 prod S3 (overwrites + deletes stale)', action: () => {
                  setActionsOpen(false);
                  setCrossFrom('staging');
                  setCrossTo('prod');
                  setCrossDirection('push');
                  setCrossConfirmText('');
                  setCrossModalOpen(true);
                }},
                { icon: '\u21A9', label: 'Revert PROD \u2192 Staging', desc: 'Full mirror prod S3 \u2192 staging S3 (rolls staging back to prod)', action: () => {
                  setActionsOpen(false);
                  setCrossFrom('prod');
                  setCrossTo('staging');
                  setCrossDirection('pull');
                  setCrossConfirmText('');
                  setCrossModalOpen(true);
                }},
              ].map((item, i) => item.divider ? (
                <div key={i} style={{ height: 1, background: V.border, margin: '4px 8px' }} />
              ) : (
                <button key={i} onClick={item.action}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', padding: '8px 12px', background: 'none', border: 'none', color: item.danger ? V.red : V.text, cursor: 'pointer', fontSize: 13 }}
                  onMouseEnter={e => e.currentTarget.style.background = item.danger ? 'rgba(255,59,48,0.08)' : V.bgHover}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <span style={{ fontSize: 14, width: 20, textAlign: 'center', flexShrink: 0 }}>{item.icon}</span>
                  <div>
                    <div style={{ fontWeight: 500 }}>{item.label}</div>
                    <div style={{ fontSize: 11, color: item.danger ? V.red : V.textDim, marginTop: 1, opacity: item.danger ? 0.85 : 1 }}>{item.desc}</div>
                  </div>
                </button>
              ))}
            </div>
          </>)}
        </div>
      </div>

      {/* Promote/Revert Modal */}
      {modalOpen && (
        <div onClick={() => setModalOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: V.bgCard, border: `1px solid ${V.border}`, borderRadius: 8, width: 500, maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: `1px solid ${V.border}` }}>
              <span style={{ fontWeight: 600, fontSize: 14 }}>{modalAction === 'promote' ? 'Push Staging \u2192 PROD' : 'Revert Staging \u2190 PROD'}</span>
              <button onClick={() => setModalOpen(false)} style={{ background: 'none', border: 'none', color: V.textDim, fontSize: 18, cursor: 'pointer' }}>&times;</button>
            </div>
            <div style={{ padding: 16, overflowY: 'auto', flex: 1 }}>
              {diffLoading ? <div style={{ color: V.textDim }}>Loading diff...</div> : diffItems.length === 0 ? (
                <div style={{ color: V.textDim }}>No differences between staging and prod.</div>
              ) : (<>
                <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                  <button onClick={() => setSelectedItems(new Set(diffItems.map(i => i.id)))} style={{ ...S.btn, fontSize: 11, padding: '2px 8px' }}>Select All</button>
                  <button onClick={() => setSelectedItems(new Set())} style={{ ...S.btn, fontSize: 11, padding: '2px 8px' }}>Select None</button>
                </div>
                {diffItems.map(item => (
                  <label key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderRadius: 4, cursor: 'pointer', fontSize: 13 }}>
                    <input type="checkbox" checked={selectedItems.has(item.id)}
                      onChange={e => { const s = new Set(selectedItems); e.target.checked ? s.add(item.id) : s.delete(item.id); setSelectedItems(s); }} />
                    <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 3, fontWeight: 600, textTransform: 'uppercase',
                      background: item.status === 'added' ? '#065f46' : item.status === 'modified' ? '#92400e' : '#991b1b',
                      color: item.status === 'added' ? '#34d399' : item.status === 'modified' ? '#fbbf24' : '#f87171' }}>
                      {item.status}
                    </span>
                    <span>{item.id}</span>
                  </label>
                ))}
              </>)}
            </div>
            <div style={{ padding: '12px 16px', borderTop: `1px solid ${V.border}`, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setModalOpen(false)} style={S.btn}>Cancel</button>
              <button onClick={handlePromoteRevert} disabled={promoting || selectedItems.size === 0}
                style={{ ...S.btn, background: modalAction === 'promote' ? V.accent : '#dc2626', color: '#fff', opacity: (promoting || selectedItems.size === 0) ? 0.4 : 1 }}>
                {promoting ? 'Working...' : `${modalAction === 'promote' ? 'Promote' : 'Revert'} ${selectedItems.size} items`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bidirectional sync dry-run preview modal */}
      {syncPreviewOpen && syncPreviewData && (() => {
        const d = syncPreviewData;
        const pullCount = d.pkgPull.length + d.tplPull.length + (d.claudeAction === 'pull' ? 1 : 0);
        const pushCount = d.pkgPush.length + d.tplPush.length + (d.claudeAction === 'push' ? 1 : 0);
        const deleteCount = d.pkgDelete.length + d.tplDelete.length;
        const lineStyle = { fontSize: 12, padding: '2px 0', display: 'flex', justifyContent: 'space-between' };
        return (
          <div onClick={() => setSyncPreviewOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div onClick={e => e.stopPropagation()} style={{ background: V.bgCard, border: `1px solid ${V.border}`, borderRadius: 8, width: 420, maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: `1px solid ${V.border}`, flexShrink: 0 }}>
                <span style={{ fontWeight: 600, fontSize: 14 }}>{`Sync Local \u21C4 ${s3Env || 'S3'} \u2014 Preview`}</span>
                <button onClick={() => setSyncPreviewOpen(false)} style={{ background: 'none', border: 'none', color: V.textDim, fontSize: 18, cursor: 'pointer' }}>&times;</button>
              </div>
              <div style={{ padding: '12px 16px', overflowY: 'auto', flex: 1 }}>
                {pullCount > 0 && (
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#60a5fa', marginBottom: 4 }}>{`\u2B07 Pull (${pullCount})`}</div>
                    {d.pkgPull.map(id => <div key={id} style={lineStyle}><span style={{ fontFamily: 'monospace' }}>{id}</span><span style={{ color: V.textDim }}>pkg</span></div>)}
                    {d.tplPull.map(id => <div key={id} style={lineStyle}><span style={{ fontFamily: 'monospace' }}>{id}</span><span style={{ color: V.textDim }}>tpl</span></div>)}
                    {d.claudeAction === 'pull' && <div style={lineStyle}><span style={{ fontFamily: 'monospace' }}>CLAUDE.md</span><span style={{ color: V.textDim }}>sys</span></div>}
                  </div>
                )}
                {pushCount > 0 && (
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#fbbf24', marginBottom: 4 }}>{`\u2B06 Push (${pushCount})`}</div>
                    {d.pkgPush.map(id => <div key={id} style={lineStyle}><span style={{ fontFamily: 'monospace' }}>{id}</span><span style={{ color: V.textDim }}>pkg</span></div>)}
                    {d.tplPush.map(id => <div key={id} style={lineStyle}><span style={{ fontFamily: 'monospace' }}>{id}</span><span style={{ color: V.textDim }}>tpl</span></div>)}
                    {d.claudeAction === 'push' && <div style={lineStyle}><span style={{ fontFamily: 'monospace' }}>CLAUDE.md</span><span style={{ color: V.textDim }}>sys</span></div>}
                  </div>
                )}
                {deleteCount > 0 && (
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#f87171', marginBottom: 4 }}>{`\u2716 Delete local (${deleteCount})`}</div>
                    {d.pkgDelete.map(id => <div key={id} style={lineStyle}><span style={{ fontFamily: 'monospace' }}>{id}</span><span style={{ color: V.textDim }}>pkg</span></div>)}
                    {d.tplDelete.map(id => <div key={id} style={lineStyle}><span style={{ fontFamily: 'monospace' }}>{id}</span><span style={{ color: V.textDim }}>tpl</span></div>)}
                  </div>
                )}
                <div style={{ fontSize: 11, color: V.textDim, marginTop: 4 }}>{d.skipped} unchanged</div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '8px 16px', borderTop: `1px solid ${V.border}`, flexShrink: 0 }}>
                <button onClick={() => setSyncPreviewOpen(false)} style={S.btn}>Cancel</button>
                <button onClick={() => { setSyncPreviewOpen(false); executeSyncAll(syncPreviewData); }} style={{ ...S.btn, background: V.accent, color: '#fff' }}>
                  {`Sync (${pullCount + pushCount + deleteCount} actions)`}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Pull All Local — destructive force-mirror confirm modal */}
      {pullModalOpen && (
        <div onClick={() => !pulling && setPullModalOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: V.bgCard, border: `1px solid ${V.red}`, borderRadius: 8, width: 480, display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: `1px solid ${V.border}` }}>
              <span style={{ fontWeight: 600, fontSize: 14, color: V.red }}>{`\u26A0 Pull All Local \u2190 ${s3Env || 'S3'}`}</span>
              <button onClick={() => !pulling && setPullModalOpen(false)} disabled={pulling} style={{ background: 'none', border: 'none', color: V.textDim, fontSize: 18, cursor: pulling ? 'not-allowed' : 'pointer' }}>&times;</button>
            </div>
            <div style={{ padding: 16, fontSize: 13, color: V.text, lineHeight: 1.5 }}>
              <div style={{ marginBottom: 12 }}>
                This will <b style={{ color: V.red }}>force-overwrite</b> the local packages cache with a pristine copy of <b>{s3Env || 'S3'}</b>.
              </div>
              <div style={{ marginBottom: 12, padding: '8px 12px', background: 'rgba(255,59,48,0.08)', borderRadius: 4, color: V.red, fontSize: 12 }}>
                Local-only edits that skipped a version bump will be <b>destroyed</b>. Unlike Sync, this bypasses the manifest hash cache and ignores archive filtering.
              </div>
              <div style={{ marginBottom: 12, color: V.textDim, fontSize: 12 }}>
                Local: {localPackages.length} packages, {localTemplates.length} templates {'\u2003\u2014\u2003'} Remote: {remotePackages.length} packages, {remoteTemplates.length} templates
              </div>
            </div>
            <div style={{ padding: '12px 16px', borderTop: `1px solid ${V.border}`, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setPullModalOpen(false)} disabled={pulling} style={S.btn}>Cancel</button>
              <button
                disabled={pulling}
                onClick={async () => {
                  setPulling(true); setSyncing(true); setSyncStatus(`Pulling all from ${s3Env}...`);
                  try {
                    const res = await api.authFetch('/api/nanoclaw/dashboard/packages/mirror-from-s3', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ env: s3Env }),
                    });
                    const d = await res.json();
                    setSyncStatus(`Pulled: ${d.downloaded || 0} downloaded, ${d.deleted || 0} stale deleted${d.errors?.length ? ` (${d.errors.length} errors)` : ''}`);
                  } catch { setSyncStatus('Pull All Local failed'); }
                  refreshAllPackages();
                  setPulling(false); setSyncing(false); setPullModalOpen(false); setPullConfirmText('');
                }}
                style={{ ...S.btn, background: '#dc2626', color: '#fff', opacity: pulling ? 0.4 : 1 }}>
                {pulling ? 'Pulling...' : `Force overwrite local`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Push All Local — destructive force-mirror confirm modal */}
      {pushModalOpen && (
        <div onClick={() => !pushing && setPushModalOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: V.bgCard, border: `1px solid ${V.red}`, borderRadius: 8, width: 480, display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: `1px solid ${V.border}` }}>
              <span style={{ fontWeight: 600, fontSize: 14, color: V.red }}>{`\u26A0 Push All Local \u2192 ${s3Env || 'S3'}`}</span>
              <button onClick={() => !pushing && setPushModalOpen(false)} disabled={pushing} style={{ background: 'none', border: 'none', color: V.textDim, fontSize: 18, cursor: pushing ? 'not-allowed' : 'pointer' }}>&times;</button>
            </div>
            <div style={{ padding: 16, fontSize: 13, color: V.text, lineHeight: 1.5 }}>
              <div style={{ marginBottom: 12 }}>
                This will <b style={{ color: V.red }}>mirror local to S3</b> — changed files are overwritten, and remote content not present locally is <b>archived</b> (not deleted — recoverable via Unarchive).
              </div>
              <div style={{ marginBottom: 12, padding: '8px 12px', background: 'rgba(255,59,48,0.08)', borderRadius: 4, color: V.red, fontSize: 12 }}>
                Remote files with different content will be overwritten by local versions. Remote packages or cards missing locally will be added to <code>{s3Env}/archive.json</code>.
              </div>
              <div style={{ marginBottom: 12, color: V.textDim, fontSize: 12 }}>
                Local: {localPackages.length} packages, {localTemplates.length} templates {'\u2003\u2014\u2003'} Remote: {remotePackages.length} packages, {remoteTemplates.length} templates
              </div>
            </div>
            <div style={{ padding: '12px 16px', borderTop: `1px solid ${V.border}`, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setPushModalOpen(false)} disabled={pushing} style={S.btn}>Cancel</button>
              <button
                disabled={pushing}
                onClick={async () => {
                  setPushing(true); setSyncing(true); setSyncStatus(`Mirroring local \u2192 ${s3Env}...`);
                  try {
                    const res = await api.authFetch('/api/nanoclaw/dashboard/packages/mirror-to-s3', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ env: s3Env }),
                    });
                    const d = await res.json();
                    setSyncStatus(`Mirror: ${d.pushed || 0} pushed, ${d.archived || 0} archived${d.errors?.length ? ` (${d.errors.length} errors)` : ''}`);
                  } catch { setSyncStatus('Mirror failed'); }
                  refreshAllPackages();
                  setPushing(false); setSyncing(false); setPushModalOpen(false); setPushConfirmText('');
                }}
                style={{ ...S.btn, background: '#dc2626', color: '#fff', opacity: pushing ? 0.4 : 1 }}>
                {pushing ? 'Pushing...' : `Force mirror to S3`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cross-env mirror (S3 env A → S3 env B) — destructive force-mirror confirm modal */}
      {crossModalOpen && crossFrom && crossTo && (() => {
        // Required confirm phrase depends only on the TARGET env being
        // overwritten, not on direction (so Revert prod → staging also
        // requires OVERWRITE STAGING). Dev is the only "safe" target.
        const requiredPhrase = crossTo === 'dev' ? 'dev' : `OVERWRITE ${crossTo.toUpperCase()}`;
        const title = crossDirection === 'pull'
          ? `\u26A0 Pull all ${crossTo} \u2190 ${crossFrom}`
          : `\u26A0 Push all ${crossFrom} \u2192 ${crossTo}`;
        const buttonLabel = `Force mirror ${crossFrom} \u2192 ${crossTo}`;
        const canConfirm = crossConfirmText === requiredPhrase && !crossMirroring;
        return (
          <div onClick={() => !crossMirroring && setCrossModalOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div onClick={e => e.stopPropagation()} style={{ background: V.bgCard, border: `1px solid ${V.red}`, borderRadius: 8, width: 520, display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: `1px solid ${V.border}` }}>
                <span style={{ fontWeight: 600, fontSize: 14, color: V.red }}>{title}</span>
                <button onClick={() => !crossMirroring && setCrossModalOpen(false)} disabled={crossMirroring} style={{ background: 'none', border: 'none', color: V.textDim, fontSize: 18, cursor: crossMirroring ? 'not-allowed' : 'pointer' }}>&times;</button>
              </div>
              <div style={{ padding: 16, fontSize: 13, color: V.text, lineHeight: 1.5 }}>
                <div style={{ marginBottom: 12 }}>
                  This will <b style={{ color: V.red }}>force-mirror</b> <code>{crossFrom}</code> S3 onto <code>{crossTo}</code> S3. Every file under <code>{crossTo}/</code> not present in <code>{crossFrom}/</code> will be <b style={{ color: V.red }}>deleted</b>, and <code>{crossFrom}/archive.json</code> will replace <code>{crossTo}/archive.json</code>.
                </div>
                <div style={{ marginBottom: 12, padding: '8px 12px', background: 'rgba(255,59,48,0.08)', borderRadius: 4, color: V.red, fontSize: 12 }}>
                  {crossTo === 'prod' ? (<>You are overwriting <b>PRODUCTION</b>. This affects every live user of prod. Prefer the per-item <b>Promote</b> flow unless you explicitly want a wholesale reset.</>)
                    : crossTo === 'staging' ? (<>You are overwriting the shared <b>staging</b> environment. Anyone else testing on staging will see their work wiped.</>)
                    : (<>This is a direct S3-to-S3 copy — no local round-trip. Use the Sync action afterwards to refresh your local cache.</>)}
                </div>
                <div style={{ marginBottom: 6, fontSize: 12, color: V.textDim }}>
                  Type <code style={{ color: V.text }}>{requiredPhrase}</code> to confirm:
                </div>
                <input
                  type="text"
                  value={crossConfirmText}
                  onChange={e => setCrossConfirmText(e.target.value)}
                  disabled={crossMirroring}
                  autoFocus
                  style={{ width: '100%', padding: '6px 10px', background: V.bg, border: `1px solid ${V.border}`, borderRadius: 4, color: V.text, fontSize: 13, fontFamily: 'monospace' }}
                />
              </div>
              <div style={{ padding: '12px 16px', borderTop: `1px solid ${V.border}`, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button onClick={() => setCrossModalOpen(false)} disabled={crossMirroring} style={S.btn}>Cancel</button>
                <button
                  disabled={!canConfirm}
                  onClick={async () => {
                    setCrossMirroring(true); setSyncing(true); setSyncStatus(`Mirroring ${crossFrom} \u2192 ${crossTo}...`);
                    try {
                      const res = await api.authFetch('/api/nanoclaw/dashboard/packages/mirror-across-envs', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ from_env: crossFrom, to_env: crossTo }),
                      });
                      const d = await res.json();
                      if (!res.ok || d.error) {
                        setSyncStatus(`Mirror failed: ${d.error || res.statusText}`);
                      } else {
                        setSyncStatus(`Mirrored ${crossFrom} \u2192 ${crossTo}: ${d.copied || 0} copied, ${d.deleted || 0} deleted${d.errors?.length ? ` (${d.errors.length} errors)` : ''}`);
                      }
                    } catch (err) { setSyncStatus(`Mirror failed: ${err.message}`); }
                    refreshAllPackages();
                    setCrossMirroring(false); setSyncing(false); setCrossModalOpen(false); setCrossConfirmText('');
                  }}
                  style={{ ...S.btn, background: '#dc2626', color: '#fff', opacity: canConfirm ? 1 : 0.4 }}>
                  {crossMirroring ? 'Mirroring...' : buttonLabel}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* SftpManagerView — full sync status list */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        <SftpManagerView
          localPackages={localPackages} remotePackages={remotePackages} s3Env={s3Env}
          onPush={handlePush} onSinglePull={handleSinglePull} onBatchPull={handleBatchPull} onBatchPush={handleBatchPush}
          onArchive={handleArchive} onUnarchive={handleUnarchive}
          syncing={syncing} claudeMd={claudeMd} claudeMdOnS3={claudeMdOnS3} s3Enabled={s3Enabled}
          claudeMdVersion={claudeMdVersion} claudeMdS3Version={claudeMdS3Version} archivedIds={archivedIds}
          localTemplates={localTemplates} remoteTemplates={remoteTemplates} archivedTemplateIds={archivedTemplateIds}
          onTemplatePush={handleTemplatePush} onTemplateSinglePull={handleTemplateSinglePull}
          onTemplateBatchPull={handleTemplateBatchPull} onTemplateBatchPush={handleTemplateBatchPush}
          onTemplateArchive={handleTemplateArchive} onTemplateUnarchive={handleTemplateUnarchive}
          onDeleteArchivedFromS3={handleDeleteArchivedFromS3}
          onDeleteArchivedTemplateFromS3={handleDeleteArchivedTemplateFromS3}
          domains={domains}
        />
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════

function SkillsPage() {
  const [skills, setSkills] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.authFetch('/api/nanoclaw/dashboard/package-skills')
      .then(r => r.json())
      .then(d => {
        const shared = (d.skills || []).filter(s => s.source === 'shared');
        setSkills(shared);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const S = {
    sidebar: { width: 260, borderRight: `1px solid ${V.border}`, background: V.bgCard, display: 'flex', flexDirection: 'column' },
    item: (active) => ({
      padding: '10px 14px', cursor: 'pointer', borderBottom: `1px solid ${V.border}`,
      background: active ? V.accentDim : 'transparent', transition: 'background 0.15s',
    }),
  };

  if (loading) return <div style={{ padding: 40, color: V.textDim }}>Loading skills...</div>;

  return (
    <div style={{ display: 'flex', height: '100%' }}>
      <div style={S.sidebar}>
        <div style={{ padding: '12px 14px', fontWeight: 600, fontSize: 13, borderBottom: `1px solid ${V.border}` }}>
          Shared Skills ({skills.length})
        </div>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {skills.map(sk => (
            <div key={sk.id} style={S.item(selected?.id === sk.id)}
              onClick={() => setSelected(sk)}
              onMouseEnter={e => { if (selected?.id !== sk.id) e.currentTarget.style.background = V.bgHover; }}
              onMouseLeave={e => { if (selected?.id !== sk.id) e.currentTarget.style.background = 'transparent'; }}
            >
              <div style={{ fontSize: 13, fontWeight: 500 }}>{sk.icon} {sk.name}</div>
              <div style={{ fontSize: 11, color: V.textDim, marginTop: 2 }}>{sk.category}</div>
            </div>
          ))}
        </div>
      </div>
      <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
        {selected ? (
          <div>
            <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 4 }}>{selected.icon} {selected.name}</h2>
            <div style={{ fontSize: 12, color: V.textDim, marginBottom: 16 }}>{selected.category} &middot; {selected.description}</div>
            {selected.skillContent ? (
              <MarkdownRenderer style={{ fontSize: 13, lineHeight: 1.6 }}>{selected.skillContent}</MarkdownRenderer>
            ) : (
              <div style={{ color: V.textDim }}>No SKILL.md content loaded</div>
            )}
          </div>
        ) : (
          <div style={{ padding: 40, color: V.textDim, textAlign: 'center' }}>Select a skill</div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// TOOLS PAGE — native tools from skill-executor
// ══════════════════════════════════════════════════════════════════════

function ToolsPage() {
  const [nativeTools, setNativeTools] = useState([]);
  const [sharedTools, setSharedTools] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.authFetch('/api/nanoclaw/dashboard/native-tools').then(r => r.json()).catch(() => ({ tools: [] })),
      api.authFetch('/api/nanoclaw/dashboard/shared-tools').then(r => r.json()).catch(() => ({ tools: [] })),
    ]).then(([native, shared]) => {
      setNativeTools(native.tools || []);
      setSharedTools(shared.tools || []);
      setLoading(false);
    });
  }, []);

  const S = {
    sidebar: { width: 260, borderRight: `1px solid ${V.border}`, background: V.bgCard, display: 'flex', flexDirection: 'column' },
    item: (active) => ({
      padding: '10px 14px', cursor: 'pointer', borderBottom: `1px solid ${V.border}`,
      background: active ? V.accentDim : 'transparent', transition: 'background 0.15s',
    }),
    sectionHeader: { padding: '10px 14px', fontWeight: 600, borderBottom: `1px solid ${V.border}`, color: V.textDim, textTransform: 'uppercase', letterSpacing: '0.03em', fontSize: 11 },
    field: { display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: `1px solid ${V.border}`, fontSize: 13 },
  };

  if (loading) return <div style={{ padding: 40, color: V.textDim }}>Loading tools...</div>;

  return (
    <div style={{ display: 'flex', height: '100%' }}>
      <div style={S.sidebar}>
        <div style={{ padding: '12px 14px', fontWeight: 600, fontSize: 13, borderBottom: `1px solid ${V.border}` }}>
          Tools ({nativeTools.length + sharedTools.length})
        </div>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {sharedTools.length > 0 && (
            <>
              <div style={S.sectionHeader}>Shared Tools ({sharedTools.length})</div>
              {sharedTools.map(t => (
                <div key={`shared-${t.name}`} style={S.item(selected?.name === t.name && selected?.source === 'shared')}
                  onClick={() => setSelected({ ...t, source: 'shared' })}
                  onMouseEnter={e => { if (selected?.name !== t.name || selected?.source !== 'shared') e.currentTarget.style.background = V.bgHover; }}
                  onMouseLeave={e => { if (selected?.name !== t.name || selected?.source !== 'shared') e.currentTarget.style.background = 'transparent'; }}
                >
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{t.icon} {t.name}</div>
                  <div style={{ fontSize: 11, color: V.textDim, marginTop: 2 }}>{t.language} script</div>
                </div>
              ))}
            </>
          )}
          <div style={S.sectionHeader}>Native Tools ({nativeTools.length})</div>
          {nativeTools.map(t => (
            <div key={t.name} style={S.item(selected?.name === t.name && selected?.source !== 'shared')}
              onClick={() => setSelected(t)}
              onMouseEnter={e => { if (selected?.name !== t.name) e.currentTarget.style.background = V.bgHover; }}
              onMouseLeave={e => { if (selected?.name !== t.name) e.currentTarget.style.background = 'transparent'; }}
            >
              <div style={{ fontSize: 13, fontWeight: 500 }}>{t.icon} {t.name}</div>
              <div style={{ fontSize: 11, color: V.textDim, marginTop: 2 }}>{t.category}</div>
            </div>
          ))}
        </div>
      </div>
      <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
        {selected ? (
          <div>
            <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 4 }}>{selected.icon} {selected.name}</h2>
            <div style={{ fontSize: 12, color: V.textDim, marginBottom: 12 }}>
              {selected.source === 'shared' ? (
                <><code>{selected.path}</code> ({selected.language})</>
              ) : selected.category}
            </div>
            <p style={{ fontSize: 13, marginBottom: 16 }}>{selected.description}</p>
            {selected.input_schema?.properties && (
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: V.textDim, marginBottom: 8, textTransform: 'uppercase' }}>Parameters</div>
                {Object.entries(selected.input_schema.properties).map(([name, schema]) => (
                  <div key={name} style={S.field}>
                    <span><code style={{ background: V.bgHover, padding: '1px 6px', borderRadius: 4 }}>{name}</code></span>
                    <span style={{ color: V.textDim, fontSize: 12 }}>
                      {schema.type}
                      {(selected.input_schema.required || []).includes(name) && <span style={{ color: V.red, marginLeft: 4 }}>*</span>}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div style={{ padding: 40, color: V.textDim, textAlign: 'center' }}>Select a tool</div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// APIs PAGE — API key health checks
// ══════════════════════════════════════════════════════════════════════

function ApisPage() {
  const [apis, setApis] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.authFetch('/api/nanoclaw/dashboard/health/all')
      .then(r => r.json())
      .then(d => { setApis(d.apis || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const statusColor = (s) => s === 'ok' ? V.green : s === 'warn' ? V.yellow : V.red;

  if (loading) return <div style={{ padding: 40, color: V.textDim }}>Loading API status...</div>;

  return (
    <div style={{ padding: 20, overflowY: 'auto', height: '100%' }}>
      <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>API Keys</h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
        {apis.map((a, i) => (
          <div key={i} style={{ background: V.bgCard, border: `1px solid ${V.border}`, borderRadius: 10, padding: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontWeight: 600, fontSize: 14 }}>{a.api || a.name}</span>
              <span style={{
                display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11,
                background: statusColor(a.status) + '22', color: statusColor(a.status),
              }}>{a.status}</span>
            </div>
            {a.envVars?.map((ev, j) => (
              <div key={j} style={{ fontSize: 12, color: V.textDim, marginTop: 4 }}>
                <code>{ev.envVar}</code>: {ev.set ? <span style={{ color: V.green }}>set ({ev.masked})</span> : <span style={{ color: V.red }}>missing</span>}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// USAGE PAGE
// ══════════════════════════════════════════════════════════════════════

function UsagePage() {
  const [balance, setBalance] = useState(null);
  const [sessions, setSessions] = useState({ items: [], total: 0, page: 1, page_size: 20 });
  const [loading, setLoading] = useState(true);
  const [expandedSession, setExpandedSession] = useState(null);
  const [details, setDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [sessionFilter, setSessionFilter] = useState('');

  useEffect(() => {
    api.request('/api/users/me/profile', {}, { retries: 1, timeout: 5000 })
      .then(d => setBalance(d?.balance ?? 0))
      .catch(() => {});
  }, []);

  const fetchSessions = async (page = 1, filterOverride) => {
    setLoading(true);
    try {
      const filter = filterOverride !== undefined ? filterOverride : sessionFilter;
      let url = `/api/users/me/usage/sessions?page=${page}&page_size=20`;
      if (filter.trim()) url += `&session_id=${encodeURIComponent(filter.trim())}`;
      const data = await api.request(url);
      setSessions(data || { items: [], total: 0, page, page_size: 20 });
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { fetchSessions(); }, []);

  const fetchSessionDetail = async (sessionId) => {
    setDetailsLoading(true);
    try {
      const data = await api.request(`/api/users/me/usage/sessions/${sessionId}?page_size=100`);
      setDetails(data);
    } catch {
      setDetails(null);
    }
    setDetailsLoading(false);
  };

  const toggleSession = async (sessionId) => {
    if (expandedSession === sessionId) {
      setExpandedSession(null);
      setDetails(null);
      return;
    }
    setExpandedSession(sessionId);
    setDetailsLoading(true);
    try {
      const data = await api.request(`/api/users/me/usage/sessions/${sessionId}?page_size=100`);
      // Guard against stale response if user switched sessions during fetch
      setExpandedSession(cur => {
        if (cur === sessionId) setDetails(data);
        return cur;
      });
    } catch {
      setExpandedSession(cur => {
        if (cur === sessionId) setDetails(null);
        return cur;
      });
    }
    setDetailsLoading(false);
  };

  return (
    <div style={{ padding: 20, overflowY: 'auto', height: '100%' }}>
      <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>Usage</h2>

      {/* Balance card */}
      <div style={{
        background: V.bgCard, border: `1px solid ${V.border}`, borderRadius: 10,
        padding: 16, marginBottom: 20,
      }}>
        <div style={{ fontSize: 12, color: V.textDim }}>Balance</div>
        <div style={{ fontSize: 24, fontWeight: 700, color: V.text }}>
          {balance != null ? `$${Number(balance).toFixed(2)}` : '...'}
          <span style={{ fontSize: 12, color: V.textDim, marginLeft: 6 }}>USD</span>
        </div>
      </div>

      {/* Session filter */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center' }}>
        <h3 style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>Sessions</h3>
        <input
          placeholder="Search Session ID..."
          value={sessionFilter}
          onChange={e => setSessionFilter(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') fetchSessions(1); }}
          style={{
            flex: 1, maxWidth: 260, padding: '4px 8px', fontSize: 12,
            border: `1px solid ${V.border}`, borderRadius: 4,
            background: V.bgCard, color: V.text, outline: 'none',
          }}
        />
        <button
          onClick={() => fetchSessions(1)}
          style={{
            padding: '4px 10px', fontSize: 12, border: `1px solid ${V.border}`,
            borderRadius: 4, background: 'transparent', color: V.text, cursor: 'pointer',
          }}
        >Search</button>
        {sessionFilter && (
          <button
            onClick={() => { setSessionFilter(''); fetchSessions(1, ''); }}
            style={{
              padding: '4px 10px', fontSize: 12, border: `1px solid ${V.border}`,
              borderRadius: 4, background: 'transparent', color: V.textDim, cursor: 'pointer',
            }}
          >Clear</button>
        )}
      </div>
      {loading && <div style={{ color: V.textDim }}>Loading...</div>}
      {!loading && sessions.items.length === 0 && (
        <div style={{ color: V.textDim, padding: 20, textAlign: 'center' }}>No usage records</div>
      )}
      {sessions.items.map(s => (
        <div key={s.session_id} style={{ marginBottom: 8 }}>
          <div
            onClick={() => toggleSession(s.session_id)}
            style={{
              background: V.bgCard, border: `1px solid ${V.border}`, borderRadius: 8,
              padding: '12px 16px', cursor: 'pointer',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}
          >
            <div>
              <span style={{ fontFamily: 'monospace', fontSize: 11, wordBreak: 'break-all' }}>
                {s.session_id}
              </span>
              <span style={{ fontSize: 11, color: V.textDim, marginLeft: 12 }}>
                {s.request_count} calls
              </span>
            </div>
            <div style={{ textAlign: 'right' }}>
              <span style={{ fontWeight: 600 }}>${Number(s.total_cost).toFixed(4)}</span>
              <div style={{ fontSize: 10, color: V.textDim }}>
                {new Date(s.last_call_at).toLocaleString('zh-CN')}
              </div>
            </div>
          </div>

          {/* Expanded detail */}
          {expandedSession === s.session_id && (
            <div style={{
              background: V.bg, border: `1px solid ${V.border}`, borderTop: 'none',
              borderRadius: '0 0 8px 8px', padding: 12,
            }}>
              {detailsLoading && <div style={{ color: V.textDim, fontSize: 12 }}>Loading details...</div>}
              {details && !detailsLoading && (
                <>
                  <div style={{ fontSize: 12, marginBottom: 8, color: V.textDim }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                      <span>
                        Total: <span style={{ fontWeight: 600, color: V.text }}>${Number(details.total_cost).toFixed(4)}</span> | {details.total} calls
                      </span>
                      <button
                      onClick={(e) => { e.stopPropagation(); fetchSessionDetail(s.session_id); }}
                      style={{
                        padding: '2px 8px', fontSize: 11, border: `1px solid ${V.border}`,
                        borderRadius: 4, background: 'transparent', color: V.textDim, cursor: 'pointer',
                      }}
                    >Refresh</button>
                    </div>
                    {details.details?.length > 0 && (() => {
                      const byModel = {};
                      details.details.forEach(d => {
                        byModel[d.model_name] = (byModel[d.model_name] || 0) + Number(d.cost || 0);
                      });
                      return (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {Object.entries(byModel).sort((a, b) => b[1] - a[1]).map(([m, c]) => (
                            <span key={m} style={{
                              padding: '2px 8px', borderRadius: 4, fontSize: 11,
                              background: V.bgCard, border: `1px solid ${V.border}`,
                            }}>
                              {m} <span style={{ color: V.text, fontWeight: 600 }}>${c.toFixed(4)}</span>
                            </span>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse', minWidth: 700 }}>
                      <thead>
                        <tr style={{ color: V.textDim, borderBottom: `2px solid ${V.border}` }}>
                          <th style={{ textAlign: 'left', padding: '4px 6px', fontWeight: 600 }}>Time</th>
                          <th style={{ textAlign: 'left', padding: '4px 6px', fontWeight: 600 }}>Model</th>
                          <th style={{ textAlign: 'right', padding: '4px 6px', fontWeight: 600 }}>Input</th>
                          <th style={{ textAlign: 'right', padding: '4px 6px', fontWeight: 600 }}>Output</th>
                          <th style={{ textAlign: 'right', padding: '4px 6px', fontWeight: 600 }}>Thinking</th>
                          <th style={{ textAlign: 'right', padding: '4px 6px', fontWeight: 600 }}>Cache</th>
                          <th style={{ textAlign: 'right', padding: '4px 6px', fontWeight: 600 }}>Cost</th>
                          <th style={{ textAlign: 'right', padding: '4px 6px', fontWeight: 600 }}>Latency</th>
                          <th style={{ textAlign: 'center', padding: '4px 6px', fontWeight: 600 }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {details.details.map((d, i) => (
                          <tr key={i} style={{ borderBottom: `1px solid ${V.border}22` }}>
                            <td style={{ padding: '3px 6px', whiteSpace: 'nowrap', fontSize: 10, color: V.textDim }}>{new Date(d.created_at).toLocaleString('zh-CN')}</td>
                            <td style={{ padding: '3px 6px', whiteSpace: 'nowrap' }}>{d.model_name}</td>
                            <td style={{ padding: '3px 6px', textAlign: 'right' }}>{d.input_tokens}</td>
                            <td style={{ padding: '3px 6px', textAlign: 'right' }}>{d.output_tokens}</td>
                            <td style={{ padding: '3px 6px', textAlign: 'right', color: V.textDim }}>{d.thinking_tokens || '-'}</td>
                            <td style={{ padding: '3px 6px', textAlign: 'right', color: V.textDim }}>{(d.cache_read_tokens || 0) + (d.cache_write_tokens || 0) || '-'}</td>
                            <td style={{ padding: '3px 6px', textAlign: 'right', fontWeight: 600 }}>${Number(d.cost).toFixed(6)}</td>
                            <td style={{ padding: '3px 6px', textAlign: 'right', color: V.textDim }}>{d.latency_ms != null ? `${d.latency_ms}ms` : '-'}</td>
                            <td style={{ padding: '3px 6px', textAlign: 'center' }}>{d.error_message ? <span title={d.error_message} style={{ color: '#ef4444', cursor: 'help' }}>error</span> : <span style={{ color: '#22c55e' }}>ok</span>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      ))}

      {/* Pagination */}
      {sessions.total > sessions.page_size && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
          <button
            disabled={sessions.page <= 1}
            onClick={() => fetchSessions(sessions.page - 1)}
            style={{
              padding: '4px 12px', border: `1px solid ${V.border}`, borderRadius: 4,
              background: 'transparent', color: V.text, cursor: 'pointer', fontSize: 12,
            }}
          >Prev</button>
          <span style={{ fontSize: 12, color: V.textDim, lineHeight: '28px' }}>
            {sessions.page} / {Math.ceil(sessions.total / sessions.page_size)}
          </span>
          <button
            disabled={sessions.page * sessions.page_size >= sessions.total}
            onClick={() => fetchSessions(sessions.page + 1)}
            style={{
              padding: '4px 12px', border: `1px solid ${V.border}`, borderRadius: 4,
              background: 'transparent', color: V.text, cursor: 'pointer', fontSize: 12,
            }}
          >Next</button>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// MEMORY PAGE
// ══════════════════════════════════════════════════════════════════════

function MemoryPage() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const viUserId = api.getViUserId();

  useEffect(() => {
    setLoading(true);
    api.authFetch('/api/nanoclaw/dashboard/memory')
      .then(r => r.json())
      .then(d => setFiles(d?.files || []))
      .catch(() => setFiles([]))
      .finally(() => setLoading(false));
  }, [viUserId]);

  return (
    <div style={{ height: '100%', overflow: 'auto', background: V.bg }}>
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '24px 20px' }}>
        <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16, fontFamily: V.fontUi }}>
          Memory Files
        </h2>
        {loading && <span style={{ color: V.textDim, fontSize: 14 }}>Loading...</span>}
        {!loading && files.length === 0 && (
          <p style={{ color: V.textDim, fontSize: 14 }}>No memory files found.</p>
        )}
        {files.map((f, i) => (
          <div key={f.filename || i} style={{
            background: V.card, borderRadius: 12, padding: 16, marginBottom: 12,
            border: `1px solid ${V.border}`,
          }}>
            <div style={{
              fontSize: 13, fontWeight: 600, color: V.accent, marginBottom: 8,
              fontFamily: 'monospace',
            }}>
              {f.filename}
              <span style={{ fontSize: 11, color: V.textDim, fontWeight: 400, marginLeft: 8 }}>
                {f.source}
              </span>
            </div>
            <pre style={{
              fontSize: 13, lineHeight: 1.5, color: V.text, margin: 0,
              whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              fontFamily: 'monospace', background: 'transparent',
            }}>
              {f.content}
            </pre>
          </div>
        ))}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// ══════════════════════════════════════════════════════════════════════
// BUDGET ERROR MODAL — fires when 429 budget_error is received
// ══════════════════════════════════════════════════════════════════════

function BudgetErrorModal() {
  const navigate = useNavigate();
  const [info, setInfo] = useState(null); // { message, reset_at } | null

  useEffect(() => {
    const handler = (e) => setInfo(e.detail || {});
    window.addEventListener('uva:budget_error', handler);
    return () => window.removeEventListener('uva:budget_error', handler);
  }, []);

  if (!info) return null;

  const resetLabel = info.reset_at
    ? new Date(info.reset_at).toLocaleString(undefined, {
        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
      })
    : null;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.4)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        background: V.bgCard, borderRadius: 14, padding: '28px 32px',
        maxWidth: 420, width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
        fontFamily: V.fontUi,
      }}>
        <div style={{ fontSize: 28, marginBottom: 8 }}>⚠️</div>
        <div style={{ fontSize: 17, fontWeight: 600, color: V.text, marginBottom: 8 }}>
          API Key Budget Exhausted
        </div>
        <div style={{ fontSize: 14, color: V.textDim, lineHeight: 1.6, marginBottom: resetLabel ? 8 : 20 }}>
          {info.message || 'Your API key has reached its spending limit for this period.'}
        </div>
        {resetLabel && (
          <div style={{ fontSize: 13, color: V.textDim, marginBottom: 20 }}>
            Resets at <strong style={{ color: V.text }}>{resetLabel}</strong>
          </div>
        )}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button
            onClick={() => setInfo(null)}
            style={{
              padding: '8px 16px', borderRadius: 8, border: `1px solid ${V.border}`,
              background: 'transparent', color: V.textDim, cursor: 'pointer', fontSize: 14,
            }}
          >
            Dismiss
          </button>
          <button
            onClick={() => { setInfo(null); navigate('/playground/keys'); }}
            style={{
              padding: '8px 16px', borderRadius: 8, border: 'none',
              background: V.accent, color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 500,
            }}
          >
            Manage API Keys →
          </button>
        </div>
      </div>
    </div>
  );
}

// LAYOUT — wraps NavSidebar + Outlet for route-based page switching
// ══════════════════════════════════════════════════════════════════════

function PlaygroundLayout() {
  return (
    <div style={{
      display: 'flex', height: '100vh',
      fontFamily: V.fontUi, background: V.bg, color: V.text,
      overflow: 'hidden',
    }}>
      <NavSidebar />
      <main style={{ flex: 1, overflow: 'hidden' }}>
        <Outlet />
      </main>
      <BudgetErrorModal />
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// SHARED SESSION VIEW — public read-only view (no auth required)
// ══════════════════════════════════════════════════════════════════════

function SharedSessionView() {
  const { viUserId, sessionId, token } = useParams();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rawMode, setRawMode] = useState(false);
  const [perCardRaw, setPerCardRaw] = useState({});

  useEffect(() => {
    setLoading(true);
    setError(null);
    // Token-based URL: /share/{token} → /api/public/share/{token}
    // Legacy URL: /share/{viUserId}/{sessionId} → /api/public/sessions/{viUserId}/{sessionId}
    const url = token
      ? `${api.getBaseUrl()}/api/public/share/${encodeURIComponent(token)}`
      : `${api.getBaseUrl()}/api/public/sessions/${encodeURIComponent(viUserId)}/${encodeURIComponent(sessionId)}`;
    fetch(url)
      .then(r => { if (!r.ok) throw new Error('Session not found'); return r.json(); })
      .then(data => {
        // Normalize to the same shape ChatPage uses
        const ts = new Date(data.started_at || 0).getTime();
        let html = data.result_html || '';
        if (!html && data.result?.cards) {
          const firstCard = Object.values(data.result.cards)[0];
          if (firstCard?.data?.html) html = firstCard.data.html;
        }
        setSession({
          sessionId: data.id,
          tasks: data.prompt ? [{ prompt: data.prompt, ts, sessionId: data.id, skillSlug: data.context?.skill_slug || null }] : [],
          latestTs: ts,
          status: data.status,
          title: data.title,
          result: data.result || null,
          result_html: html,
          result_summary: data.result_summary || '',
          timeline: data.timeline || [],
          artifacts: data.artifacts || [],
          thumbnail_url: data.thumbnail_url || null,
          context: data.context || null,
        });
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [viUserId, sessionId, token]);

  if (loading) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '100vh', fontFamily: V.fontUi, color: V.textDim, fontSize: 14,
        background: V.bg,
      }}>Loading shared session...</div>
    );
  }
  if (error || !session) {
    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        height: '100vh', fontFamily: V.fontUi, color: V.textDim, fontSize: 14,
        background: V.bg, gap: 12,
      }}>
        <span style={{ fontSize: 32, opacity: 0.3 }}>🔗</span>
        <span>{error || 'Session not found'}</span>
      </div>
    );
  }

  // Build sorted tasks and cards (same logic as ChatPage)
  const allCards = {};
  const cardTaskMap = {};
  const sortedTasks = [];

  if (session.timeline?.length > 0) {
    session.timeline.forEach((round, i) => {
      const roundCards = {};
      if (round.cards) {
        for (const [cid, card] of Object.entries(round.cards)) {
          const c = { ...card, status: card.status || 'finalized' };
          roundCards[cid] = c;
          allCards[cid] = c;
        }
      }
      const task = {
        taskId: `round-${i}`, prompt: round.prompt || '', ts: new Date(round.ts || 0).getTime(),
        skillSlug: session.tasks?.[0]?.skillSlug, cards: roundCards,
        mediaUrls: round.photos || [],
      };
      for (const cid of Object.keys(roundCards)) cardTaskMap[cid] = task;
      sortedTasks.push(task);
    });
  } else if (session.result?.cards) {
    const cards = {};
    for (const [cid, card] of Object.entries(session.result.cards)) {
      const c = { ...card, status: card.status || 'finalized' };
      cards[cid] = c;
      allCards[cid] = c;
    }
    const task = {
      taskId: session.tasks?.[0]?.taskId || 'task-0',
      prompt: session.tasks?.[0]?.prompt || '',
      ts: session.latestTs,
      skillSlug: session.tasks?.[0]?.skillSlug,
      cards,
    };
    for (const cid of Object.keys(cards)) cardTaskMap[cid] = task;
    sortedTasks.push(task);
  } else if (session.tasks?.length) {
    sortedTasks.push(...session.tasks.map(t => ({ ...t, cards: {} })));
  }

  // Collect debug-html cards across all tasks (Issue #1007). Rendered in a
  // collapsible DebugPanel at the bottom; main content stays clean.
  const sharedDebugCards = Object.entries(allCards)
    .filter(([, card]) => card.template === 'debug-html')
    .map(([id, card]) => ({ cardId: id, ...card }))
    .sort((a, b) => (a.ts || 0) - (b.ts || 0));

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', height: '100vh',
      fontFamily: V.fontUi, background: V.bg, color: V.text,
    }}>
      {/* Header */}
      <div style={{
        padding: '12px 24px', background: V.bgCard,
        borderBottom: `1px solid ${V.border}`,
        display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <span style={{ fontSize: 16 }}>🔗</span>
        <span style={{ fontWeight: 600, fontSize: 14 }}>Shared Session</span>
        <span style={{ fontSize: 12, color: V.textDim }}>
          {session.title || session.tasks?.[0]?.prompt?.slice(0, 60) || session.sessionId}
        </span>
        <span style={{ marginLeft: 'auto', fontSize: 11, color: V.textDim }}>
          {session.latestTs ? new Date(session.latestTs).toLocaleString() : ''}
        </span>
        <RawToggleButton active={rawMode} onClick={() => setRawMode(r => !r)} />
      </div>
      {/* Session content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px', maxWidth: 800, margin: '0 auto', width: '100%' }}>
        {sortedTasks.map((task, i) => {
          const taskCardIds = Object.keys(task.cards || {})
            // Partition out debug-html cards → rendered in DebugPanel below (Issue #1007)
            .filter(id => allCards[id]?.template !== 'debug-html')
            .sort((a, b) => {
              const tsA = allCards[a]?.ts || 0;
              const tsB = allCards[b]?.ts || 0;
              return tsA - tsB;
            });
          return (
            <div key={task.taskId || i}>
              {/* User message */}
              {task.prompt && (
                <div style={{ marginBottom: 16, maxWidth: '80%', marginLeft: 'auto' }}>
                  <div style={{
                    padding: '12px 16px', borderRadius: 12, fontSize: 14,
                    lineHeight: 1.5, wordBreak: 'break-word',
                    background: V.accent, color: '#fff', borderBottomRightRadius: 4,
                  }}>
                    {task.mediaUrls?.length > 0 && (
                      <div style={{ marginBottom: 8 }}>
                        {task.mediaUrls.map((url, j) => (
                          _isVideoUrl(url) ? (
                            <video key={j} src={url} controls playsInline preload="metadata" style={{
                              maxWidth: 200, maxHeight: 150, borderRadius: 8, margin: 2,
                            }} />
                          ) : (
                            <img key={j} src={url} alt="" style={{
                              maxWidth: 200, maxHeight: 150, borderRadius: 8, margin: 2,
                            }} onError={e => { e.target.style.display = 'none'; }} />
                          )
                        ))}
                      </div>
                    )}
                    {task.prompt}
                  </div>
                  <div style={{ fontSize: 11, color: V.textDim, marginTop: 4, textAlign: 'right' }}>
                    {task.skillSlug && (
                      <span style={{
                        background: 'rgba(175,82,222,0.12)', color: V.purple,
                        padding: '2px 8px', borderRadius: 6, fontSize: 11, marginRight: 6,
                      }}>{formatSkillTag(task.skillSlug)}</span>
                    )}
                    {formatDateTime(task.ts)}
                  </div>
                </div>
              )}
              {/* Cards */}
              {taskCardIds.length > 0 && (
                <div style={{ marginBottom: 16, maxWidth: '80%', marginRight: 'auto' }}>
                  {taskCardIds.map(id => {
                    const card = allCards[id];
                    const cardTime = card.ts ? formatDateTime(card.ts) : '';

                    // Thinking + agent-speech cards — render as simple blocks without card frame
                    if (card.template === 'thinking' || card.template === 'thinking-process' || card.template === 'agent-speech') {
                      return (
                        <div key={id} style={{ marginTop: 6 }}>
                          <CardRenderer card={card} />
                          {cardTime && <div style={{ fontSize: 11, color: V.textDim, marginTop: 4, padding: '0 4px' }}>{cardTime}</div>}
                        </div>
                      );
                    }

                    // Content cards — bordered container with dynamic status badge + timestamp
                    const statusBadge = card.status === 'finalized' || card.status === 'done'
                      ? { bg: 'rgba(52,199,89,0.12)', color: V.green, text: 'done' }
                      : card.status === 'error' || card.status === 'failed'
                        ? { bg: 'rgba(255,59,48,0.12)', color: V.red, text: 'error' }
                        : card.status === 'stopped'
                          ? { bg: 'rgba(255,149,0,0.12)', color: '#ff9500', text: 'stopped' }
                          : { bg: 'rgba(52,199,89,0.12)', color: V.green, text: 'done' };
                    const showRaw = rawMode || perCardRaw[id];
                    return (
                      <div key={id} style={{
                        background: V.bg, border: `1px solid ${V.border}`,
                        borderRadius: 8, overflow: 'hidden', marginTop: 6,
                      }}>
                        <div style={{
                          display: 'flex', alignItems: 'center', gap: 8,
                          padding: '8px 12px', fontSize: 11, fontWeight: 600,
                          textTransform: 'uppercase', letterSpacing: '0.04em',
                          color: V.textDim, borderBottom: `1px solid ${V.border}`, background: V.bg,
                        }}>
                          {card.template || 'card'}
                          <span style={{ fontWeight: 400, fontSize: 10, marginLeft: 'auto' }}>
                            {cardTime}
                          </span>
                          <span style={{
                            background: statusBadge.bg, color: statusBadge.color,
                            padding: '2px 8px', borderRadius: 6, fontSize: 10,
                            fontWeight: 500,
                          }}>
                            {statusBadge.text}
                          </span>
                          <RawToggleButton
                            active={showRaw}
                            onClick={() => setPerCardRaw(prev => ({ ...prev, [id]: !prev[id] }))}
                          />
                        </div>
                        <div style={{ background: '#fff', color: '#111', maxHeight: '80vh', overflow: 'auto' }}>
                          {showRaw ? <RawDataView card={card} /> : <CardRenderer card={card} />}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {/* Fallback: result_html */}
              {taskCardIds.length === 0 && session.result_html && i === sortedTasks.length - 1 && (
                <div style={{ marginBottom: 16, maxWidth: '80%', marginRight: 'auto' }}>
                  <PersistentHtmlRenderer streamingChunks={[session.result_html]} isStreaming={false} />
                </div>
              )}
            </div>
          );
        })}
        {/* Debug panel — agent-speak tool I/O, collapsed by default (Issue #1007) */}
        <DebugPanel cards={sharedDebugCards} />
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// API KEYS PAGE (Stage 8 — user self-service bound keys)
// ══════════════════════════════════════════════════════════════════════

function ApiKeysPage() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [minted, setMinted] = useState(null); // { plaintext, name }
  const [confirmCopied, setConfirmCopied] = useState(false);
  const defaultKey = tokenStore.getApiKey();

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await keysListRequest();
      setKeys(rows || []);
    } catch (e) {
      setError(e.message || 'Failed to load keys');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const onCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    setError(null);
    try {
      const row = await keysCreateRequest(newName.trim());
      setMinted({ plaintext: row.plaintext, name: row.name });
      setConfirmCopied(false);
      setNewName('');
      await reload();
    } catch (e) {
      setError(e.message || 'Failed to create key');
    } finally {
      setCreating(false);
    }
  };

  const onToggle = async (row) => {
    try {
      await keysUpdateRequest(row.id, { is_active: !row.is_active });
      await reload();
    } catch (e) {
      setError(e.message);
    }
  };

  const onDelete = async (row) => {
    if (!window.confirm(`Delete key "${row.name}"? This cannot be undone.`)) return;
    try {
      await keysDeleteRequest(row.id);
      // If the deleted key is the one we had stored locally, clear it.
      if (defaultKey && defaultKey.startsWith(row.prefix.replace('...', ''))) {
        tokenStore.clearApiKey();
      }
      await reload();
    } catch (e) {
      setError(e.message);
    }
  };

  const onSetDefault = (plaintext) => {
    tokenStore.setApiKey(plaintext);
    setMinted(null);
    alert('Key stored as default for business calls');
  };

  return (
    <div style={{ padding: 24, overflowY: 'auto', height: '100%', fontFamily: V.fontUi }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: V.text, margin: 0 }}>API Keys</h2>
        <div style={{ fontSize: 12, color: V.textDim }}>
          {defaultKey ? `default: ${defaultKey.slice(0, 12)}...` : 'no default key set'}
        </div>
      </div>

      <div style={{
        background: V.bgCard, border: `1px solid ${V.border}`, borderRadius: 10,
        padding: 16, marginBottom: 16, display: 'flex', gap: 8, alignItems: 'center',
      }}>
        <input
          type="text"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="Key name (e.g. production, staging, laptop)"
          style={{
            flex: 1, padding: '8px 12px', border: `1px solid ${V.border}`, borderRadius: 6,
            fontSize: 13, outline: 'none',
          }}
          onKeyDown={(e) => { if (e.key === 'Enter') onCreate(); }}
        />
        <button
          onClick={onCreate}
          disabled={creating || !newName.trim()}
          style={{
            padding: '8px 18px', background: V.accent, color: '#fff', border: 'none',
            borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer',
            opacity: (creating || !newName.trim()) ? 0.5 : 1,
          }}
        >
          {creating ? 'Creating…' : 'Mint new key'}
        </button>
      </div>

      {error && <div style={{ color: V.red, marginBottom: 12, fontSize: 13 }}>{error}</div>}

      {minted && (
        <div style={{
          background: '#fffbeb', border: `1px solid ${V.yellow}`, borderRadius: 10,
          padding: 16, marginBottom: 16,
        }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: V.text, marginBottom: 8 }}>
            New key: {minted.name}
          </div>
          <div style={{ fontSize: 11, color: V.textDim, marginBottom: 8 }}>
            Copy it now — this plaintext will never be shown again.
          </div>
          <div style={{
            background: V.bg, border: `1px solid ${V.border}`, borderRadius: 6,
            padding: 10, fontFamily: V.font, fontSize: 12, wordBreak: 'break-all', marginBottom: 10,
          }}>
            {minted.plaintext}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => {
                navigator.clipboard.writeText(minted.plaintext);
                setConfirmCopied(true);
              }}
              style={{
                padding: '6px 14px', background: V.accent, color: '#fff',
                border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer',
              }}
            >
              {confirmCopied ? 'Copied ✓' : 'Copy'}
            </button>
            <button
              onClick={() => onSetDefault(minted.plaintext)}
              style={{
                padding: '6px 14px', background: V.green, color: '#fff',
                border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer',
              }}
            >
              Use as default
            </button>
            <button
              onClick={() => setMinted(null)}
              disabled={!confirmCopied}
              style={{
                padding: '6px 14px', background: 'transparent', color: V.textDim,
                border: `1px solid ${V.border}`, borderRadius: 6, fontSize: 12,
                cursor: confirmCopied ? 'pointer' : 'not-allowed', opacity: confirmCopied ? 1 : 0.5,
              }}
            >
              I copied it — dismiss
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ color: V.textDim }}>Loading…</div>
      ) : keys.length === 0 ? (
        <div style={{ color: V.textDim, padding: 20, textAlign: 'center' }}>
          No keys yet. Mint one above.
        </div>
      ) : (
        <div style={{
          background: V.bgCard, border: `1px solid ${V.border}`, borderRadius: 10, overflow: 'hidden',
        }}>
          <div style={{
            display: 'grid', gridTemplateColumns: '1.2fr 1.3fr 0.5fr 0.5fr 0.6fr 0.6fr 0.8fr',
            padding: '10px 14px', borderBottom: `1px solid ${V.border}`,
            fontSize: 11, fontWeight: 600, color: V.textDim, textTransform: 'uppercase', letterSpacing: '0.05em',
          }}>
            <div>Name</div>
            <div>Prefix</div>
            <div>Status</div>
            <div>RPM</div>
            <div>Balance</div>
            <div>Spend</div>
            <div>Actions</div>
          </div>
          {keys.map(row => (
            <div key={row.id} style={{
              display: 'grid', gridTemplateColumns: '1.2fr 1.3fr 0.5fr 0.5fr 0.6fr 0.6fr 0.8fr',
              padding: '10px 14px', borderBottom: `1px solid ${V.border}`, fontSize: 12, alignItems: 'center',
            }}>
              <div style={{ fontWeight: 600, color: V.text }}>{row.name}</div>
              <div style={{ fontFamily: V.font, color: V.textDim }}>{row.prefix}</div>
              <div>
                <span style={{
                  padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600,
                  background: row.is_active ? V.green + '22' : V.red + '22',
                  color: row.is_active ? V.green : V.red,
                }}>
                  {row.is_active ? 'active' : 'disabled'}
                </span>
              </div>
              <div style={{ color: V.textDim }}>{row.rate_limit_rpm}</div>
              <div style={{ color: V.textDim }}>{row.balance == null ? '—' : row.balance.toFixed(2)}</div>
              <div style={{ color: V.textDim }}>{Number(row.spend || 0).toFixed(2)}</div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  onClick={() => onToggle(row)}
                  style={{
                    padding: '4px 8px', background: 'transparent', color: V.text,
                    border: `1px solid ${V.border}`, borderRadius: 4, fontSize: 11, cursor: 'pointer',
                  }}
                >
                  {row.is_active ? 'Disable' : 'Enable'}
                </button>
                <button
                  onClick={() => onDelete(row)}
                  style={{
                    padding: '4px 8px', background: 'transparent', color: V.red,
                    border: `1px solid ${V.red}`, borderRadius: 4, fontSize: 11, cursor: 'pointer',
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// PROMPTS PAGE (Stage 8 — DB-backed prompt_cards CRUD)
// ══════════════════════════════════════════════════════════════════════

function PromptsPage() {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [editing, setEditing] = useState(null); // null | {new:true, ...} | existing card
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await cardsListRequest();
      setCards(rows || []);
    } catch (e) {
      setError(e.message || 'Failed to load prompts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const selected = cards.find(c => c.id === selectedId);

  const startNew = () => {
    setSelectedId(null);
    setEditing({
      isNew: true,
      slug: '',
      name: '',
      description: '',
      system_prompt: '',
      default_model: '',
      allowed_skill_slugs: '',
    });
  };

  const startEdit = (card) => {
    setSelectedId(card.id);
    setEditing({
      isNew: false,
      id: card.id,
      slug: card.slug,
      name: card.name,
      description: card.description || '',
      system_prompt: card.system_prompt || '',
      default_model: card.default_model || '',
      allowed_skill_slugs: Array.isArray(card.allowed_skill_slugs) ? card.allowed_skill_slugs.join(', ') : '',
    });
  };

  const onSave = async () => {
    if (!editing) return;
    setSaving(true);
    setError(null);
    try {
      const slugs = editing.allowed_skill_slugs
        ? editing.allowed_skill_slugs.split(',').map(s => s.trim()).filter(Boolean)
        : null;
      const body = {
        slug: editing.slug.trim(),
        name: editing.name.trim(),
        description: editing.description || null,
        system_prompt: editing.system_prompt || '',
        default_model: editing.default_model || null,
        allowed_skill_slugs: slugs,
      };
      if (editing.isNew) {
        const row = await cardsCreateRequest(body);
        await reload();
        setSelectedId(row.id);
        setEditing(null);
      } else {
        // slug is immutable in update — strip it from the patch.
        const { slug, ...patch } = body;
        await cardsUpdateRequest(editing.id, patch);
        await reload();
        setEditing(null);
      }
    } catch (e) {
      setError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (card) => {
    if (!window.confirm(`Delete prompt "${card.name}"?`)) return;
    try {
      await cardsDeleteRequest(card.id);
      setSelectedId(null);
      setEditing(null);
      await reload();
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', fontFamily: V.fontUi }}>
      {/* Banner — explains what this page is for */}
      <div style={{
        padding: '10px 18px',
        background: V.accentDim,
        borderBottom: `1px solid ${V.border}`,
        fontSize: 12,
        color: V.text,
        lineHeight: 1.5,
      }}>
        <strong style={{ color: V.accent }}>Prompt Presets</strong>
        {' — '}
        这里保存的是预置 system prompt 模板(slug + system_prompt + 默认模型 + 允许的 skills)。
        等阶段 10 接入运行时后,通过 <code>POST /v1/chat/completions</code> 带 <code>card_id=&lt;slug&gt;</code> 自动注入。
      </div>

      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
      {/* Left: list */}
      <div style={{
        width: 260, borderRight: `1px solid ${V.border}`, background: V.bgCard,
        display: 'flex', flexDirection: 'column',
      }}>
        <div style={{
          padding: 14, borderBottom: `1px solid ${V.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ fontSize: 14, fontWeight: 700 }}>Presets</div>
          <button
            onClick={startNew}
            style={{
              padding: '4px 10px', background: V.accent, color: '#fff',
              border: 'none', borderRadius: 6, fontSize: 11, cursor: 'pointer',
            }}
          >
            + New
          </button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading ? (
            <div style={{ padding: 14, color: V.textDim, fontSize: 12 }}>Loading…</div>
          ) : cards.length === 0 ? (
            <div style={{ padding: 14, color: V.textDim, fontSize: 12 }}>No prompts yet</div>
          ) : (
            cards.map(c => (
              <div
                key={c.id}
                onClick={() => startEdit(c)}
                style={{
                  padding: '10px 14px', borderBottom: `1px solid ${V.border}`, cursor: 'pointer',
                  background: selectedId === c.id ? V.accentDim : 'transparent',
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 600, color: V.text }}>{c.name}</div>
                <div style={{ fontSize: 11, color: V.textDim, marginTop: 2, fontFamily: V.font }}>
                  {c.slug}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Right: detail / editor */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
        {error && <div style={{ color: V.red, marginBottom: 12, fontSize: 13 }}>{error}</div>}
        {!editing ? (
          <div style={{ color: V.textDim, fontSize: 13 }}>
            Select a prompt on the left, or create a new one.
          </div>
        ) : (
          <div style={{ maxWidth: 720 }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, marginTop: 0, marginBottom: 16 }}>
              {editing.isNew ? 'New prompt' : `Edit: ${editing.name}`}
            </h2>
            <Field label="Slug (immutable)">
              <input
                type="text"
                value={editing.slug}
                disabled={!editing.isNew}
                onChange={(e) => setEditing({ ...editing, slug: e.target.value })}
                placeholder="welcome"
                style={inputStyle}
              />
            </Field>
            <Field label="Name">
              <input
                type="text"
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                placeholder="Welcome message"
                style={inputStyle}
              />
            </Field>
            <Field label="Description">
              <input
                type="text"
                value={editing.description}
                onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                placeholder="Short description"
                style={inputStyle}
              />
            </Field>
            <Field label="System prompt">
              <textarea
                value={editing.system_prompt}
                onChange={(e) => setEditing({ ...editing, system_prompt: e.target.value })}
                placeholder="You are a helpful assistant..."
                rows={10}
                style={{ ...inputStyle, fontFamily: V.font, resize: 'vertical' }}
              />
            </Field>
            <Field label="Default model (optional)">
              <input
                type="text"
                value={editing.default_model}
                onChange={(e) => setEditing({ ...editing, default_model: e.target.value })}
                placeholder="claude-sonnet-4-6"
                style={inputStyle}
              />
            </Field>
            <Field label="Allowed skill slugs (comma-separated, optional)">
              <input
                type="text"
                value={editing.allowed_skill_slugs}
                onChange={(e) => setEditing({ ...editing, allowed_skill_slugs: e.target.value })}
                placeholder="web-search, summarizer"
                style={inputStyle}
              />
            </Field>
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button
                onClick={onSave}
                disabled={saving || !editing.slug.trim() || !editing.name.trim()}
                style={{
                  padding: '8px 18px', background: V.accent, color: '#fff', border: 'none',
                  borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  opacity: saving ? 0.5 : 1,
                }}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button
                onClick={() => setEditing(null)}
                style={{
                  padding: '8px 18px', background: 'transparent', color: V.text,
                  border: `1px solid ${V.border}`, borderRadius: 6, fontSize: 13, cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              {!editing.isNew && selected && (
                <button
                  onClick={() => onDelete(selected)}
                  style={{
                    marginLeft: 'auto', padding: '8px 18px', background: 'transparent',
                    color: V.red, border: `1px solid ${V.red}`, borderRadius: 6,
                    fontSize: 13, cursor: 'pointer',
                  }}
                >
                  Delete
                </button>
              )}
            </div>
          </div>
        )}
      </div>
      </div>
    </div>
  );
}

const inputStyle = {
  width: '100%', padding: '8px 12px', border: `1px solid ${V.border}`,
  borderRadius: 6, fontSize: 13, outline: 'none', background: '#fff',
};

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: V.textDim, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </div>
      {children}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// Webhooks Page
// ══════════════════════════════════════════════════════════════════════

const EVENT_OPTIONS = [
  { value: 'usage.recorded', label: 'usage.recorded — 每笔扣费（opt-in, 高频）' },
  { value: 'budget.low', label: 'budget.low — 余额低于 10%' },
  { value: 'budget.exhausted', label: 'budget.exhausted — 预算耗尽 429 触发' },
  { value: 'budget.reset', label: 'budget.reset — 周期预算重置' },
  { value: 'credit.topup', label: 'credit.topup — 管理员充值' },
];

function WebhooksPage() {
  const [endpoints, setEndpoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newUrl, setNewUrl] = useState('');
  const [newEvents, setNewEvents] = useState(['budget.low', 'budget.exhausted']);
  const [minted, setMinted] = useState(null);
  const [deliveries, setDeliveries] = useState(null);
  const [deliveriesId, setDeliveriesId] = useState(null);
  const [deliveriesLoading, setDeliveriesLoading] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await api.request('/api/webhooks');
      setEndpoints(rows || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const onCreate = async () => {
    if (!newUrl.trim() || newEvents.length === 0) return;
    setCreating(true);
    try {
      const res = await api.request('/api/webhooks', {
        method: 'POST',
        body: JSON.stringify({ url: newUrl.trim(), events: newEvents, is_active: true }),
      });
      setMinted({ secret: res.secret, url: res.url });
      setShowCreate(false);
      setNewUrl('');
      setNewEvents(['budget.low', 'budget.exhausted']);
      await reload();
    } catch (e) {
      setError(e.message);
    } finally {
      setCreating(false);
    }
  };

  const onToggle = async (ep) => {
    try {
      await api.request(`/api/webhooks/${ep.id}`, { method: 'PATCH', body: JSON.stringify({ is_active: !ep.is_active }) });
      await reload();
    } catch (e) { setError(e.message); }
  };

  const onDelete = async (ep) => {
    if (!window.confirm(`Delete webhook ${ep.url}?`)) return;
    try {
      await api.request(`/api/webhooks/${ep.id}`, { method: 'DELETE' });
      await reload();
    } catch (e) { setError(e.message); }
  };

  const loadDeliveries = async (id) => {
    if (deliveriesId === id) { setDeliveries(null); setDeliveriesId(null); return; }
    setDeliveriesLoading(true);
    setDeliveriesId(id);
    try {
      const rows = await api.request(`/api/webhooks/${id}/deliveries?limit=20`);
      setDeliveries(rows || []);
    } catch (e) { setError(e.message); }
    finally { setDeliveriesLoading(false); }
  };

  const btnStyle = (primary) => ({
    padding: '6px 14px', borderRadius: 6, border: 'none', cursor: 'pointer',
    fontSize: 12, fontWeight: 600,
    background: primary ? V.accent : V.bgHover,
    color: primary ? '#fff' : V.text,
  });

  return (
    <div style={{ padding: 24, overflowY: 'auto', height: '100%', fontFamily: V.fontUi }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: V.text, margin: 0 }}>Webhooks</h2>
        <button style={btnStyle(true)} onClick={() => setShowCreate(!showCreate)}>
          {showCreate ? 'Cancel' : '+ New Endpoint'}
        </button>
      </div>

      {error && (
        <div style={{ marginBottom: 12, padding: '8px 12px', background: '#fff0f0', border: '1px solid #ffd0d0', borderRadius: 6, fontSize: 12, color: '#c00' }}>
          {error} <button style={{ marginLeft: 8, background: 'none', border: 'none', cursor: 'pointer', color: '#c00' }} onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {minted && (
        <div style={{ marginBottom: 16, padding: 16, background: '#f0fff4', border: '1px solid #a8e6c0', borderRadius: 10 }}>
          <div style={{ fontWeight: 700, marginBottom: 8, color: '#155724' }}>✓ Webhook created — copy the secret now, it won't be shown again</div>
          <code style={{ fontSize: 12, background: '#fff', padding: '6px 10px', borderRadius: 4, display: 'block', wordBreak: 'break-all' }}>{minted.secret}</code>
          <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>URL: {minted.url}</div>
          <button style={{ ...btnStyle(false), marginTop: 10 }} onClick={() => setMinted(null)}>Dismiss</button>
        </div>
      )}

      {showCreate && (
        <div style={{ marginBottom: 20, padding: 16, background: V.bgCard, border: `1px solid ${V.border}`, borderRadius: 10 }}>
          <div style={{ marginBottom: 10 }}>
            <label style={{ fontSize: 12, color: V.textDim, display: 'block', marginBottom: 4 }}>Endpoint URL</label>
            <input
              type="url"
              value={newUrl}
              onChange={(e) => setNewUrl(e.target.value)}
              placeholder="https://your-server.com/webhook"
              style={{ width: '100%', padding: '8px 12px', border: `1px solid ${V.border}`, borderRadius: 6, fontSize: 13 }}
            />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 12, color: V.textDim, display: 'block', marginBottom: 6 }}>Events</label>
            {EVENT_OPTIONS.map(opt => (
              <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, cursor: 'pointer', fontSize: 12 }}>
                <input
                  type="checkbox"
                  checked={newEvents.includes(opt.value)}
                  onChange={(e) => setNewEvents(prev =>
                    e.target.checked ? [...prev, opt.value] : prev.filter(v => v !== opt.value)
                  )}
                />
                <span style={{ color: V.text }}>{opt.label}</span>
              </label>
            ))}
          </div>
          <button style={btnStyle(true)} onClick={onCreate} disabled={creating || !newUrl.trim() || newEvents.length === 0}>
            {creating ? 'Creating…' : 'Create Webhook'}
          </button>
        </div>
      )}

      {loading ? (
        <div style={{ color: V.textDim, fontSize: 13 }}>Loading…</div>
      ) : endpoints.length === 0 ? (
        <div style={{ color: V.textDim, fontSize: 13, textAlign: 'center', marginTop: 40 }}>
          No webhook endpoints yet. Create one above to receive event notifications.
        </div>
      ) : (
        endpoints.map(ep => (
          <div key={ep.id} style={{ marginBottom: 12, background: V.bgCard, border: `1px solid ${V.border}`, borderRadius: 10, overflow: 'hidden' }}>
            <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: V.text, wordBreak: 'break-all' }}>{ep.url}</div>
                <div style={{ fontSize: 11, color: V.textDim, marginTop: 3 }}>
                  Events: {(ep.events || []).join(', ') || 'none'}
                  {ep.secret_last4 && <span style={{ marginLeft: 8 }}>secret: …{ep.secret_last4}</span>}
                </div>
              </div>
              <span style={{
                fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
                background: ep.is_active ? '#e8f5e9' : '#f5f5f5',
                color: ep.is_active ? '#2e7d32' : '#999',
              }}>{ep.is_active ? 'Active' : 'Inactive'}</span>
              <button style={btnStyle(false)} onClick={() => onToggle(ep)}>
                {ep.is_active ? 'Disable' : 'Enable'}
              </button>
              <button style={btnStyle(false)} onClick={() => loadDeliveries(ep.id)}>
                Deliveries
              </button>
              <button
                style={{ ...btnStyle(false), color: '#c00' }}
                onClick={() => onDelete(ep)}
              >Delete</button>
            </div>

            {deliveriesId === ep.id && (
              <div style={{ borderTop: `1px solid ${V.border}`, padding: '12px 16px' }}>
                {deliveriesLoading ? (
                  <div style={{ fontSize: 12, color: V.textDim }}>Loading deliveries…</div>
                ) : !deliveries || deliveries.length === 0 ? (
                  <div style={{ fontSize: 12, color: V.textDim }}>No deliveries yet.</div>
                ) : (
                  deliveries.map(d => (
                    <div key={d.id} style={{ marginBottom: 8, fontSize: 11, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                      <span style={{
                        fontWeight: 700, padding: '1px 6px', borderRadius: 4, flexShrink: 0,
                        background: d.status === 'success' ? '#e8f5e9' : d.status === 'pending' ? '#fff8e1' : '#fce4ec',
                        color: d.status === 'success' ? '#2e7d32' : d.status === 'pending' ? '#f57f17' : '#b71c1c',
                      }}>{d.status}</span>
                      <span style={{ color: V.textDim }}>{d.event_type}</span>
                      <span style={{ color: V.textDim }}>attempt {d.attempt}</span>
                      {d.response_code && <span style={{ color: V.textDim }}>HTTP {d.response_code}</span>}
                      <span style={{ color: V.textDim, marginLeft: 'auto' }}>{d.created_at ? new Date(d.created_at).toLocaleString() : ''}</span>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// MAIN — PlaygroundView (React Router)
// ══════════════════════════════════════════════════════════════════════

export default function PlaygroundView({ nanoClaw, sseEvents, viUserId }) {
  return (
    <Routes>
      <Route path="/playground" element={<PlaygroundLayout />}>
        <Route index element={<Navigate to="chat" replace />} />
        <Route path="chat" element={<ChatPage nanoClaw={nanoClaw} sseEvents={sseEvents} viUserId={viUserId} />} />
        <Route path="chat/:sessionId" element={<ChatPage nanoClaw={nanoClaw} sseEvents={sseEvents} viUserId={viUserId} />} />
        <Route path="prompts" element={<PromptsPage />} />
        <Route path="keys" element={<ApiKeysPage />} />
        <Route path="webhooks" element={<WebhooksPage />} />
        <Route path="intention" element={<IntentionPage />} />
        <Route path="templates" element={<CardsPage />} />
        <Route path="packages" element={<PackagesPage nanoClaw={nanoClaw} />} />
        <Route path="testbench" element={<PackagesPage nanoClaw={nanoClaw} />} />
        <Route path="skills" element={<SkillsPage />} />
        <Route path="tools" element={<ToolsPage />} />
        <Route path="apis" element={<ApisPage />} />
        <Route path="usage" element={<UsagePage />} />
        <Route path="memory" element={<MemoryPage />} />
        <Route path="s3" element={<S3ManagerPage />} />
      </Route>
      <Route path="/playground/share/:token" element={<SharedSessionView />} />
      <Route path="/playground/share/:viUserId/:sessionId" element={<SharedSessionView />} />
    </Routes>
  );
}
